/**
 * Travelers Rest Events — Instant Quote Calculator
 * Loaded via: {{ 'quote-calculator.js' | asset_url | script_tag }}
 *
 * Rates are injected from Liquid metafields into window.QuoteRates
 * (see sections/quote-calculator.liquid for the injection block)
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     STATE
  ------------------------------------------------------------------ */
  const state = {
    currentStep:     1,
    totalSteps:      6,
    eventType:       null,
    adults:          0,
    children:        0,
    notes:           '',
    date:            null,
    flexibleDate:    false,
    dayPreferences:  [],
    startTime:       null,
    dateTimeNotes:   '',
    menuNotes:       '',
    duration:        '4',
    // Food
    horsOeuvres:     null,   // key or null
    courses:         [],     // ['pasta_course', 'salad_course']
    mainCourse:      null,   // key or null (required)
    dessertBase:     null,   // key or null
    cocktailAddons:  [],     // array of cocktail hour addon keys
    entreeAddons:    [],     // array of entrée addon keys
    dessertAddons:   [],     // array of addon keys
    // Event-level enhancements — neither food nor bar. 'extend_hour' is the
    // first (2026-08-31); see EVENING_SLOT_ONLY_ADDONS for why it is gated.
    eventAddons:     [],     // array of event addon keys
    afterDinner:     null,   // key or null
    // Bar
    barPackage:      null,   // key (required)
    cocktailTab:     false,  // true when Wine & Beer "Host-Paid Cocktail Tab" is chosen in the Cocktail Requests modal (no price change)
    cashForCocktails: false, // true when Wine & Beer "Guest-Paid Cocktails" is chosen in the Cocktail Requests modal (no price change)
    barEnhancements: [],     // array of enhancement keys
    mocktailScope:     'all',  // 'all' (kids & adults, every guest pays full) | 'kids' (kids only, min 10)
    // Savory station sub-selections (multi-select — each its own price)
    savoryStations:    [],     // array of keys from SAVORY_STATIONS
    sweetStationQty:   0,      // number of Sweet Stations (each $10/pp); 0 = not selected
    followUpIntent:    '',     // follow-up intent selection before proposal send
    _foodDefaultsApplied: false, // true once food pre-selection has been applied
    // Additional requests
    additionalRequests: '',
    // Room
    rooms:           [],
    // Lead capture
    firstName:       '',
    lastName:        '',
    email:           '',
    phone:           '',
    referral:        '',
    // Persisted gate flag — set true only after a successful proposal send.
    // Gates the Step 6 estimate reveal; survives reload via restoreState so a
    // returning visitor who already submitted sees their pricing without
    // re-sending the proposal. (state._leadSubmitted is session-only and NOT
    // persisted because it isn't in this initial object.)
    leadCaptured:    false,
  };

  /* ------------------------------------------------------------------
     HELPERS
  ------------------------------------------------------------------ */
  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  /**
   * Email validation — replaces the bare `includes('@')` this file used in eight
   * places until 2026-08-18.
   *
   * The defect: a prospect's address was submitted with a SPACE inside the domain
   * (`name@out look.com`). `includes('@')` passed it, Resend rejected the recipient, so she
   * never received the proposal she asked for, and the Worker's follow-up cron then
   * retried her record every five minutes and alerted the office on every tick.
   *
   * ⚠️ The `type="email"` on the inputs does NOT save us: the browser only enforces
   * that on native form submission, and this builder submits via JS.
   *
   * ⛔ Not RFC-complete on purpose — it rejects what actually breaks delivery.
   * 🔑 MIRROR of `isValidEmail()` in scripts/deposit-worker/worker.js, which is the
   * server-side gate. Keep the two in step; the Worker is the one that binds.
   */
  function isValidEmail(value) {
    var s = String(value == null ? '' : value).trim();
    if (!s || s.length > 254) return false;
    if (/\s/.test(s)) return false;
    var at = s.indexOf('@');
    if (at < 1 || at !== s.lastIndexOf('@')) return false;
    var local = s.slice(0, at);
    var domain = s.slice(at + 1);
    if (!local || local.length > 64 || !domain) return false;
    if (domain.charAt(0) === '.' || domain.charAt(domain.length - 1) === '.' || domain.indexOf('..') !== -1) return false;
    if (!/^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)) return false;
    if (!/^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)+$/.test(domain)) return false;
    if (!/\.[A-Za-z]{2,}$/.test(domain)) return false;
    return true;
  }

  // One readiness rule serves both live edits and restored local state. The submit
  // function remains the authoritative enforcement point; this only keeps the
  // Step-6 affordance truthful.
  function isProposalReady() {
    var phoneDigits = (state.phone || '').replace(/\D/g, '');
    return Boolean(normalizePersonName(state.firstName) && normalizePersonName(state.lastName) &&
      isValidEmail(state.email) && state.referral && state.followUpIntent && phoneDigits.length >= 10);
  }

  function getEl(selector) {
    return document.querySelector(selector);
  }

  function getEls(selector) {
    return document.querySelectorAll(selector);
  }

  /* ------------------------------------------------------------------
     GA4 attribution helpers
     _ga cookie format:  GA1.1.{rand}.{timestamp}  → client_id = "{rand}.{timestamp}"
     _ga_<stream_id> format:  GS1.1.{session_id}.{count}.{engaged}.{...}
     Stream/measurement ID: G-NTQKR56JPN → cookie suffix "NTQKR56JPN"
  ------------------------------------------------------------------ */
  function getGA4ClientId() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (!m) return '';
      var parts = decodeURIComponent(m[1]).split('.');
      if (parts.length < 4) return '';
      return parts.slice(-2).join('.');
    } catch (e) { return ''; }
  }
  function getGA4SessionId() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)_ga_NTQKR56JPN=([^;]+)/);
      if (!m) return '';
      var parts = decodeURIComponent(m[1]).split('.');
      if (parts.length < 3) return '';
      return parts[2];
    } catch (e) { return ''; }
  }
  // Ad attribution captured site-wide by the theme.liquid <head> snippet into
  // the first-party `tr_attr` cookie. Returns {} for organic visitors.
  function getAttribution() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)tr_attr=([^;]+)/);
      if (!m) return {};
      return JSON.parse(decodeURIComponent(m[1])) || {};
    } catch (e) { return {}; }
  }

  /* ------------------------------------------------------------------
     BOOKING MINIMUMS — headcount & dollar gates per room/day/time
     AM = event ends by 6 PM;  PM = event ends after 6 PM
     Keys match room card handles (data-room-card attribute)
     'all-venue' included for future use — not gated in current UI
  ------------------------------------------------------------------ */
  // 2026-09-13: temporary minimum reduction removed on owner instruction.
  // Four Terrace dollars use owner-chosen current / 0.8, not the older CSV amounts.
  // Exact targets and release status: Website wrapper specs/restore-booking-minimums.md.
  var BOOKING_MINIMUMS = {
    'atlantic-room': {
      monday:    { am: { guests: 50, dollars: 2450 }, pm: { guests: 50, dollars: 3450 } },
      tuesday:   { am: { guests: 50, dollars: 2450 }, pm: { guests: 50, dollars: 3450 } },
      wednesday: { am: { guests: 50, dollars: 2450 }, pm: { guests: 50, dollars: 3450 } },
      thursday:  { am: { guests: 50, dollars: 2450 }, pm: { guests: 50, dollars: 3450 } },
      friday:    { am: { guests: 50, dollars: 2940 }, pm: { guests: 60, dollars: 4740 } },
      saturday:  { am: { guests: 60, dollars: 5120 }, pm: { guests: 80, dollars: 7120 } },
      sunday:    { am: { guests: 50, dollars: 4160 }, pm: { guests: 65, dollars: 5135 } }
    },
    'crystal-room': {
      monday:    { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      tuesday:   { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      wednesday: { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      thursday:  { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      friday:    { am: { guests: 25, dollars: 1470 }, pm: { guests: 40, dollars: 3160 } },
      saturday:  { am: { guests: 40, dollars: 3200 }, pm: { guests: 50, dollars: 4450 } },
      sunday:    { am: { guests: 30, dollars: 2560 }, pm: { guests: 40, dollars: 3160 } }
    },
    'lantern-room': {
      monday:    { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      tuesday:   { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      wednesday: { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      thursday:  { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 2070 } },
      friday:    { am: { guests: 25, dollars: 1470 }, pm: { guests: 40, dollars: 3160 } },
      saturday:  { am: { guests: 40, dollars: 3200 }, pm: { guests: 50, dollars: 4450 } },
      sunday:    { am: { guests: 30, dollars: 2560 }, pm: { guests: 40, dollars: 3160 } }
    },
    'terrace-room': {
      monday:    { am: { guests: 25, dollars: 1225 }, pm: { guests: 25, dollars: 1725 } },
      tuesday:   { am: { guests: 25, dollars: 1225 }, pm: { guests: 25, dollars: 1725 } },
      wednesday: { am: { guests: 25, dollars: 1225 }, pm: { guests: 25, dollars: 1725 } },
      thursday:  { am: { guests: 25, dollars: 1225 }, pm: { guests: 25, dollars: 1725 } },
      friday:    { am: { guests: 25, dollars: 1470 }, pm: { guests: 25, dollars: 1975 } },
      saturday:  { am: { guests: 25, dollars: 2240 }, pm: { guests: 30, dollars: 2790 } },
      sunday:    { am: { guests: 25, dollars: 2000 }, pm: { guests: 30, dollars: 2100 } }
    },
    'all-venue': {
      monday:    { am: { guests: 100, dollars: 5390 }, pm: { guests: 50, dollars: 4140 } },
      tuesday:   { am: { guests: 100, dollars: 5390 }, pm: { guests: 100, dollars: 7590 } },
      wednesday: { am: { guests: 100, dollars: 5390 }, pm: { guests: 100, dollars: 7590 } },
      thursday:  { am: { guests: 100, dollars: 5390 }, pm: { guests: 100, dollars: 7590 } },
      friday:    { am: { guests: 110, dollars: 9188 }, pm: { guests: 165, dollars: 16788 } },
      saturday:  { am: { guests: 160, dollars: 20640 }, pm: { guests: 210, dollars: 30038 } },
      sunday:    { am: { guests: 125, dollars: 14400 }, pm: { guests: 175, dollars: 18269 } }
    }
  };

  // Expose for proposal.js PDF under-minimum note
  window.__TR_BOOKING_MINIMUMS = BOOKING_MINIMUMS;

  /* ------------------------------------------------------------------
     SEATED CAPACITY — the room MAXIMUM
     ------------------------------------------------------------------
     🔴 CANONICAL SOURCE: ~/TR/reference/venue.md -> "Seated capacities".
     ⛔ DO NOT RE-TYPE THESE ANYWHERE ELSE and ⛔ do not "correct" them from
     a number already in the theme — venue.md wins over any project doc, and
     scripts/deposit-worker/test/room-capacity-gate.test.js PARSES venue.md
     and fails if this map drifts from it. Fix venue.md, then fix this.

     WHY IT EXISTS (found by the CRM front 2026-08-22, analysis
     projects/crm/analysis/2026-08-22-pricing-review.md §5b): BOOKING_MINIMUMS
     above greys a room out from BELOW and nothing capped it from above, so a
     90-guest seated party could build in the Terrace, which seats 60. That is
     a PRICING defect, not a UX one — dollar floors are per room and the
     Terrace carries the lowest in the building (Sat PM $2,232 vs Crystal
     $3,560), so the error only ever runs one way, toward the cheapest floor
     we have. 22.3% of seated-meal proposals; 33% of all Terrace requests.

     ⚠️ Atlantic is 180-200 in venue.md. The UPPER bound is used deliberately:
     this gate should never fire on a party the room can actually seat.
  ------------------------------------------------------------------ */
  var ROOM_SEATED_CAPACITY = {
    'atlantic-room': 200,
    'crystal-room':  120,
    'lantern-room':  120,
    'terrace-room':   60
  };

  /* Only the two combinations venue.md CONFIRMS (2026-07-25). ⛔ Any other
     multi-room selection is NOT summed — venue.md forbids inferring a
     whole-building figure that way, and Entire Venue is still UNDEFINED. An
     unconfirmed combination is left ungated rather than warned about on a
     number nobody has confirmed. */
  var ROOM_COMBINATION_CAPACITY = [
    { rooms: ['atlantic-room', 'terrace-room'], seats: 250, label: 'the Atlantic and Terrace Rooms' },
    { rooms: ['crystal-room',  'lantern-room'], seats: 250, label: 'the Crystal and Lantern Rooms' }
  ];

  /* A served meal needs a chair. ⛔ food_stations and hors_doeuvres_heavy are
     EXCLUDED ON PURPOSE — those are standing receptions, which legitimately
     run 1.4-1.8x seated capacity (venue.md), and gating them would
     manufacture the finding §5b was careful not to manufacture. */
  var SEATED_MAIN_COURSES = ['entree_plated', 'lunch_dinner_buffet', 'brunch_buffet'];

  /** Seated capacity for the CURRENT room selection, or null when we have no
   *  confirmed figure for it (unconfirmed combination, all-venue, unknown key). */
  function getSelectionSeatedCapacity() {
    var sel = (state.rooms || []).slice().sort();
    if (sel.length === 0) return null;
    if (sel.length === 1) {
      var cap = ROOM_SEATED_CAPACITY[sel[0]];
      if (!cap) return null;
      return { seats: cap, label: roomDisplayNames[sel[0]] || sel[0] };
    }
    for (var i = 0; i < ROOM_COMBINATION_CAPACITY.length; i++) {
      var combo = ROOM_COMBINATION_CAPACITY[i];
      var key = combo.rooms.slice().sort().join('|');
      if (key === sel.join('|')) return { seats: combo.seats, label: combo.label };
    }
    return null;
  }

  /** The over-capacity check. Returns null unless a SEATED meal is selected
   *  AND the party exceeds a capacity we actually have a confirmed figure for. */
  function getSeatedCapacityInfo() {
    if (SEATED_MAIN_COURSES.indexOf(state.mainCourse) === -1) return null;
    var cap = getSelectionSeatedCapacity();
    if (!cap) return null;
    var guests = (state.adults || 0) + (state.children || 0);
    if (guests <= cap.seats) return null;
    return { guests: guests, seats: cap.seats, roomName: cap.label, over: guests - cap.seats };
  }

  /** Rooms that WOULD seat this party, largest last — used to name the way out. */
  function roomsThatSeat(guests) {
    return Object.keys(ROOM_SEATED_CAPACITY)
      .filter(function(k) { return ROOM_SEATED_CAPACITY[k] >= guests; })
      .sort(function(a, b) { return ROOM_SEATED_CAPACITY[a] - ROOM_SEATED_CAPACITY[b]; })
      .map(function(k) { return roomDisplayNames[k] || k; });
  }

  /* ------------------------------------------------------------------
     SAVORY STATIONS — name + per-person price
  ------------------------------------------------------------------ */
  var SAVORY_STATIONS = {
    savory_asian:           { label: 'Asian Station',                    rate: 12 },
    savory_beef_slider:     { label: 'Beef Slider Station',              rate: 12 },
    savory_carving:         { label: 'Carving Station',                  rate: 15 },
    savory_grilled_cheese:  { label: 'Grilled Cheese Station',           rate: 12 },
    savory_italian:         { label: 'Italian Station',                  rate: 12 },
    savory_lobster_chowder: { label: 'Lobster Roll & Chowder Station',   rate: 15 },
    savory_omelette:        { label: 'Omelette Station',                 rate: 12 },
    savory_paella:          { label: 'Paella Valenciana Station',         rate: 12 },
    savory_pizza:           { label: 'Hand-Tossed Pizza Station',        rate: 12 },
    savory_raw_bar:         { label: 'Raw Bar Station',                  rate: 25 },
    savory_sushi:           { label: 'Sushi Station',                    rate: 25 },
    savory_taco:            { label: 'Taco Station',                     rate: 12 },
    savory_potato:          { label: 'Twice-Baked Potato Station',       rate: 12 },
    savory_vegetarian:      { label: 'Vegetarian Station',               rate: 12 },
  };

  // Sweet Station — flat per-person rate, charged per station added (qty)
  var SWEET_STATION_RATE = 10;

  // Espresso Martinis — only offered on bar packages with a full liquor bar.
  // Premium Open Bar includes it at no extra charge (handled via card gating,
  // never added to state.barEnhancements while barPackage === 'bar_premium').
  var ESPRESSO_MARTINI_ALLOWED_PACKAGES = ['bar_premium', 'bar_cocktails', 'wine_beer'];

  /** Espresso Martini per-person rate for the currently selected bar package */
  function getEspressoMartiniRate() {
    var RATES = window.QuoteRates || {};
    if (state.barPackage === 'wine_beer') return RATES.barEnhancements?.bar_espresso_martinis_wine_beer || 0;
    return RATES.barEnhancements?.bar_espresso_martinis || 0;
  }

  /* ------------------------------------------------------------------
     EVENT ENHANCEMENTS — "Extend Your Event One Hour"
     Owner-set 2026-08-31. ⛔ These are the owner's numbers; do not re-derive.

     PRICE = room component + HALF the selected bar package's per-person rate,
     per person, per hour.
     🔴 ⛔ THE BREAKDOWN IS NEVER SHOWN TO THE CLIENT — owner explicit. One
     line, one price. Nothing in the cart, the summary, the proposal text or
     the PDF may print the two components separately.

     Three readings this build had to take, all stated rather than guessed:

     ① WHICH BAR / WHAT IF NONE. The bar package is REQUIRED by the Step 4
       validator, and all five options carry a billed per-person rate (Cash Bar
       bills at the soft-drinks rate), so growth's "no bar" branch — the one
       that breaks even at ~17 guests — CANNOT OCCUR in this builder. There is
       no unavailable-with-no-bar case to handle; the half-bar term is always
       defined. (Growth's measured answer:
       ~/TR/projects/growth/analysis/2026-08-31-extend-an-hour-marginal-cost.md)

     ② WHERE THE ROUNDING HAPPENS. Per person FIRST, then multiply by billed
       guests. Every other rate in this system is a whole-dollar per-person
       figure and Exhibit E prints per-person rates, so rounding after the
       multiply would print a rate whose own arithmetic did not reproduce the
       total. Half-up: Premium 39/2 = 19.50 + 8 = 27.50 → $28/pp.

     ③ WHAT ELSE EXTENDING RE-PRICES. Nothing. No other rate in this builder is
       hourly — cocktail_extend_30 is a flat per-person add-on, room fees and
       staff are flat per event, and growth confirmed the bridal suite is flat
       per event too. The extended hour also carries NO FOOD, which is true by
       construction: the price is room + bar and no food line scales with it.

     🔴 AND THE GATE — see EVENING_SLOT_ONLY_ADDONS below. That one is worth
     more than the price.
  ------------------------------------------------------------------ */

  /** Per-person, per-hour rate for the event extension, rounded to whole
   *  dollars. Returns 0 when no bar package is selected yet (the card is not
   *  reachable before Step 4 is valid, but the estimate can be recomputed at
   *  any time). */
  function getExtendHourRate() {
    var RATES = window.QuoteRates || {};
    var roomPart = RATES.eventAddons?.extend_hour_room ?? 0;
    if (!state.barPackage) return 0;
    var barPart = state.barPackage === 'bar_cash'
      ? (RATES.bar?.['bar_soft_drinks'] ?? 0)
      : (RATES.bar?.[state.barPackage] ?? 0);
    return Math.round(roomPart + (barPart / 2));
  }

  /* 🔴 THE CALENDAR GUARD, and ⛔ do not weaken it without real availability data.
     A room turn is worth ~$3,737 GP against ~$1,513 at the median event, so an
     extension sold into a booked next turn destroys about 2.5x what it earns.
     NOTHING in this builder reads a calendar — there is no availability source
     on this front at all — so the only guard that exists is the venue's own
     slot grid: the AM/afternoon slots end 3-5 PM and the evening turns START
     5-7 PM, so extending one of them can collide with a turn that may already
     be booked. The three evening slots end 9-11 PM and no slot starts after
     them, so extending those cannot block anything.
     ⛔ Do NOT widen this list. Widen it only by adding a real availability
     check; until then AM/afternoon guests are routed to the existing
     "make a note below" path, which puts a human in the loop. */
  var EVENING_TIME_SLOTS = ['5:00 PM - 9:00 PM', '6:00 PM - 10:00 PM', '7:00 PM - 11:00 PM'];

  function isEveningSlot(slot) {
    return EVENING_TIME_SLOTS.indexOf(slot) !== -1;
  }

  // Pastries / Cookies / Fresh Fruit dessert add-ons — priced as a bundle by
  // count selected, not summed individually (hardcoded tier, like Sweet Station).
  var DESSERT_TRIO_KEYS = ['dessert_addon_pastries', 'dessert_addon_cookies', 'dessert_addon_fruit'];
  var DESSERT_TRIO_TIER_RATES = { 1: 7, 2: 10, 3: 12 };
  var DESSERT_TRIO_SHORT_LABELS = {
    dessert_addon_pastries: 'Pastries',
    dessert_addon_cookies:  'Cookies',
    dessert_addon_fruit:    'Fresh Fruit',
  };

  /** Number of the pastries/cookies/fruit trio currently selected */
  function getDessertTrioCount() {
    return DESSERT_TRIO_KEYS.filter(function (k) { return (state.dessertAddons || []).indexOf(k) !== -1; }).length;
  }

  /** Combined per-person rate for the currently selected trio items */
  function getDessertTrioRate() {
    return DESSERT_TRIO_TIER_RATES[getDessertTrioCount()] || 0;
  }

  /** Human-readable label listing the selected trio items, e.g. "Pastries & Cookies" */
  function getDessertTrioLabel() {
    var picks = DESSERT_TRIO_KEYS.filter(function (k) { return (state.dessertAddons || []).indexOf(k) !== -1; })
      .map(function (k) { return DESSERT_TRIO_SHORT_LABELS[k]; });
    var joined;
    if (picks.length <= 1) {
      joined = picks[0] || '';
    } else if (picks.length === 2) {
      joined = picks.join(' & ');
    } else {
      joined = picks.slice(0, -1).join(', ') + ' & ' + picks[picks.length - 1];
    }
    return joined + ' (added to dessert)';
  }

  /** Total savory station rate across all current selections */
  function getSavoryRate() {
    return (state.savoryStations || []).reduce(function (sum, k) {
      return sum + (SAVORY_STATIONS[k] ? SAVORY_STATIONS[k].rate : 0);
    }, 0);
  }

  /** Human-readable label listing all selected savory stations */
  function getSavoryLabel() {
    var picks = (state.savoryStations || []).filter(function (k) { return SAVORY_STATIONS[k]; });
    if (!picks.length) return 'Savory Station (Cocktail Hour)';
    return picks.map(function (k) { return SAVORY_STATIONS[k].label; }).join(', ');
  }

  /** Display label for a dessert add-on, expanding the Sweet Station quantity
      or the pastries/cookies/fruit trio into its combined label */
  function dessertAddonDisplay(k) {
    if (k === 'dessert_addon_sweet_station') {
      var q = Math.max(1, state.sweetStationQty || 1);
      return q > 1 ? 'Sweet Station (×' + q + ')' : 'Sweet Station';
    }
    if (DESSERT_TRIO_KEYS.indexOf(k) !== -1) return getDessertTrioLabel();
    return dessertAddonLabels[k] || k;
  }

  /** Full list of dessert add-on display strings — collapses the pastries/
      cookies/fruit trio into a single combined entry instead of 3 separate ones */
  function getDessertAddonDisplayList() {
    var seenTrio = false;
    var out = [];
    (state.dessertAddons || []).forEach(function (k) {
      if (DESSERT_TRIO_KEYS.indexOf(k) !== -1) {
        if (!seenTrio) { out.push(getDessertTrioLabel()); seenTrio = true; }
        return;
      }
      out.push(dessertAddonDisplay(k));
    });
    return out;
  }

  /** Display label for a bar enhancement, expanding the Mocktail Bar scope */
  function barEnhancementDisplay(k) {
    if (k === 'bar_add_mocktail') {
      return state.mocktailScope === 'kids' ? 'Mocktail Bar (Kids Only)' : 'Mocktail Bar (All Guests)';
    }
    return barEnhancementLabels[k] || k;
  }

  /** Determine AM/PM from a time slot string based on END time.
   *  Ends by 6 PM (18:00) → 'am';  ends after 6 PM → 'pm'
   *  Input example: "5:00 PM - 9:00 PM" */
  function getTimePeriod(timeSlot) {
    if (!timeSlot || !timeSlot.includes(' - ')) return null;
    var endPart = timeSlot.split(' - ')[1].trim();
    var match = endPart.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    var h = parseInt(match[1], 10);
    var period = match[3].toUpperCase();
    var h24 = h;
    if (period === 'PM' && h !== 12) h24 += 12;
    if (period === 'AM' && h === 12) h24 = 0;
    return h24 <= 18 ? 'am' : 'pm';
  }

  /** Get lowercase day name from a YYYY-MM-DD date string */
  function getDayName(dateStr) {
    if (!dateStr) return null;
    var d = new Date(dateStr + 'T00:00:00');
    return ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][d.getDay()];
  }

  /** Look up the minimum for a room given current state.
   *  Returns { guests: N, dollars: N } or null if no minimum applies. */
  function getRoomMinimum(roomKey) {
    var roomMins = BOOKING_MINIMUMS[roomKey];
    if (!roomMins) return null;
    var day = getDayName(state.date);
    var period = getTimePeriod(state.startTime);
    if (!day || !period) return null;
    var dayData = roomMins[day];
    if (!dayData) return null;
    return dayData[period] || null;
  }

  /* ------------------------------------------------------------------
     PERSISTENT SAVE (localStorage — survives browser close, 30-day TTL)
  ------------------------------------------------------------------ */
  var STORAGE_KEY = 'tr_quote_state';
  var STORAGE_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days in ms

  // One-time migration from sessionStorage → localStorage
  try {
    var oldSession = sessionStorage.getItem(STORAGE_KEY);
    if (oldSession && !localStorage.getItem(STORAGE_KEY)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state: JSON.parse(oldSession), savedAt: Date.now() }));
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) { /* noop */ }

  var _saveTimer = null;
  function saveState() {
    /* Debounce localStorage writes — JSON.stringify + setItem blocks main thread */
    if (_saveTimer) return;
    _saveTimer = setTimeout(function() {
      _saveTimer = null;
      try {
        var payload = { state: state, savedAt: Date.now() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (e) { /* quota or private mode */ }
    }, 200);
  }

  function restoreState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var payload = JSON.parse(raw);
      // Expire after 30 days
      if (payload.savedAt && (Date.now() - payload.savedAt > STORAGE_TTL)) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      var data = payload.state || payload; // backward compat with old sessionStorage format
      Object.keys(data).forEach(function(k) { if (k in state) state[k] = data[k]; });
      return true;
    } catch (e) { return false; }
  }

  function clearSavedState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
  }

  function hasSavedState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var payload = JSON.parse(raw);
      if (payload.savedAt && (Date.now() - payload.savedAt > STORAGE_TTL)) {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      var data = payload.state || payload;
      return data.currentStep > 1 || !!data.eventType;
    } catch (e) { return false; }
  }

  function applyStateToUI() {
    // Step 1 — event type text input
    var eti = getEl('[data-input-event-type]');
    if (eti && state.eventType) {
      eti.value = state.eventType;
    }
    var ai = getEl('[data-input-adults]');   if (ai && state.adults)   ai.value = state.adults;
    var ci = getEl('[data-input-children]'); if (ci && state.children) ci.value = state.children;
    var ni = getEl('[data-input-notes]');    if (ni && state.notes)    ni.value = state.notes;

    // Step 2 — date/time
    var di = getEl('[data-input-date]');
    if (di && state.date) di.value = state.date;
    var fc = getEl('[data-input-flexible]');
    var fd = getEl('[data-flexible-days]');
    if (fc && state.flexibleDate) { fc.checked = true; if (fd) fd.hidden = false; }
    state.dayPreferences.forEach(function(d) {
      var dp = getEl('[data-input-day-pref][value="' + d + '"]');
      if (dp) dp.checked = true;
    });
    if (state.startTime) {
      var stc = getEl('[data-time-slot="' + state.startTime + '"]');
      if (stc) stc.classList.add('is-selected');
    }
    var dtni = getEl('[data-input-datetime-notes]');
    if (dtni && state.dateTimeNotes) dtni.value = state.dateTimeNotes;

    // Step 4 — food
    if (state.horsOeuvres) {
      var hc = getEl('[data-hors-card="' + state.horsOeuvres + '"]');
      if (hc) hc.classList.add('is-selected');
    }
    state.courses.forEach(function(k) {
      var cc = getEl('[data-course-card="' + k + '"]');
      if (cc) cc.classList.add('is-selected');
    });
    if (state.mainCourse) {
      var mc = getEl('[data-main-card="' + state.mainCourse + '"]');
      if (mc) mc.classList.add('is-selected');
      updateAfterDinnerAvailability();
      updatePlatedCourses();
    }
    updateMimosaHints();
    updateChildrensMenuAvailability();
    updateEventAddonAvailability();
    refreshStationsGate();
    (state.cocktailAddons || []).forEach(function(k) {
      var cac = getEl('[data-cocktail-addon-card="' + k + '"]');
      if (cac) cac.classList.add('is-selected');
      // Restore savory checklist visibility + checked stations
      if (k === 'cocktail_savory_station') {
        var sc = getEl('[data-savory-checklist]');
        if (sc) {
          sc.style.display = 'block';
          (state.savoryStations || []).forEach(function (sk) {
            var cb = getEl('[data-savory-check][value="' + sk + '"]');
            if (cb) cb.checked = true;
          });
        }
      }
    });
    (state.entreeAddons || []).forEach(function(k) {
      var eac = getEl('[data-entree-addon-card="' + k + '"]');
      if (eac) eac.classList.add('is-selected');
    });
    (state.eventAddons || []).forEach(function(k) {
      var evc = getEl('[data-event-addon-card="' + k + '"]');
      if (evc) evc.classList.add('is-selected');
    });
    if (state.dessertBase) {
      var dc = getEl('[data-dessert-card="' + state.dessertBase + '"]');
      if (dc) dc.classList.add('is-selected');
    }
    state.dessertAddons.forEach(function(k) {
      var dac = getEl('[data-dessert-addon-card="' + k + '"]');
      if (dac) dac.classList.add('is-selected');
      // Restore sweet-station stepper visibility + quantity
      if (k === 'dessert_addon_sweet_station') {
        var sw = getEl('[data-sweet-stepper]');
        if (sw) sw.style.display = 'flex';
        if (!state.sweetStationQty) state.sweetStationQty = 1;
        var sq = getEl('[data-sweet-qty]');
        if (sq) sq.textContent = state.sweetStationQty;
      }
    });
    if (state.afterDinner) {
      var ad = getEl('[data-afterdinner-card="' + state.afterDinner + '"]');
      if (ad) ad.classList.add('is-selected');
    }
    var mni = getEl('[data-input-menu-notes]');
    if (mni && state.menuNotes) mni.value = state.menuNotes;

    // Step 5 — additional requests (enhancements)
    var ari = getEl('[data-input-additional-requests]');
    if (ari && state.additionalRequests) ari.value = state.additionalRequests;

    // Step 4 — bar (part of food step)
    if (state.barPackage) {
      var bc = getEl('[data-bar-card="' + state.barPackage + '"]');
      if (bc) bc.classList.add('is-selected');
      // Restore Cocktail Requests status line (Wine & Beer only)
      updateCocktailHandlingStatus();
      // Sync Aperol & Hugo Spritz card disabled state with restored bar package
      var spritzRestore = getEl('[data-wine-beer-only][data-bar-enhancement-card="bar_spritz_upgrade"]');
      if (spritzRestore) {
        if (state.barPackage === 'wine_beer') {
          spritzRestore.classList.remove('is-disabled');
        } else {
          spritzRestore.classList.add('is-disabled');
        }
      }
    }
    state.barEnhancements.forEach(function(k) {
      var bec = getEl('[data-bar-enhancement-card="' + k + '"]');
      if (bec) bec.classList.add('is-selected');
      // Restore mocktail scope selector + chosen option
      if (k === 'bar_add_mocktail') {
        var ms = getEl('[data-mocktail-scope]');
        if (ms) ms.style.display = 'block';
        var opt = getEl('[data-mocktail-scope-opt][value="' + (state.mocktailScope || 'all') + '"]');
        if (opt) opt.checked = true;
        updateMocktailScopeAvailability();
      }
    });
    updateEspressoMartiniAvailability();

    // Step 3 — rooms
    state.rooms.forEach(function(k) {
      var rc = getEl('[data-room-card="' + k + '"]');
      if (rc) rc.classList.add('is-selected');
    });

    // Step 6 — lead capture
    var fni = getEl('[data-input-firstname]'); if (fni && state.firstName) fni.value = state.firstName;
    var lni = getEl('[data-input-lastname]');  if (lni && state.lastName)  lni.value = state.lastName;
    var emi = getEl('[data-input-email]');     if (emi && state.email)     emi.value = state.email;
    var phi = getEl('[data-input-phone]');     if (phi && state.phone)     phi.value = state.phone;
    var rfi = getEl('[data-input-referral]');  if (rfi && state.referral)  rfi.value = state.referral;
    var fui = getEl('[data-input-followup-intent]'); if (fui && state.followUpIntent) fui.value = state.followUpIntent;

    // Restored state must use the same readiness rule as live edits.
    var proposalBtn = getEl('[data-send-proposal]');
    if (proposalBtn) {
      var restoredReady = isProposalReady();
      proposalBtn.disabled = !restoredReady;
      var restoredHint = getEl('[data-send-proposal-hint]');
      if (restoredHint) restoredHint.style.display = restoredReady ? 'none' : '';
    }
  }

  /* ------------------------------------------------------------------
     CALCULATION ENGINE
  ------------------------------------------------------------------ */

  /* ── Label lookup objects ── */
  const horsOeuvresLabels = {
    hors_doeuvres_6_full: "Hors d'Oeuvres — 6 choices, 1 hour",
    hors_doeuvres_4_half: "Hors d'Oeuvres — 4 choices, 30 min",
    hors_doeuvres_heavy:  "Heavily Passed Hors d'oeuvres — Heavier selections, 2.5 hrs",
  };
  const courseLabels = {
    pasta_course: 'Pasta Course',
    salad_course: 'Salad Course',
  };
  const mainCourseLabels = {
    entree_plated:       'Plated Entrée — 4 choices, 2 sides, bread & butter',
    brunch_buffet:       'Brunch Buffet — omelette station, baked goods & fruit',
    lunch_dinner_buffet: 'Buffet',
    hors_doeuvres_heavy: "Heavily Passed Hors d'oeuvres — 2.5 hrs, serves as main course",
    food_stations:       'Food Stations — interactive culinary stations',
  };
  const dessertBaseLabels = {
    dessert_cake:              'Celebration Cake',
    dessert_pastries:          'Selection of Pastries',
    dessert_creme_brulee:      'Crème Brûlée',
    dessert_tiramisu:          'Tiramisu',
    dessert_gf_cheesecake:     'GF Cheesecake',
    dessert_brownie_ice_cream: 'Brownie a la Mode',
    dessert_tres_leches:       'Tres Leches',
    dessert_bread_pudding:     'Bread Pudding',
    dessert_apple_pie:         'Apple Pie a la Mode',
  };
  const cocktailAddonLabels = {
    cocktail_savory_station: 'Savory Station (Cocktail Hour)',
    cocktail_extend_30:      'Extend Hors d\'Oeuvres 30 Min',
    ceremony_setup:          'Ceremony (+30 min)',
  };
  const entreeAddonLabels = {
    entree_lobster_tails:  'Add Lobster Tails',
    entree_filet_mignon:   'Upgrade to Filet Mignon',
    entree_childrens_menu: 'Dedicated Children\'s Menu',
  };
  const eventAddonLabels = {
    extend_hour: 'Extend Your Event One Hour',
  };
  const dessertAddonLabels = {
    dessert_addon_pastries:       'Pastries (added to dessert)',
    dessert_addon_cookies:        'Cookies (added to dessert)',
    dessert_addon_fruit:          'Fresh Fruit (added to dessert)',
    dessert_addon_sweet_station:  'Sweet Station',
  };
  const afterDinnerLabels = {
    coffee_tea:             'Coffee & Tea',
    coffee_espresso_dinner: 'Espresso & Cappuccino',
    coffee_espresso_brunch: 'Espresso Bar (Brunch)',
  };
  const barLabels = {
    bar_soft_drinks: 'Soft Drinks, Juice & Mixers',
    wine_beer:       'Wine & Beer, Soft Drinks, Juice & Mixers',
    wine_beer_tab:   'Wine & Beer + Cocktail Tab, Soft Drinks, Juice & Mixers',
    bar_cocktails:   'Cocktails, Wine & Beer, Soft Drinks, Juice & Mixers',
    bar_premium:     'Premium Cocktails, Wine & Beer, Soft Drinks, Juice & Mixers',
    bar_cash:        'Cash Bar',
  };
  const barEnhancementLabels = {
    bar_wine_table:        'Wine Service at Table',
    bar_champagne_wall:    'Champagne Wall',
    bar_sparkling_toast:   'Sparkling Wine Toast',
    bar_espresso_martinis: 'Espresso Martinis',
    bar_add_mocktail:      'Add Mocktail Bar',
    bar_spritz_upgrade:    'Aperol & Hugo Spritz',
  };

  /* Memoization: cache estimate result until state changes */
  var _estimateCache = null;
  var _estimateCacheKey = null;
  function invalidateEstimateCache() { _estimateCacheKey = null; _estimateCache = null; }

  function calculateEstimate() {
    /* Build a lightweight cache key from the fields that affect pricing */
    var ck = '' + state.adults + '|' + state.children + '|' + state.mainCourse + '|' + state.horsOeuvres
      + '|' + state.barPackage + '|' + (state.cocktailAddons||[]).join(',')
      + '|' + (state.courses||[]).join(',') + '|' + (state.entreeAddons||[]).join(',')
      + '|' + (state.eventAddons||[]).join(',')
      + '|' + state.dessertBase + '|' + (state.dessertAddons||[]).join(',')
      + '|' + state.afterDinner + '|' + (state.barEnhancements||[]).join(',')
      + '|' + (state.rooms||[]).join(',') + '|' + (state.savoryStations||[]).join(',')
      + '|' + state.mocktailScope + '|' + state.sweetStationQty;
    if (_estimateCacheKey === ck && _estimateCache) return _estimateCache;

    const RATES = window.QuoteRates || {};

    const roomRates   = RATES.rooms       || {};
    const adminFeePct = RATES.adminFeePct ?? 0;

    // ── Build line items ──
    const lineItems = []; // { key, label, rate, isBar }

    if (state.horsOeuvres && (RATES.horsOeuvres?.[state.horsOeuvres] > 0)) {
      lineItems.push({
        key:   state.horsOeuvres,
        label: horsOeuvresLabels[state.horsOeuvres] || state.horsOeuvres,
        rate:  RATES.horsOeuvres[state.horsOeuvres],
        isBar: false,
      });
    }

    state.cocktailAddons.forEach(key => {
      if (key === 'cocktail_savory_station') {
        // One line item per selected savory station, each at its own price
        (state.savoryStations || []).forEach(function (sk) {
          var s = SAVORY_STATIONS[sk];
          if (s && s.rate > 0) {
            lineItems.push({ key: sk, label: s.label, rate: s.rate, isBar: false });
          }
        });
      } else if (RATES.cocktailAddons?.[key] > 0) {
        lineItems.push({
          key,
          label: cocktailAddonLabels[key] || key,
          rate:  RATES.cocktailAddons[key],
          isBar: false,
        });
      }
    });

    state.courses.forEach(key => {
      if (RATES.courses?.[key] > 0) {
        lineItems.push({
          key,
          label: courseLabels[key] || key,
          rate:  RATES.courses[key],
          isBar: false,
        });
      }
    });

    if (state.mainCourse && (RATES.mainCourse?.[state.mainCourse] > 0)) {
      lineItems.push({
        key:   state.mainCourse,
        label: mainCourseLabels[state.mainCourse] || state.mainCourse,
        rate:  RATES.mainCourse[state.mainCourse],
        isBar: false,
      });
    }

    state.entreeAddons.forEach(key => {
      if (RATES.entreeAddons?.[key] > 0) {
        lineItems.push({
          key,
          label: entreeAddonLabels[key] || key,
          rate:  RATES.entreeAddons[key],
          isBar: false,
          childOnly: key === 'entree_childrens_menu',
        });
      }
    });

    if (state.dessertBase && (RATES.dessertBase?.[state.dessertBase] > 0)) {
      lineItems.push({
        key:   state.dessertBase,
        label: dessertBaseLabels[state.dessertBase] || state.dessertBase,
        rate:  RATES.dessertBase[state.dessertBase],
        isBar: false,
      });
    }

    state.dessertAddons.forEach(key => {
      if (key === 'dessert_addon_sweet_station') {
        // Flat $10/pp per station added (quantity)
        var qty = Math.max(0, state.sweetStationQty || 0);
        if (qty > 0) {
          lineItems.push({
            key,
            label: qty > 1 ? 'Sweet Station (×' + qty + ')' : 'Sweet Station',
            rate:  SWEET_STATION_RATE * qty,
            isBar: false,
          });
        }
      } else if (DESSERT_TRIO_KEYS.indexOf(key) !== -1) {
        // Handled once, combined, below — not summed per-item
      } else if (RATES.dessertAddons?.[key] > 0) {
        lineItems.push({
          key,
          label: dessertAddonLabels[key] || key,
          rate:  RATES.dessertAddons[key],
          isBar: false,
        });
      }
    });

    // Pastries / Cookies / Fresh Fruit — priced as a single bundle by count
    // selected (1=$7, 2=$10, 3=$12), not summed individually.
    var dessertTrioCount = getDessertTrioCount();
    if (dessertTrioCount > 0) {
      lineItems.push({
        key:   'dessert_addon_trio',
        label: getDessertTrioLabel(),
        rate:  DESSERT_TRIO_TIER_RATES[dessertTrioCount],
        isBar: false,
      });
    }

    if (state.afterDinner && (RATES.afterDinner?.[state.afterDinner] > 0)) {
      lineItems.push({
        key:   state.afterDinner,
        label: afterDinnerLabels[state.afterDinner] || state.afterDinner,
        rate:  RATES.afterDinner[state.afterDinner],
        isBar: false,
        adultOnly: true,   // coffee/tea/espresso — charged to adults only, never children
      });
    }

    if (state.barPackage) {
      const barPkgRate = state.barPackage === 'bar_cash' ? (RATES.bar?.['bar_soft_drinks'] ?? 0) : (RATES.bar?.[state.barPackage] ?? 0);
      if (barPkgRate > 0 || state.barPackage === 'bar_cash') {
        var barLabelKey = (state.barPackage === 'wine_beer' && state.cocktailTab) ? 'wine_beer_tab' : state.barPackage;
        lineItems.push({
          key:   state.barPackage,
          label: barLabels[barLabelKey] || barLabels[state.barPackage] || state.barPackage,
          rate:  barPkgRate,
          isBar: true,
        });
      }
    }

    state.barEnhancements.forEach(key => {
      if (key === 'bar_add_mocktail') {
        // Scope-aware: 'all' = every guest pays full; 'kids' = children only (min 10)
        var mockRate = RATES.barEnhancements?.['bar_add_mocktail'] || 0;
        if (mockRate > 0) {
          if (state.mocktailScope === 'kids') {
            // Kids-only: charged to children only, requires ≥10 children
            if ((state.children || 0) >= 10) {
              lineItems.push({ key, label: 'Mocktail Bar (Kids Only)', rate: mockRate, isBar: false, childOnly: true });
            }
          } else {
            // Kids & Adults: every guest pays the full rate
            lineItems.push({ key, label: 'Mocktail Bar (All Guests)', rate: mockRate, isBar: false, everyone: true });
          }
        }
      } else if (key === 'bar_espresso_martinis') {
        // Tiered by bar package: $12 on Wine & Beer, $8 on Full Open Bar.
        // Never present here for Premium (included, not a toggleable add-on)
        // or Cash Bar/Soft Drinks (not offered — gated at the card level).
        var martiniRate = getEspressoMartiniRate();
        if (martiniRate > 0) {
          lineItems.push({ key, label: barEnhancementLabels[key] || key, rate: martiniRate, isBar: true });
        }
      } else if (RATES.barEnhancements?.[key] > 0) {
        lineItems.push({
          key,
          label: barEnhancementLabels[key] || key,
          rate:  RATES.barEnhancements[key],
          isBar: true,
        });
      }
    });

    // Event-level enhancements. `isEvent` keeps them OUT of the Food & Catering
    // group on Exhibit E — they are neither food nor bar. The rate is a single
    // composed figure; ⛔ the room/bar split is never exposed.
    // Bucket-wise they fall through to adultFood, which is the default for
    // anything with no special rule: adults pay it in full and children pick it
    // up through the standard child blend.
    state.eventAddons.forEach(key => {
      var eventRate = key === 'extend_hour' ? getExtendHourRate() : 0;
      if (eventRate > 0) {
        lineItems.push({
          key,
          label: eventAddonLabels[key] || key,
          rate:  eventRate,
          isBar: false,
          isEvent: true,
        });
      }
    });

    // ── Per-person totals ──
    // Buckets:
    //   adultFood  — shared food: adults full; children per the rule below
    //   bar        — bar packages/enhancements (alcohol): adults full; NEVER counted toward child pricing
    //   adultOnly  — after-dinner coffee/espresso: adults only, NEVER children
    //   childOnly  — kids' menu, kids-only mocktail: children only
    //   everyone   — mocktail "Kids & Adults": every guest pays full (both adult & child)
    const adultFoodRate  = lineItems.filter(i => !i.isBar && !i.childOnly && !i.adultOnly && !i.everyone).reduce((s, i) => s + i.rate, 0);
    const childOnlyRate  = lineItems.filter(i => i.childOnly).reduce((s, i) => s + i.rate, 0);
    const adultOnlyRate  = lineItems.filter(i => i.adultOnly).reduce((s, i) => s + i.rate, 0);
    const everyoneRate   = lineItems.filter(i => i.everyone).reduce((s, i) => s + i.rate, 0);
    const barRate        = lineItems.filter(i =>  i.isBar).reduce((s, i) => s + i.rate, 0);
    const adultRate      = adultFoodRate + barRate + adultOnlyRate + everyoneRate;

    // Child rate — REVISED 2026-08-18 (owner instruction). The 60% is a discount off what
    // an ADULT actually pays, not off a stripped-down food subtotal:
    //   ≤20% of ADULT headcount (children ÷ adults) → 60% of the FULL adult per-person rate
    //       (shared food + bar + after-dinner), rounded up, + child-only addons + everyone-full items.
    //   >20% of adult headcount ("exceeds 20%")      → shared food only — bar/alcohol (isBar) and
    //       after-dinner coffee/espresso (adultOnly) drop out — + child-only addons + everyone-full
    //       items, FLOORED at the ≤20% blended rate.
    //   Why the floor: once bar sits in the blend base, a food-light/bar-heavy build could price the
    //   food-only branch BELOW the blended one, so adding one child would have CUT the child rate
    //   (e.g. plated $37 food + $63 bar: $60/child at 19%, $37/child at 23.8%). The floor keeps the
    //   child rate monotonic in child count. Owner decision 2026-08-18; no effect on food-heavy builds.
    //   PRIOR BEHAVIOR (until 2026-08-18): the blend base was adultFoodRate alone, which priced a
    //   child at $48 against a $102 adult on a normal wedding build — too low.
    //   'everyone' items (Kids & Adults mocktail bar) stay OUT of the blend and are added at face
    //   value in both branches: they are defined as full price for every guest, adult or child.
    const actualChildren = state.children || 0;
    const actualAdults   = state.adults   || 0;
    const childPct       = actualAdults > 0 ? actualChildren / actualAdults : (actualChildren > 0 ? Infinity : 0);
    const childBlendBase = adultFoodRate + barRate + adultOnlyRate; // == adultRate less everyone-full items
    const childBlended   = Math.ceil(childBlendBase * 0.60) + childOnlyRate + everyoneRate;
    const childFoodOnly  = adultFoodRate + childOnlyRate + everyoneRate;
    const childRate      = actualChildren === 0    ? childFoodOnly
                         : childPct <= 0.20        ? childBlended
                         : Math.max(childFoodOnly, childBlended);

    // Is this child paying MORE than the blended rate — i.e. did the food-only
    // branch actually win? The contract's minimum guaranteed head count switches
    // from an ADULT basis to a TOTAL basis on this flag (owner decision
    // 2026-09-09; spec/minimum-headcount-basis.md).
    // ⛔ THIS IS NOT `childPct > 0.20`, and the difference is a live exploit.
    //   The Math.max floor above means >20% children can return EXACTLY
    //   childBlended on a bar-heavy build — no elevation at all. Worked case:
    //   food $37 + bar $63 -> adult $100, blended $60, foodOnly $37; the child
    //   pays $60 at 19% AND at 24%. A percentage test would hand that client the
    //   loose total-based guarantee AND the cheap blended rate, which is the one
    //   combination the rule exists to prevent — and it is reachable on purpose
    //   by over-stating children, because crossing 20% costs them nothing here.
    // The zero-children guard matters: that branch assigns childFoodOnly, which
    // CAN exceed childBlended, and without it a childless party would lose the
    // word "Adult" from a box whose number is an adult count.
    const childRateElevated = actualChildren > 0 && childRate > childBlended;

    const roomFee   = state.rooms.reduce((sum, r) => sum + (roomRates[r] ?? 0), 0);

    // Enforce 25-adult-rate minimum:
    // - If total < 25, bill as 25 adults (minimum applies)
    // - If adults < 25 but total >= 25, first 25 at adult rate
    let billedAdults   = state.adults   || 0;
    let billedChildren = state.children || 0;
    const totalGuests  = billedAdults + billedChildren;
    if (totalGuests < 25) {
      billedAdults = 25;
      billedChildren = 0;
    } else if (billedAdults < 25 && totalGuests >= 25) {
      billedAdults = 25;
      billedChildren = totalGuests - 25;
    }

    const subtotal  = (billedAdults   * adultRate)
                    + (billedChildren * childRate)
                    + roomFee;

    const adminFee  = adminFeePct > 0 ? Math.round(subtotal * (adminFeePct / 100)) : 0;
    const total     = subtotal + adminFee;
    const guestCount = (state.adults || 0) + (state.children || 0);

    // billedAdults/billedChildren are exposed because the proposal and the
    // contract PDF have to print the counts the SUBTOTAL was computed from.
    // Printing the entered counts beside a minimum-derived total produced a
    // price schedule whose rows did not add up to its own total.
    _estimateCache = { lineItems, adultRate, childRate, childRateElevated, subtotal, adminFee, adminFeePct, total, guestCount,
                       billedAdults, billedChildren };
    _estimateCacheKey = ck;
    return _estimateCache;
  }

  /* ------------------------------------------------------------------
     GA4 FUNNEL TRACKING
  ------------------------------------------------------------------ */
  function trackGA4(eventName, params) {
    if (typeof window.gtag === 'function') {
      // ⛔ Never let a gtag throw (blocked/broken tag, ad-blocker stub) escape into a
      // caller's control flow. submitProposal() awaits this after a Worker success is
      // already durable; an uncaught throw here would reject that promise and strand
      // the lead-modal path's `await submitProposal()` before it closes the modal or
      // runs the deferred action — a captured lead that LOOKS hung to the visitor.
      try {
        window.gtag('event', eventName, params || {});
      } catch (err) {
        console.error('GA4 tracking error:', eventName, err);
      }
    }
  }

  function getStepParams(stepNumber) {
    var base = { event_type: state.eventType || '', step: stepNumber };
    var gc = (state.adults || 0) + (state.children || 0);
    switch (stepNumber) {
      case 1:
        base.guest_count = gc;
        base.adults = state.adults || 0;
        base.children = state.children || 0;
        break;
      case 2:
        base.guest_count = gc;
        base.event_date = state.date || '';
        base.day_of_week = getDayName(state.date) || '';
        base.start_time = state.startTime || '';
        break;
      case 3:
        base.guest_count = gc;
        base.rooms = state.rooms.join(',') || '';
        break;
      case 4:
        base.guest_count = gc;
        base.main_course = state.mainCourse || '';
        base.bar_package = state.barPackage || '';
        break;
      case 5:
        try { var e5 = calculateEstimate(); base.estimated_total = e5.total; } catch (_) {}
        base.guest_count = gc;
        break;
      case 6:
        try { var e6 = calculateEstimate(); base.estimated_total = e6.total; } catch (_) {}
        base.guest_count = gc;
        break;
    }
    return base;
  }

  /* ------------------------------------------------------------------
     STEP NAVIGATION
  ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     STEP 6 ESTIMATE GATE
     The itemized pricing + proposal CTAs live inside [data-estimate-reveal]
     and stay hidden until the visitor submits their contact info (which
     sends the proposal email). [data-estimate-gate] holds the "enter your
     info" prompt shown while locked. A returning visitor with
     state.leadCaptured === true is unlocked on arrival (no re-send).
  ------------------------------------------------------------------ */
  function lockEstimate() {
    var reveal = getEl('[data-estimate-reveal]');
    var gate = getEl('[data-estimate-gate]');
    if (reveal) reveal.style.display = 'none';
    if (gate) gate.style.display = '';
  }

  function unlockEstimate() {
    var reveal = getEl('[data-estimate-reveal]');
    var gate = getEl('[data-estimate-gate]');
    if (reveal) reveal.style.display = '';
    if (gate) gate.style.display = 'none';
  }

  function showStep(stepNumber) {
    const steps = getEls('[data-step]');
    steps.forEach(el => {
      el.classList.toggle('is-active', parseInt(el.dataset.step, 10) === stepNumber);
    });

    // Update progress indicators
    const progressSteps = getEls('[data-progress-step]');
    progressSteps.forEach(el => {
      const n = parseInt(el.dataset.progressStep, 10);
      el.classList.remove('is-active', 'is-complete');
      if (n === stepNumber) el.classList.add('is-active');
      if (n < stepNumber)   el.classList.add('is-complete');
    });

    // Update back button visibility
    const backBtn = getEl('[data-quote-back]');
    if (backBtn) {
      backBtn.style.visibility = stepNumber > 1 ? 'visible' : 'hidden';
    }

    // Pricing teaser — only visible on Step 1
    var teaser = getEl('[data-pricing-teaser]');
    if (teaser) teaser.style.display = stepNumber === 1 ? '' : 'none';

    // Update next button label — hide it on final step, show Back so user can go back and edit
    const nextBtn = getEl('[data-quote-next]');
    if (nextBtn) {
      if (stepNumber === state.totalSteps) {
        nextBtn.style.display = 'none';
      } else {
        nextBtn.style.display = '';
        nextBtn.textContent = stepNumber === state.totalSteps - 1
          ? 'See My Estimate'
          : 'Continue';
      }
    }

    state.currentStep = stepNumber;
    saveState();

    // Update room minimums when entering Step 3
    if (stepNumber === 3) { updateRoomMinimums(); updateRoomCapacityNotes(); }

    // Pre-select recommended food package on first visit to Step 4
    if (stepNumber === 4 && !state._foodDefaultsApplied) {
      applyFoodDefaults();
    }

    // Re-assert the Food Stations guest gate on EVERY entry to Step 4. It is also
    // refreshed by the two guest-count inputs and by state restore; this call makes
    // the guarantee hold by construction rather than by which handler happened to
    // fire. Idempotent. (2026-08-19, with the floor move 40 -> 75.)
    if (stepNumber === 4) refreshStationsGate();

    // Update dollar minimum banner when entering Step 5
    if (stepNumber === 5) updateDollarMinimumBanner();

    // Reset shortfall acknowledgment if user navigates back from Step 6
    if (stepNumber < 6) _minShortfallAcknowledged = false;

    // Same for the capacity override: going back to the rooms clears it, so a
    // user who returns to Step 3 and changes nothing is asked again.
    if (stepNumber <= 3) _capacityAcknowledged = false;

    // Clear stale dollar-minimum status message and update deposit gate on Step 6
    if (stepNumber === 6) {
      var statusEl = getEl('[data-send-proposal-status]');
      if (statusEl) { statusEl.style.display = 'none'; statusEl.innerHTML = ''; }
      updateDepositGate();
      // Gate the estimate behind contact submission. Unlock only for a
      // returning visitor who already completed it (persisted leadCaptured)
      // or who submitted earlier this session.
      if (state.leadCaptured || state._leadSubmitted) {
        unlockEstimate();
      } else {
        lockEstimate();
      }
    }

    // Microsoft Clarity custom tags — filter recordings by funnel stage
    if (typeof window.clarity === 'function') {
      window.clarity('set', 'quote_step', '' + stepNumber);
      if (state.eventType) window.clarity('set', 'event_type', state.eventType);
    }

    // GA4 funnel tracking — enriched params per step
    var stepData = getStepParams(stepNumber);
    trackGA4('quote_step_' + stepNumber, stepData);
    // Clarity custom tag — enables filtering recordings by step reached
    if (window.clarity) { try { clarity('set', 'quote_step', '' + stepNumber); } catch (_) {} }
    if (stepNumber === state.totalSteps) {
      var leadParams = { event_type: state.eventType || '', currency: 'USD', guest_count: (state.adults || 0) + (state.children || 0) };
      try { leadParams.value = calculateEstimate().total; } catch (_) { leadParams.value = 0; }
      trackGA4('generate_lead', leadParams);
    }

    // Subtract sticky nav height so the section header isn't hidden behind the fixed/sticky nav
    const stickyNav = document.querySelector('.header-section');
    const navH = stickyNav ? stickyNav.offsetHeight : 80;
    window.scrollTo({ top: Math.max(0, (getEl('#quote-calculator')?.offsetTop ?? 0) - navH), behavior: 'smooth' });
  }

  /** Check if user is under the dollar minimum for their selected room */
  function getShortfallInfo() {
    if (!state.rooms.length || !state.date || !state.startTime) return null;
    var roomKey = state.rooms[0];
    var min = getRoomMinimum(roomKey);
    if (!min) return null;
    var est = calculateEstimate();
    var currentTotal = est.subtotal;
    var shortfall = min.dollars - currentTotal;
    if (shortfall <= 0) return null;
    var guestCount = (state.adults || 0) + (state.children || 0);
    var perPerson = guestCount > 0 ? Math.ceil(shortfall / guestCount) : shortfall;
    var perPersonFmt = formatCurrency(perPerson);
    var roomName = roomDisplayNames[roomKey] || roomKey;
    return { shortfall: shortfall, perPerson: perPerson, perPersonFmt: perPersonFmt, roomName: roomName };
  }

  function nextStep() {
    if (!validateStep(state.currentStep)) return;

    // Step 4→5: the room MAXIMUM. See ROOM_SEATED_CAPACITY for why this gate
    // exists and why it lives HERE and not on the Step 3 room cards — this is
    // the first transition at which BOTH the room and the meal format are
    // known, so a standing reception cannot be false-positived.
    // ⚠️ ESCAPABLE by design, matching the under-budget modal below: that is
    // the pattern already set for this builder. The override is TRACKED, so
    // §5b's join can tell "they moved room" from "they clicked past it".
    if (state.currentStep === 4) {
      var capInfo = getSeatedCapacityInfo();
      if (capInfo && !_capacityAcknowledged) {
        var capModal = getEl('[data-capacity-reminder-modal]');
        var capText  = getEl('[data-capacity-reminder-text]');
        if (capModal && capText) {
          var fits = roomsThatSeat(capInfo.guests);
          var fitsList = fits.length > 1
            ? fits.slice(0, -1).join(', ') + ' or ' + fits[fits.length - 1]
            : fits[0];
          var wayOut = fits.length
            ? ' <strong>' + fitsList + '</strong> ' + (fits.length > 1 ? 'seat' : 'seats') + ' a party this size.'
            : ' A party this size is larger than any single room seats \u2014 we would look at combining spaces with you.';
          capText.innerHTML = 'You have chosen a seated meal for <strong>' + capInfo.guests +
            ' guests</strong>, and ' + capInfo.roomName + ' seats <strong>' + capInfo.seats +
            '</strong>.' + wayOut +
            ' Room pricing differs by space, so the estimate will only be right once the space matches the party.';
          capModal.style.display = 'flex';
          trackGA4('quote_capacity_modal_shown', {
            room: capInfo.roomName, room_seats: capInfo.seats,
            guest_count: capInfo.guests, over_by: capInfo.over,
            main_course: state.mainCourse || '', event_type: state.eventType || ''
          });
          return; // Don't advance — modal buttons handle it
        }
      }
    }

    // Step 5→6: check if savory station is toggled but no choice selected
    if (state.currentStep === 5) {
      var hasSavory = (state.cocktailAddons || []).indexOf('cocktail_savory_station') !== -1;
      if (hasSavory && (state.savoryStations || []).length === 0) {
        var savoryModal = getEl('[data-savory-reminder-modal]');
        if (savoryModal && !savoryModal._dismissed) {
          savoryModal.style.display = 'flex';
          return;
        }
      }
    }

    // Step 5→6: check for under-budget and show reminder modal
    if (state.currentStep === 5) {
      var info = getShortfallInfo();
      if (info) {
        var modal = getEl('[data-budget-reminder-modal]');
        var textEl = getEl('[data-budget-reminder-text]');
        if (modal && textEl) {
          textEl.innerHTML = 'Your current selections are <strong>' + info.perPersonFmt + ' per person</strong> below the minimum for ' + info.roomName + ' on your selected date and time. You can go back and add enhancements, adjust your guest count, or choose a different time slot.';
          modal.style.display = 'flex';
          trackGA4('quote_budget_modal_shown', { shortfall_per_person: info.perPersonFmt, room: info.roomName, event_type: state.eventType || '' });
          return; // Don't advance — modal buttons handle it
        }
      }
    }

    if (state.currentStep < state.totalSteps) {
      showStep(state.currentStep + 1);
    }
    if (state.currentStep === state.totalSteps) {
      renderEstimate();
    }
  }

  function prevStep() {
    if (state.currentStep > 1) {
      showStep(state.currentStep - 1);
    }
  }

  /* ------------------------------------------------------------------
     GUEST MINIMUM NOTICE (25 adult minimum)
  ------------------------------------------------------------------ */
  function updateGuestMinimumNotice() {
    const notice = getEl('[data-guest-minimum-notice]');
    if (!notice) return;
    const adults = state.adults || 0;

    if (adults > 0 && adults < 25) {
      // Under 25 adults — show notice
      notice.style.display = '';
      notice.style.background = 'rgba(201,168,76,0.1)';
      notice.style.borderColor = '#C9A96E';
      notice.style.color = '#5a4e3e';
    } else {
      // 25+ adults or nothing entered — hide notice
      notice.style.display = 'none';
    }
  }

  /* ------------------------------------------------------------------
     PLATED ENTRÉE — AUTO-INCLUDE COURSES
  ------------------------------------------------------------------ */
  function updatePlatedCourses() {
    const isPlated = state.mainCourse === 'entree_plated';
    const courseCards = getEls('[data-course-card]');
    const includedLabels = getEls('[data-course-included-label]');

    if (isPlated) {
      // Auto-add both courses if not already present
      ['pasta_course', 'salad_course'].forEach(key => {
        if (!state.courses.includes(key)) {
          state.courses.push(key);
        }
        var cc = getEl('[data-course-card="' + key + '"]');
        if (cc) {
          cc.classList.add('is-selected');
          cc.classList.add('is-locked');
          cc.style.pointerEvents = 'none';
          cc.style.opacity = '0.85';
        }
      });
      // Show included labels
      includedLabels.forEach(l => { l.style.display = ''; });
    } else {
      // Unlock course cards
      courseCards.forEach(card => {
        card.classList.remove('is-locked');
        card.style.pointerEvents = '';
        card.style.opacity = '';
      });
      // Hide included labels
      includedLabels.forEach(l => { l.style.display = 'none'; });
      // If switching away from plated, remove auto-added courses
      if (state._platedAutoAdded) {
        ['pasta_course', 'salad_course'].forEach(key => {
          state.courses = state.courses.filter(k => k !== key);
          var cc = getEl('[data-course-card="' + key + '"]');
          if (cc) cc.classList.remove('is-selected');
        });
      }
    }
    state._platedAutoAdded = isPlated;
  }

  /* ------------------------------------------------------------------
     WEEKDAY-ONLY TIME SLOT VISIBILITY
  ------------------------------------------------------------------ */
  function isWeekday(dateStr) {
    if (!dateStr) return false;
    var d = new Date(dateStr + 'T00:00:00');
    var day = d.getDay(); // 0=Sun, 6=Sat
    return day >= 1 && day <= 5;
  }

  function updateWeekdaySlots() {
    var weekdaySlots = getEls('[data-weekday-only]');
    if (!weekdaySlots.length) return;

    var showSlot = false;

    if (state.flexibleDate) {
      // If flexible, show weekday slot if ANY weekday is preferred (or no prefs selected)
      var prefs = state.dayPreferences || [];
      if (prefs.length === 0) {
        showSlot = true; // No prefs = show all options
      } else {
        var weekdayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
        showSlot = prefs.some(function(p) { return weekdayNames.indexOf(p) > -1; });
      }
    } else if (state.date) {
      showSlot = isWeekday(state.date);
    } else {
      showSlot = true; // No date yet, show all options
    }

    weekdaySlots.forEach(function(slot) {
      slot.style.display = showSlot ? '' : 'none';
      // If hiding and it was selected, deselect it
      if (!showSlot && slot.classList.contains('is-selected')) {
        slot.classList.remove('is-selected');
        state.startTime = null;
      }
    });
  }

  /* ------------------------------------------------------------------
     ROOM BOOKING MINIMUMS — headcount gate on Step 3
  ------------------------------------------------------------------ */
  var roomDisplayNames = {
    'atlantic-room':   'The Atlantic Room',
    'crystal-room':    'The Crystal Room',
    'terrace-room':    'The Terrace Room',
    'lantern-room':    'The Lantern Room'
  };

  /** Update room cards: dim those below headcount minimum, show note. */
  function updateRoomMinimums() {
    var totalGuests = (state.adults || 0) + (state.children || 0);
    var day = getDayName(state.date);
    var period = getTimePeriod(state.startTime);
    var hasDateTimeInfo = day && period;

    // Track if any room is gated for the helper note
    var anyGated = false;
    var gatedRooms = [];

    getEls('[data-room-card]').forEach(function(card) {
      var roomKey = card.dataset.roomCard;
      var min = getRoomMinimum(roomKey);
      // Remove any existing minimum note
      var existingNote = card.querySelector('[data-min-note]');
      if (existingNote) existingNote.remove();

      if (!hasDateTimeInfo || !min || roomKey === 'all-venue') {
        // No date/time yet or no minimums for this room — show normally
        card.classList.remove('is-gated');
        return;
      }

      if (totalGuests < min.guests) {
        // Below minimum — dim card and show note
        card.classList.add('is-gated');
        anyGated = true;
        gatedRooms.push({ key: roomKey, min: min.guests });

        var note = document.createElement('div');
        note.setAttribute('data-min-note', '');
        note.className = 'tr-room-min-note';
        var dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
        var periodLabel = period === 'am' ? ' during the day' : ' evenings';
        note.textContent = min.guests + ' guest minimum for ' + dayLabel + periodLabel;
        card.appendChild(note);

        // Auto-deselect if currently selected
        if (state.rooms.includes(roomKey)) {
          state.rooms = state.rooms.filter(function(r) { return r !== roomKey; });
          card.classList.remove('is-selected');
          renderCart();
        }
      } else {
        card.classList.remove('is-gated');
      }
    });

    // Update the helper suggestion note
    var helperNote = getEl('[data-min-helper-note]');
    if (helperNote) {
      if (anyGated && hasDateTimeInfo) {
        // Build a suggestion based on whether a different day/time might lower the minimum
        var suggestion = buildMinimumSuggestion(gatedRooms, day, period, totalGuests);
        helperNote.innerHTML = suggestion;
        helperNote.style.display = '';
      } else {
        helperNote.style.display = 'none';
      }
    }
  }

  /** Step 3 card note: the room's SEATED capacity, from ROOM_SEATED_CAPACITY.
   *  ⚠️ Deliberately LABELLED "for a seated meal". The card's other figure
   *  ("Up to N guests") comes from the theme-editor block setting and is
   *  unlabelled — venue.md records an UNRESOLVED conflict about whether those
   *  higher numbers are standing capacities, and ⛔ it forbids reconciling by
   *  picking one. Labelling the measure states a fact without settling that.
   *  ⛔ Informational only — no gating here: the meal format is not chosen
   *  until Step 4, and a standing reception legitimately exceeds this. */
  function updateRoomCapacityNotes() {
    var guests = (state.adults || 0) + (state.children || 0);
    getEls('[data-room-card]').forEach(function(card) {
      var seats = ROOM_SEATED_CAPACITY[card.dataset.roomCard];
      var existing = card.querySelector('[data-cap-note]');
      if (existing) existing.remove();
      if (!seats) return;
      var note = document.createElement('div');
      note.setAttribute('data-cap-note', '');
      note.className = 'tr-room-cap-note' + (guests > seats ? ' is-over' : '');
      note.textContent = guests > seats
        ? 'Seats up to ' + seats + ' for a seated meal \u2014 your party is ' + guests
        : 'Seats up to ' + seats + ' for a seated meal';
      card.appendChild(note);
    });
  }

  /** Build a helpful suggestion when rooms are gated */
  function buildMinimumSuggestion(gatedRooms, currentDay, currentPeriod, guestCount) {
    var suggestions = [];

    // Check if switching AM/PM would help any gated room
    var altPeriod = currentPeriod === 'pm' ? 'am' : 'pm';
    gatedRooms.forEach(function(gr) {
      var roomMins = BOOKING_MINIMUMS[gr.key];
      if (!roomMins || !roomMins[currentDay]) return;
      var altMin = roomMins[currentDay][altPeriod];
      if (altMin && altMin.guests <= guestCount) {
        var name = roomDisplayNames[gr.key] || gr.key;
        var label = altPeriod === 'am' ? 'a slot during the day' : 'an evening slot';
        suggestions.push('Switching to ' + label + ' would qualify you for ' + name);
      }
    });

    // Check if a nearby day has lower minimums
    if (suggestions.length === 0 && (currentDay === 'saturday' || currentDay === 'sunday')) {
      gatedRooms.forEach(function(gr) {
        var roomMins = BOOKING_MINIMUMS[gr.key];
        if (!roomMins) return;
        // Check Sunday for Saturday guests, and weekdays for Sunday guests
        var altDay = currentDay === 'saturday' ? 'sunday' : 'friday';
        var altDayMins = roomMins[altDay];
        if (altDayMins && altDayMins[currentPeriod] && altDayMins[currentPeriod].guests <= guestCount) {
          var name = roomDisplayNames[gr.key] || gr.key;
          var dayLabel = altDay.charAt(0).toUpperCase() + altDay.slice(1);
          suggestions.push(dayLabel + ' offers a lower minimum for ' + name);
        }
      });
    }

    if (suggestions.length > 0) {
      return '<span class="tr-info-diamond" aria-hidden="true">&#9670;</span> Explore other days and times for reduced minimums.';
    }
    return '<span class="tr-info-diamond" aria-hidden="true">&#9670;</span> Explore other days and times for reduced minimums.';
  }

  /* ------------------------------------------------------------------
     DOLLAR MINIMUM BANNER — Step 5 entry
  ------------------------------------------------------------------ */
  function updateDollarMinimumBanner() {
    var banner = getEl('[data-dollar-min-banner]');
    if (!banner) return;

    // Need a room selected and date/time to check
    if (!state.rooms.length || !state.date || !state.startTime) {
      banner.style.display = 'none';
      return;
    }

    var estimate = calculateEstimate();
    var roomKey = state.rooms[0]; // Primary room
    var min = getRoomMinimum(roomKey);
    if (!min) { banner.style.display = 'none'; return; }

    var currentTotal = estimate.subtotal;
    var shortfall = min.dollars - currentTotal;

    if (shortfall <= 0) {
      banner.style.display = 'none';
      return;
    }

    var guestCount = (state.adults || 0) + (state.children || 0);
    var perPerson = guestCount > 0 ? Math.ceil(shortfall / guestCount) : shortfall;
    var perPersonFmt = formatCurrency(perPerson);

    var day = getDayName(state.date);
    var dayLabel = day ? day.charAt(0).toUpperCase() + day.slice(1) : '';
    var period = getTimePeriod(state.startTime);
    var periodLabel = period === 'am' ? ' during the day' : ' evenings';
    var roomName = roomDisplayNames[roomKey] || roomKey;

    var html = '<div class="tr-dollar-min-banner__inner">';
    html += '<p class="tr-dollar-min-banner__title">Almost There</p>';
    html += '<p>Based on your current selections, you\'re <strong>' + perPersonFmt + ' per person</strong> below the minimum for ' + roomName + ' on ' + dayLabel + periodLabel + '.</p>';
    html += '<p class="tr-dollar-min-banner__suggestions">You can reach the minimum by increasing your guest count, adding enhancements, or selecting a different day or time slot.</p>';
    html += '</div>';

    banner.innerHTML = html;
    banner.style.display = '';
  }

  /* ------------------------------------------------------------------
     DEPOSIT GATE — block deposit when under minimum
  ------------------------------------------------------------------ */
  function updateDepositGate() {
    var depositBtn = getEl('[data-quote-deposit]');
    if (!depositBtn) return;
    var info = getShortfallInfo();
    if (info) {
      depositBtn.disabled = true;
      depositBtn.title = 'Your selections are ' + info.perPersonFmt + ' per person below the minimum for ' + info.roomName;
      depositBtn.style.opacity = '0.5';
      depositBtn.style.cursor = 'not-allowed';
    } else {
      depositBtn.disabled = false;
      depositBtn.title = '';
      depositBtn.style.opacity = '';
      depositBtn.style.cursor = '';
    }
  }

  /* ------------------------------------------------------------------
     VALIDATION
  ------------------------------------------------------------------ */
  function validateStep(step) {
    clearErrors();
    switch (step) {
      case 1:
        if (!state.eventType || !state.eventType.trim()) {
          showError('Please tell us what you\'re celebrating.');
          return false;
        }
        if (!state.adults || state.adults < 1) {
          showError('Please enter the number of adult guests.');
          return false;
        }
        // 25 adult minimum is enforced in billing, not as a blocker
        return true;
      case 2:
        if (!state.date && !state.flexibleDate) {
          showError('Please select a date or mark yourself as flexible.');
          return false;
        }
        if (!state.startTime) {
          showError('Please select a time slot.');
          return false;
        }
        return true;
      case 3:
        // Room/venue selection (moved from old step 5)
        if (state.rooms.length === 0) {
          showError('Please select at least one space.');
          return false;
        }
        return true;
      case 4:
        // Food & beverages (moved from old step 3)
        if (!state.mainCourse) {
          showError('Please select a main course.');
          return false;
        }
        if (!state.dessertBase) {
          showError('Please select a dessert.');
          return false;
        }
        if (!state.afterDinner) {
          showError('Please select an after-dinner service.');
          return false;
        }
        if (!state.barPackage) {
          showError('Please select a bar package.');
          return false;
        }
        return true;
      case 5:
        // All enhancements are optional
        return true;
      default:
        return true;
    }
  }

  function showError(message) {
    const el = getEl('[data-quote-error]');
    if (el) {
      el.textContent = message;
      el.style.display = 'block';
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function clearErrors() {
    const el = getEl('[data-quote-error]');
    if (el) {
      el.textContent = '';
      el.style.display = 'none';
    }
  }

  /* ------------------------------------------------------------------
     RENDER ESTIMATE (Step 7)
  ------------------------------------------------------------------ */
  function formatTime(timeStr) {
    if (!timeStr) return '';
    // Time slots are stored as labels (e.g. "6:00 PM - 10:00 PM")
    if (timeStr.includes('AM') || timeStr.includes('PM')) return timeStr;
    // Legacy HH:MM format fallback.
    // ⛔ AND PASS ANYTHING ELSE THROUGH UNCHANGED. This used to split on ':'
    // unconditionally, so a non-time string became "12:undefined AM" — a
    // garbled time on a contract is worse than an unformatted one, because it
    // still LOOKS authoritative. Not reachable today (state.startTime is only
    // ever null or a slot label) but computeSchedule() degrades through here.
    const legacy = String(timeStr).match(/^(\d{1,2}):(\d{2})$/);
    if (!legacy) return timeStr;
    const h = Number(legacy[1]), m = Number(legacy[2]);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour   = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  }

  /* ------------------------------------------------------------------
     THE EVENT WINDOW — ⛔ ONE COMPUTATION, and it must stay that way.

     Two things move the window and they move opposite ends:
       ceremony_setup (cocktailAddon) -> START 30 min EARLIER
       extend_hour    (eventAddon)    -> END   60 min LATER

     🔴 WHY THIS FUNCTION EXISTS. The ceremony shift used to be written out
     inline at FOUR separate call sites in this file (cart summary, Step-6
     review lines, proposalText, and the Worker payload) and NOWHERE in
     assets/proposal.js. That is exactly why "Extend Your Event One Hour"
     shipped 2026-08-31 without ever touching the end time: there was no single
     place to add it, and adding it in four would have been four chances to
     disagree. Owner-reported 2026-09-09 on a real proposal — 5-9 PM with a
     ceremony AND an extension printed "4:30 PM - 9:00 PM" instead of
     "4:30 PM - 10:00 PM".
     ⛔ DO NOT re-inline any of this. New call site -> call this.

     🔑 barHours is NOT the event duration and the difference is load-bearing.
     The extension's price is "room component + HALF the selected bar package's
     per-person rate, per person, per hour" and the extended hour carries NO
     FOOD (see EVENT ENHANCEMENTS above) -- so an extension buys an hour of bar
     and the contract's "N Hours unlimited <bar> included" sentence must say so.
     ⛔ A CEREMONY DOES NOT ADD A BAR HOUR: it is a flat per-person cocktailAddon
     with no bar component in its price, and the bar is not open during it.
     ✅ 7:00-11:00 PM extended lands at 12:00 AM, and that is FINE -- owner
     asked and answered 2026-09-09: "going 7-12 is not a problem at all, leave
     as is." ⛔ DO NOT "fix" a midnight end time, and ⛔ do not drop the 7-11
     slot from the extension's eligible slots to avoid it. It is also not a new
     sale: the extension has been purchasable on that slot since 2026-08-31 and
     the contract simply never printed the hour the client had bought.
  ------------------------------------------------------------------ */
  var BASE_BAR_HOURS = 4;

  /** "4:30 PM" -> 990. Returns null on anything this does not understand, so
   *  every caller degrades to the raw slot string rather than to a wrong time. */
  function parseClockMins(str) {
    var m = String(str || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    var h = parseInt(m[1], 10) % 12;
    if (m[3].toUpperCase() === 'PM') h += 12;
    return h * 60 + parseInt(m[2], 10);
  }

  /** 990 -> "4:30 PM". Wraps, so 1440 is "12:00 AM", not "24:00". */
  function formatClockMins(mins) {
    mins = ((mins % 1440) + 1440) % 1440;
    var h = Math.floor(mins / 60), m = mins % 60;
    return (h % 12 || 12) + ':' + String(m).padStart(2, '0') + ' ' + (h >= 12 ? 'PM' : 'AM');
  }

  /**
   * { display, barHours, totalHours } for the current state.
   * display  — "4:30 PM - 10:00 PM", the string every surface prints
   * barHours — what the contract may promise as unlimited bar
   * totalHours — ceremony + base + extension, the modal's Duration field
   */
  function computeSchedule(st) {
    st = st || state;
    var slot = st.startTime || '';
    var hasCeremony  = (st.cocktailAddons || []).indexOf('ceremony_setup') !== -1;
    var hasExtension = (st.eventAddons    || []).indexOf('extend_hour')    !== -1;
    var barHours = BASE_BAR_HOURS + (hasExtension ? 1 : 0);
    var out = { display: formatTime(slot), barHours: barHours, totalHours: barHours };

    if (!slot || slot.indexOf(' - ') === -1) return out;
    var parts = slot.split(' - ');
    var startMins = parseClockMins(parts[0]);
    var endMins   = parseClockMins(parts[1]);
    if (startMins === null || endMins === null) return out;

    if (endMins <= startMins) endMins += 1440;   // a slot that already crosses midnight
    if (hasCeremony)  startMins -= 30;
    if (hasExtension) endMins   += 60;

    out.display    = formatClockMins(startMins) + ' - ' + formatClockMins(endMins);
    out.totalHours = (endMins - startMins) / 60;
    return out;
  }

  var _renderingEstimate = false;
  function renderEstimate() {
    if (_renderingEstimate) return;
    _renderingEstimate = true;

    // Calculate once, share across all render functions
    var est = calculateEstimate();

    renderProposalHeader();
    renderValueBreakdown();
    renderItemizedList(est);
    renderCart();
    updateDepositGate(est);
    _renderingEstimate = false;

    // Dispatch proposal data for external consumers (proposal.js)
    document.dispatchEvent(new CustomEvent('tr:estimate-ready', {
      detail: {
        state: JSON.parse(JSON.stringify(state)),
        // billedAdults/billedChildren must be forwarded: proposal.js prints the
        // counts the SUBTOTAL was computed from, not the counts as entered.
        schedule: computeSchedule(state),
        estimate: { lineItems: est.lineItems, adultRate: est.adultRate, childRate: est.childRate, childRateElevated: est.childRateElevated, subtotal: est.subtotal, adminFee: est.adminFee, adminFeePct: est.adminFeePct, total: est.total, guestCount: est.guestCount, billedAdults: est.billedAdults, billedChildren: est.billedChildren },
        labels: {
          horsOeuvres:    horsOeuvresLabels,
          courses:        courseLabels,
          mainCourse:     mainCourseLabels,
          dessertBase:    dessertBaseLabels,
          cocktailAddons: Object.assign({}, cocktailAddonLabels, (state.savoryStations || []).length ? { cocktail_savory_station: getSavoryLabel() } : {}),
          entreeAddons:   entreeAddonLabels,
          eventAddons:    eventAddonLabels,
          dessertAddons:  dessertAddonLabels,
          afterDinner:    afterDinnerLabels,
          bar:            barLabels,
          barEnhancements: barEnhancementLabels
        }
      }
    }));
  }

  function renderItemizedList(precomputed) {
    const estimate  = precomputed || calculateEstimate();
    const container = getEl('[data-itemized-list]');
    if (!container) return;

    const rows = [];

    // ── Per-person line items — show rate/person, math note below ──
    estimate.lineItems.forEach(item => {
      const adults   = state.adults   || 0;
      const children = state.children || 0;
      // Who pays this line:
      //   bar / adult-only (after-dinner) → adults only
      //   child-only (kids' menu, kids-only mocktail) → children only
      //   everyone (mocktail all) / shared food → all guests
      let payerCount, payerLabel;
      if (item.isBar || item.adultOnly) {
        payerCount = adults;
        payerLabel = adults === 1 ? 'adult' : 'adults';
      } else if (item.childOnly) {
        payerCount = children;
        payerLabel = children === 1 ? 'child' : 'children';
      } else {
        payerCount = adults + children;
        payerLabel = payerCount === 1 ? 'guest' : 'guests';
      }
      const note = payerCount > 0 ? `${payerCount} ${payerLabel} × ${formatCurrency(item.rate)}` : '';

      rows.push({
        label:      item.label,
        note,
        primary:    formatCurrency(item.rate),
        subLabel:   'per person',
        isPerPerson: true,
      });
    });

    // ── Room fee(s) — flat amount ──
    const roomRates  = (window.QuoteRates || {}).rooms || {};
    const roomLookup = {
      'atlantic-room':   'The Atlantic Room',
      'crystal-room':    'The Crystal Room',
      'terrace-room':    'The Terrace Room',
      'lantern-room':    'The Lantern Room',
      'lobby-bar':       'The Lobby Bar',
      'outdoor-gardens': 'Outdoor Gardens',
    };
    state.rooms.forEach(r => {
      const fee = roomRates[r] ?? 0;
      if (fee > 0) {
        rows.push({
          label:   roomLookup[r] || r.replace(/-/g, ' '),
          note:    'venue fee',
          primary: formatCurrency(fee),
        });
      }
    });

    if (rows.length === 0) {
      container.innerHTML = '';
      return;
    }

    // ── Render ──
    // Per-person rates: adultRate = food + bar, childRate = food only (no admin fee, no room fees)
    const adultPP = estimate.adultRate || 0;
    const childPP = estimate.childRate || 0;
    const hasChildren = (state.children || 0) > 0;

    container.innerHTML = `
      <div style="border-top:1px solid rgba(255,255,255,0.15); padding-top:1.25rem;">
        <p style="color:var(--color-brand-gold-light); font-family:var(--font-body); font-size:0.7rem;
                  letter-spacing:0.12em; text-transform:uppercase; margin-bottom:1rem;">
          Your Event Estimate
        </p>
        <ul style="list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:0;">
          ${rows.map(row => `
            <li style="display:grid; grid-template-columns:1fr auto; gap:0.75rem;
                       padding:0.65rem 0; border-bottom:1px solid rgba(255,255,255,0.07);
                       align-items:start; ${row.isAdmin ? 'opacity:0.7;' : ''}">
              <span>
                <span style="display:block; color:rgba(250,248,244,0.9); font-size:0.875rem; line-height:1.3;">
                  ${row.label}
                </span>
                ${row.note ? `<span style="display:block; color:rgba(250,248,244,0.4); font-size:0.75rem; margin-top:0.15rem;">${row.note}</span>` : ''}
              </span>
              <span style="text-align:right;">
                <span style="display:block; font-size:0.9rem; font-weight:600;
                             color:var(--color-brand-gold-light); white-space:nowrap;">
                  ${row.primary}
                </span>
                ${row.subLabel ? `<span style="display:block; font-size:0.68rem; color:rgba(250,248,244,0.35); white-space:nowrap; margin-top:0.1rem;">${row.subLabel}</span>` : ''}
              </span>
            </li>
          `).join('')}
        </ul>
        ${adultPP > 0 ? `
        <div style="margin-top:1rem; padding-top:0.75rem; border-top:1px solid rgba(255,255,255,0.1);
                    font-family:var(--font-body); font-size:0.875rem; color:rgba(250,248,244,0.55);
                    display:flex; flex-direction:column; gap:0.3rem; text-align:right;">
          <p style="margin:0;"><span style="color:var(--color-brand-gold-light); font-weight:500;">${formatCurrency(adultPP)}</span> per adult</p>
          ${hasChildren ? `<p style="margin:0;"><span style="color:var(--color-brand-gold-light); font-weight:500;">${formatCurrency(childPP)}</span> per child</p>` : ''}
        </div>` : ''}

      </div>
    `;
  }

  /* ------------------------------------------------------------------
     DEPOSIT BREAKDOWN — shows deposit + CC fee before the CTA button
  ------------------------------------------------------------------ */
  var CC_FEE_PCT = 0.03; // 3% credit card processing fee


  /* ------------------------------------------------------------------
     PROPOSAL HEADER — "The Smith Wedding · June 14, 2026"
  ------------------------------------------------------------------ */
  // Capitalize the first letter of each word, preserving any intentional
  // caps the customer typed (smith→Smith, o'brien→O'Brien, while McDonald
  // and DeVito stay intact). Mirrored in proposal.js + worker pdf-templates.js.
  function titleCaseName(str) {
    return (str || '').trim().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  /* ── normalizePersonName — §A of specs/lead-capture-data-quality.md ──
     ⛔ NOT titleCaseName, and ⛔ do not merge the two. titleCaseName PRESERVES interior
     caps (it only upper-cases the first letter of each word), which is right for an
     event-type label but wrong here: it turns `pPAUL` into `PPAUL` and leaves `STRAFACE`
     shouting. The owner's rule for names is "first letter up, everything else down, so
     everything comes through standardized" — ⚠️ he accepted that this yields `Mcdonald`
     and `Oconnor`. STANDARDIZED BEATS CORRECT HERE, DELIBERATELY.

     Word separators are whitespace and `-` (two-token names: smith-jones → Smith-Jones).
     An apostrophe capitalizes its tail ONLY when that tail is >= 2 chars, which gives
     O'Connor and D'Angelo while leaving a trailing `'` alone. Same rule as the occasion
     ruleset's §2, so the two stay consistent.

     🔑 IDEMPOTENT BY CONSTRUCTION — it lower-cases before it capitalizes, so
     f(f(x)) === f(x) for every input. That is a REQUIRED property, not a nicety:
     §C ruled out a double-firing transform as the cause of the PPaulPPaul defect, but a
     blur handler that re-applies to an already-transformed value must still be safe.
     Pinned by the self-test below.

     🔴 THE SPEC'S OWN EXAMPLE `pPAUL` -> `Paul` IS NOT IMPLEMENTED, DELIBERATELY.
     It is 5 characters in and 4 out, so it is a DEDUPE, not a casing rule — and §C
     forbids exactly that ("a defensive dedupe would hide a bug"). Worse, stripping a
     repeated leading letter destroys real names: Aaron -> Aron, Lloyd -> Loyd,
     Aaliyah, Eero. The owner's stated rule ("first letter up, everything else down")
     yields `Ppaul`, and that is what ships. ⚠️ The example conflated §C's doubling
     defect with §A's casing rule; §C is now fixed at source (the 31px box), so the
     input that produced it no longer occurs. ⛔ Do not "fix" this by adding a dedupe. */
  function normalizePersonName(str) {
    var out = (str == null ? '' : String(str)).replace(/\s+/g, ' ').trim().toLowerCase();
    // Capitalize the first letter of each whitespace- or hyphen-delimited token.
    out = out.replace(/(^|[\s-])([a-z\u00e0-\u024f])/g, function (_m, sep, ch) {
      return sep + ch.toUpperCase();
    });
    // Capitalize after an apostrophe only when the tail is >= 2 chars: O'Connor, not O'C.
    out = out.replace(/(['\u2019])([a-z\u00e0-\u024f])(?=[a-z\u00e0-\u024f])/g, function (_m, ap, ch) {
      return ap + ch.toUpperCase();
    });
    return out;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     §B — OCCASION NORMALIZATION. Implements the growth front's ruleset:
     ~/TR/projects/growth/specs/occasion-normalization.md (⛔ the rules are THEIRS —
     do not change behaviour here; change it there and re-port). The executable
     reference is that project's occasion-normalization-reference.py, and
     scripts/check-occasion-normalizer.mjs proves this port reproduces it over every
     occasion string the business has ever received.

     Pipeline, and ⛔ THE ORDER IS LOAD-BEARING:
       S1 trim + collapse whitespace
       S2 synonym/typo map   (keyed on the LOWER-CASED string)
       S3 title case
       S4 append "Celebration" — conditionally, deny-first
     S2 must precede S3 (the map keys are lowercase) and S4 must run last (appending
     first breaks every map key).

     🔑 IDEMPOTENT: rule D2 ("already contains the stem 'celebrat'") is what makes
     f(f(x)) === f(x). Pinned by the checker. ⚠️ Required by §C.
     ══════════════════════════════════════════════════════════════════════════ */

  /* 🔴 GENERATED FROM THE CRM — ⛔ DO NOT HAND-EDIT THESE TWO PATTERNS.
     Source: ~/TR/projects/crm/pipeline/draft_copy.py -> _SOLEMN / _CORPORATE.
     The growth spec's rule is "fetch, never copy": a bereavement net that drifts is
     the highest-consequence bug on this path — an unlisted solemn word means a
     grieving family's proposal reads "<something> Celebration". A browser cannot
     import Python, so instead these are DERIVED and FINGERPRINTED:
     scripts/check-occasion-normalizer.mjs re-reads draft_copy.py and FAILS if this
     fingerprint no longer matches. ▶ When it fails, re-run the generator, do not
     edit by hand. Fingerprint: a499e019a2dfdeb3 */
  var OCCASION_SOLEMN    = /memorial|funeral|repast|in memory|loved one|celebrat\w*\s+(?:of\s+)?(?:the\s+|his\s+|her\s+|their\s+|a\s+)*life|life celebration|\bpass(?:ed|ing)\b|\bshiva\b|\bunveiling\b|\bwake\b|\binterment\b|\binternment\b/i;
  var OCCASION_CORPORATE = /corporate|\borg\b|company|team building|business|conference|meeting|network|off[\s-]?site|training|retreat|summit|\bagm\b/i;

  var OCCASION_SYNONYMS = {
    'weeding': 'Wedding', 'wedding celebration': 'Wedding', 'marriage': 'Wedding',
    'weddin he reception': 'Wedding Reception',
    'private dinne': 'Private Dinner',
    'enagement': 'Engagement', 'elopement celebratio': 'Elopement Celebration',
    'qincenera': 'Quincea\u00f1era', 'sweet sixteen': 'Sweet 16',
    'christening': 'Baptism', 'christening/baptism': 'Baptism',
    'babyshower': 'Baby Shower',
    'bar mitzvah': 'Bar Mitzvah', 'bat mitzvah': 'Bat Mitzvah',
    '90th birthay': '90th Birthday', 'first birthdya': 'First Birthday',
    'milestone birthda': 'Milestone Birthday',
    'moms 80th bday': "Mom's 80th Birthday",
    '80 th bay party': '80th Birthday Party',
    'org celevrstuon': 'Organization Celebration',
    '50th wedding anniversay': '50th Wedding Anniversary',
    'org': 'Organization', 'team': 'Team'
  };

  var OCCASION_MINOR = ['a','an','and','as','at','but','by','for','from','in','nor',
                        'of','on','or','the','to','vs','with'];
  var OCCASION_SEP_SPLIT = /([\s\/\-\u2013\u2014]+)/;
  var OCCASION_SEP_ONLY  = /^[\s\/\-\u2013\u2014]+$/;
  var OCCASION_ALPHA     = /[A-Za-z\u00c0-\u024f]/;

  function occCap(tok) {
    // A token STARTING with a digit is an ordinal/number: 40th, 1st, 80s. Its alpha
    // tail stays lower — naive title case emits "40Th", the most common defect on
    // this dataset.
    var core = tok.replace(/^[("\u2018\u201c\[]+/, '');
    if (core && /^[0-9]/.test(core)) return tok.toLowerCase();
    for (var i = 0; i < tok.length; i++) {
      if (OCCASION_ALPHA.test(tok[i])) return tok.slice(0, i) + tok[i].toUpperCase() + tok.slice(i + 1);
    }
    return tok;
  }
  function occApostrophe(tok) {
    // Capitalize after ' or ’ only when the tail is >= 2 chars: O'Connor yes,
    // Daughter's no. Same rule as normalizePersonName above.
    return tok.replace(/(['\u2019])([A-Za-z0-9_\u00c0-\u024f]{2,})/g, function (_m, ap, tail) {
      return ap + occCap(tail);
    });
  }
  function occTitleCase(s) {
    if (!s) return s;
    var allCaps = s.toUpperCase() === s && OCCASION_ALPHA.test(s);
    var parts = s.split(OCCASION_SEP_SPLIT);
    var words = [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i] && !OCCASION_SEP_ONLY.test(parts[i])) words.push(i);
    }
    var out = parts.slice();
    for (var n = 0; n < words.length; n++) {
      var idx = words[n], w = parts[idx], low = w.toLowerCase();
      // In MIXED-case input, an all-caps token of >=2 chars is an acronym and survives.
      var keepAcronym = !allCaps && w.length >= 2 && w === w.toUpperCase() && w !== w.toLowerCase();
      if (keepAcronym) out[idx] = w;
      else if (OCCASION_MINOR.indexOf(low) !== -1 && n !== 0 && n !== words.length - 1) out[idx] = low;
      else out[idx] = occApostrophe(occCap(low));
    }
    return out.join('');
  }

  var OCC_GATHERING = /\b(party|dinner|reception|banquet|shower|gala|dance|luncheon|lunch|brunch|ceremony|service|prom|reunion|meeting|conference|dinne)\b/i;
  var OCC_CELEBRATORY = /\b(wedding|marriage|elopement|renewal|anniversar\w*|engagement|birthday|bday|sweet\s*16|sweet\s*sixteen|quince\w*|mitzvah|baptism|christening|communion|confirmation|graduation|retirement|holiday|milestone|bridal)\b/i;
  var OCC_ORDINAL = /\b\d{1,3}(st|nd|rd|th)?\b/i;
  var OCC_JUNK = /^(test|zz\b.*|bu|production|other|n\/?a|\W*|\d{1,2}[\/\-\s].*\d{4}|[a-z]{1,2})$/i;
  var OCC_DATEISH = /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b.*\b\d{4}\b/i;

  /* Deny-first cascade, then an allow gate. First rule that fires wins.
     ⛔ NEVER invert this to "append unless denied". The two errors are not
     symmetric: a missed append costs nothing (add a word to A1); a wrong append
     puts "Celebration" on a bereavement and that cannot be taken back. `shiva` is
     the live example — it is NOT in the CRM's solemn net, so D7 is what saves it. */
  function occShouldAppend(value) {
    var v = value || '', low = v.toLowerCase();
    if (!v.trim()) return false;                       // D1 empty
    if (low.indexOf('celebrat') !== -1) return false;  // D2 already says it (also = idempotence)
    if (OCCASION_SOLEMN.test(low)) return false;       // D3 bereavement
    if (OCCASION_CORPORATE.test(low)) return false;    // D4 business/meeting
    if (OCC_JUNK.test(v.trim()) || OCC_DATEISH.test(v)) return false; // D5 not an occasion
    if (OCC_GATHERING.test(low)) return false;         // D6 already names the gathering
    if (OCC_CELEBRATORY.test(low)) return true;        // A1
    if (OCC_ORDINAL.test(low)) return true;            // A2
    return false;                                      // D7 safe default
  }

  function normalizeOccasion(raw) {
    var s = (raw == null ? '' : String(raw)).replace(/\s+/g, ' ').trim();
    var mapped = OCCASION_SYNONYMS[s.toLowerCase()];
    if (mapped !== undefined) s = mapped;
    var disp = occTitleCase(s);
    return disp + (occShouldAppend(disp) ? ' Celebration' : '');
  }

  /* Self-test: runs once at parse time, costs microseconds, and fails loudly in the
     console rather than silently shipping a broken normalizer. Cases are the ones named
     in the spec plus the idempotence requirement. */
  (function selfTestNormalizePersonName() {
    var cases = [
      ['pPAUL', 'Ppaul'], ['STRAFACE', 'Straface'], ['mcdonald', 'Mcdonald'],
      ['smith-jones', 'Smith-Jones'], ["o'connor", "O'Connor"], ['  ann   marie  ', 'Ann Marie'],
      ['\u2019', '\u2019'], ['', ''], ['JEAN-LUC', 'Jean-Luc'], ['d\u2019angelo', 'D\u2019Angelo']
    ];
    for (var i = 0; i < cases.length; i++) {
      var got = normalizePersonName(cases[i][0]);
      if (got !== cases[i][1]) {
        console.error('[quote] normalizePersonName FAIL', JSON.stringify(cases[i][0]), '->', JSON.stringify(got), 'expected', JSON.stringify(cases[i][1]));
      }
      if (normalizePersonName(got) !== got) {
        console.error('[quote] normalizePersonName NOT IDEMPOTENT for', JSON.stringify(cases[i][0]));
      }
    }
  })();

  function renderProposalHeader() {
    const titleEl = getEl('[data-proposal-title]');
    const dateEl  = getEl('[data-proposal-date]');
    if (!titleEl) return;

    const lastName  = titleCaseName(state.lastName);
    const eventName = (state.eventType || 'Event').trim();

    if (lastName) {
      titleEl.textContent = `The ${lastName} ${eventName}`;
    } else {
      titleEl.textContent = `Your ${eventName} Proposal`;
    }

    if (dateEl && state.date) {
      const d = new Date(state.date + 'T00:00:00');
      dateEl.textContent = d.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } else if (dateEl) {
      dateEl.textContent = '';
    }
  }

  /* ------------------------------------------------------------------
     VALUE BREAKDOWN — show what's included before the price
  ------------------------------------------------------------------ */
  function renderValueBreakdown() {
    const listEl = getEl('[data-value-breakdown-list]');
    if (!listEl) return;

    const inclusions = [];

    // Food package inclusions (dynamic based on main course selection)
    const foodInclusions = {
      entree_plated:       { icon: '&#9670;', label: 'Plated Dinner Service', desc: 'Full pasta course, salad course, and 4 choices of plated entrees' },
      lunch_dinner_buffet: { icon: '&#9670;', label: 'Buffet', desc: 'Seasonal dishes with generous portions for all guests' },
      food_stations:       { icon: '&#9670;', label: 'Interactive Food Stations', desc: 'Multiple stations of different cuisines — guests roam, served by our staff' },
      hors_doeuvres_heavy: { icon: '&#9670;', label: 'Heavily Passed Hors d\'oeuvres', desc: 'Heartier selections passed continuously — serves as your main course' },
      brunch_buffet:       { icon: '&#9670;', label: 'Brunch Buffet', desc: 'A sunlit spread of morning favorites for your celebration' },
    };

    if (state.mainCourse && foodInclusions[state.mainCourse]) {
      inclusions.push(foodInclusions[state.mainCourse]);
    }

    // Brunch buffet — mimosas & bellinis are only complimentary when the
    // bar package includes wine (Wine & Beer or higher). Soft Drinks and
    // Cash Bar do NOT include them. Showing them otherwise has caused us
    // to comp the upgrade at past events.
    var BAR_INCLUDES_BUBBLES = ['wine_beer', 'bar_cocktails', 'bar_premium'];
    if (state.mainCourse === 'brunch_buffet' && BAR_INCLUDES_BUBBLES.indexOf(state.barPackage) !== -1) {
      inclusions.push({
        icon: '&#9670;',
        label: 'Mimosas & Bellinis',
        desc: 'Complimentary mimosas and bellinis served throughout your brunch celebration',
      });
    }

    // Cocktail hour — only show if hors d'oeuvres are actually selected
    if (state.horsOeuvres) {
      inclusions.push({
        icon: '&#9670;',
        label: 'Cocktail Hour',
        desc: horsOeuvresLabels[state.horsOeuvres] || 'Passed hors d\'oeuvres',
      });
    }

    // Bar package
    const barInclusions = {
      bar_premium:     { icon: '&#9670;', label: 'Premium Open Bar', desc: 'Your own private bar stocked with top-shelf spirits, wines, and craft cocktails — including espresso martinis — for the full duration' },
      bar_cocktails:   { icon: '&#9670;', label: 'Full Open Bar', desc: 'Your own private bar with cocktails, spirits, wine, and beer for the full duration' },
      wine_beer:       { icon: '&#9670;', label: state.cocktailTab ? 'Wine & Beer + Cocktail Tab' : 'Wine & Beer Service', desc: state.cocktailTab ? 'Your own private bar with wines, beers, and a cocktail tab for the full duration' : 'Your own private bar with a curated selection of wines and beers for the full duration' },
      bar_soft_drinks: { icon: '&#9670;', label: 'Non-Alcoholic Beverages', desc: 'Soft drinks, juices, and mixers available at your private bar' },
      bar_cash:        { icon: '&#9670;', label: 'Cash Bar', desc: 'Guests purchase their own drinks — soft drinks and coffee included for all guests' },
    };

    if (state.barPackage && barInclusions[state.barPackage]) {
      inclusions.push(barInclusions[state.barPackage]);
    }

    // Children's package
    if (state.children > 0) {
      inclusions.push({
        icon: '&#9670;',
        label: 'Child Package',
        desc: 'Full food package (non-alcoholic) at a reduced rate',
      });
    }

    // Dessert
    if (state.dessertBase && dessertBaseLabels[state.dessertBase]) {
      var dessertDesc = dessertBaseLabels[state.dessertBase];
      if (state.dessertAddons.length) {
        var addonNames = getDessertAddonDisplayList();
        dessertDesc += ' + ' + addonNames.join(', ');
      }
      inclusions.push({
        icon: '&#9670;',
        label: 'Dessert',
        desc: dessertDesc,
      });
    }

    // Finale — always included
    inclusions.push({
      icon: '&#9670;',
      label: 'Finale Service',
      desc: 'Coffee, tea, and after-dinner service',
    });

    // Venue inclusions — always included
    inclusions.push({
      icon: '✦',
      label: 'Venue & Staff',
      desc: 'Tables, chairs, linens, and dedicated event staff — all included',
    });

    listEl.innerHTML = inclusions.map(item =>
      `<li class="tr-value-breakdown__item">
        <span class="tr-value-breakdown__icon">${item.icon}</span>
        <span>
          <span class="tr-value-breakdown__label">${item.label}</span>
          <span class="tr-value-breakdown__desc"> — ${item.desc}</span>
        </span>
      </li>`
    ).join('');
  }

  /* ------------------------------------------------------------------
     RUNNING CART SIDEBAR
  ------------------------------------------------------------------ */
  var _cartTimer = null;
  function renderCartDebounced() {
    if (_cartTimer) clearTimeout(_cartTimer);
    _cartTimer = setTimeout(function() { _cartTimer = null; renderCart(); }, 60);
  }

  function renderCart() {
    if (_cartTimer) { clearTimeout(_cartTimer); _cartTimer = null; }
    // Re-evaluate the evening-slot gate BEFORE the estimate is built: a guest
    // who picks the extension and then moves to an afternoon slot must lose it
    // from the price, not merely from the card.
    updateEventAddonAvailability();
    invalidateEstimateCache();
    saveState();
    const cart = getEl('[data-quote-cart]');
    if (!cart) return;

    const RATES    = window.QuoteRates || {};
    const roomRates = RATES.rooms || {};
    const sections = [];
    // ⛔ USED BY THE CEREMONY SECTION BELOW. This declaration was deleted on
    // 2026-09-09 as a "dead local" when the four inline ceremony copies were
    // collapsed into computeSchedule() -- the grep that found the copies
    // searched for `ceremony_setup` and `adjustTimeForCeremony`, and this line
    // uses NEITHER. renderCart() then threw ReferenceError on every state
    // change. ⛔ `node --check` cannot see this; only running it can.
    const hasCeremony = (state.cocktailAddons || []).includes('ceremony_setup');

    // ── Event Details (non-removable) ──
    const evtDetailItems = [];
    if (state.eventType) evtDetailItems.push({ text: state.eventType });
    const gP = [];
    if (state.adults)   gP.push(state.adults   + ' adult'    + (state.adults   !== 1 ? 's' : ''));
    if (state.children) gP.push(state.children + ' child'    + (state.children !== 1 ? 'ren' : ''));
    if (gP.length) evtDetailItems.push({ text: gP.join(' + ') });
    if (state.date) {
      const d = new Date(state.date + 'T00:00:00');
      evtDetailItems.push({ text: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) });
    } else if (state.flexibleDate) {
      evtDetailItems.push({ text: 'Flexible date' });
    }
    if (state.startTime) {
      evtDetailItems.push({ text: computeSchedule(state).display });
    }
    if (evtDetailItems.length) sections.push({ label: 'Event Details', items: evtDetailItems, removable: false });

    // ── Ceremony (between Event Details and Food) ──
    if (hasCeremony) {
      const r = RATES.cocktailAddons?.['ceremony_setup'] || 0;
      sections.push({
        label: 'Ceremony',
        items: [{ text: cocktailAddonLabels['ceremony_setup'], rate: r, action: 'cocktailAddon', key: 'ceremony_setup' }],
        removable: true
      });
    }

    // ── Food & Catering (removable) ──
    const foodItems = [];
    if (state.horsOeuvres) {
      const r = RATES.horsOeuvres?.[state.horsOeuvres] || 0;
      foodItems.push({ text: horsOeuvresLabels[state.horsOeuvres] || state.horsOeuvres, rate: r, action: 'hors', key: state.horsOeuvres });
    }
    (state.cocktailAddons || []).forEach(function(key) {
      if (key === 'ceremony_setup') return; // shown in its own Ceremony section
      if (key === 'cocktail_savory_station') {
        var sr = getSavoryRate();
        foodItems.push({ text: getSavoryLabel(), rate: sr, action: 'cocktailAddon', key: key });
        return;
      }
      const r = RATES.cocktailAddons?.[key] || 0;
      foodItems.push({ text: cocktailAddonLabels[key] || key, rate: r, action: 'cocktailAddon', key: key });
    });
    state.courses.forEach(function(key) {
      const r = RATES.courses?.[key] || 0;
      const isAutoIncluded = state.mainCourse === 'entree_plated';
      foodItems.push({
        text: (courseLabels[key] || key) + (isAutoIncluded ? ' (included)' : ''),
        rate: r,
        action: isAutoIncluded ? null : 'course',
        key: key
      });
    });
    if (state.mainCourse) {
      const r = RATES.mainCourse?.[state.mainCourse] || 0;
      foodItems.push({ text: mainCourseLabels[state.mainCourse] || state.mainCourse, rate: r, action: 'mainCourse', key: state.mainCourse });
    }
    (state.entreeAddons || []).forEach(function(key) {
      const r = RATES.entreeAddons?.[key] || 0;
      foodItems.push({ text: entreeAddonLabels[key] || key, rate: r, action: 'entreeAddon', key: key });
    });

    if (state.dessertBase) {
      const r = RATES.dessertBase?.[state.dessertBase] || 0;
      foodItems.push({ text: dessertBaseLabels[state.dessertBase] || state.dessertBase, rate: r, action: 'dessert', key: state.dessertBase });
    }
    state.dessertAddons.forEach(function(key) {
      if (key === 'dessert_addon_sweet_station') {
        var q = Math.max(1, state.sweetStationQty || 1);
        foodItems.push({ text: q > 1 ? 'Sweet Station (×' + q + ')' : 'Sweet Station', rate: SWEET_STATION_RATE * q, action: 'dessertAddon', key: key });
        return;
      }
      if (DESSERT_TRIO_KEYS.indexOf(key) !== -1) return; // handled once, combined, below
      const r = RATES.dessertAddons?.[key] || 0;
      foodItems.push({ text: dessertAddonLabels[key] || key, rate: r, action: 'dessertAddon', key: key });
    });
    var trioCountForCart = getDessertTrioCount();
    if (trioCountForCart > 0) {
      foodItems.push({
        text: getDessertTrioLabel(),
        rate: DESSERT_TRIO_TIER_RATES[trioCountForCart],
        action: 'dessertAddonGroup',
        key: 'dessert_addon_trio',
      });
    }
    if (state.afterDinner) {
      const r = RATES.afterDinner?.[state.afterDinner] || 0;
      foodItems.push({ text: afterDinnerLabels[state.afterDinner] || state.afterDinner, rate: r, action: 'afterDinner', key: state.afterDinner });
    }
    if (foodItems.length) sections.push({ label: 'Food & Catering', items: foodItems, removable: true });

    // ── Event Enhancements (removable) ──
    // Its own group, not folded into Food & Catering: an hour extension is
    // neither food nor bar, and the cart must say the same thing Exhibit E does.
    // One composed rate — ⛔ never the room/bar split.
    const eventItems = [];
    (state.eventAddons || []).forEach(function(key) {
      const r = key === 'extend_hour' ? getExtendHourRate() : 0;
      eventItems.push({ text: eventAddonLabels[key] || key, rate: r, action: 'eventAddon', key: key });
    });
    if (eventItems.length) sections.push({ label: 'Event Enhancements', items: eventItems, removable: true });

    // ── Bar (removable) ──
    const barItems = [];
    if (state.barPackage) {
      const r = state.barPackage === 'bar_cash' ? (RATES.bar?.['bar_soft_drinks'] || 0) : (RATES.bar?.[state.barPackage] || 0);
      barItems.push({ text: barLabels[state.barPackage] || state.barPackage, rate: r, action: 'bar', key: state.barPackage });
    }
    state.barEnhancements.forEach(function(key) {
      if (key === 'bar_add_mocktail') {
        var mr = RATES.barEnhancements?.['bar_add_mocktail'] || 0;
        var mockLabel = state.mocktailScope === 'kids' ? 'Mocktail Bar (Kids Only)' : 'Mocktail Bar (All Guests)';
        barItems.push({ text: mockLabel, rate: mr, action: 'barEnhancement', key: key });
        return;
      }
      if (key === 'bar_espresso_martinis') {
        var martiniRate2 = getEspressoMartiniRate();
        if (martiniRate2 > 0) {
          barItems.push({ text: barEnhancementLabels[key] || key, rate: martiniRate2, action: 'barEnhancement', key: key });
        }
        return;
      }
      const r = RATES.barEnhancements?.[key] || 0;
      barItems.push({ text: barEnhancementLabels[key] || key, rate: r, action: 'barEnhancement', key: key });
    });
    if (barItems.length) sections.push({ label: 'Bar', items: barItems, removable: true });

    // ── Venue Spaces (removable) ──
    const roomLbls = {
      'atlantic-room': 'The Atlantic Room', 'crystal-room': 'The Crystal Room',
      'terrace-room': 'The Terrace Room',   'lantern-room': 'The Lantern Room',
      'lobby-bar': 'The Lobby Bar',         'outdoor-gardens': 'Outdoor Gardens',
    };
    const venueItems = state.rooms.map(function(r) {
      return { text: roomLbls[r] || r.replace(/-/g, ' '), rate: roomRates[r] || 0, action: 'room', key: r, isFlat: true };
    });
    if (venueItems.length) sections.push({ label: 'Venue Space', items: venueItems, removable: true });

    // ── Build HTML ──
    const hasItems = sections.some(function(s) { return s.items.length > 0; });

    let html = '<div class="tr-quote-cart"><p class="tr-quote-cart__heading">Your Selection</p>';

    if (!hasItems) {
      html += '<p class="tr-quote-cart__empty">Your selections will appear here as you build your event.</p>';
    } else {
      sections.forEach(function(section, si) {
        if (!section.items.length) return;
        html += '<div class="tr-quote-cart__section"><p class="tr-quote-cart__section-label">' + section.label + '</p>';
        section.items.forEach(function(item) {
          if (!section.removable) {
            html += '<p class="tr-quote-cart__non-removable">' + item.text + '</p>';
          } else {
            const rateStr = item.isFlat
              ? (item.rate > 0 ? formatCurrency(item.rate) : '')
              : (item.rate > 0 ? formatCurrency(item.rate) + '/pp' : '');
            html += '<div class="tr-quote-cart__item">'
              + '<span class="tr-quote-cart__item-name">' + item.text + '</span>'
              + (rateStr ? '<span class="tr-quote-cart__item-rate">' + rateStr + '</span>' : '');
            if (item.action) {
              html += '<button class="tr-quote-cart__remove" aria-label="Remove" data-cart-remove="' + item.action + '" data-cart-key="' + item.key + '">&#215;</button>';
            }
            html += '</div>';
          }
        });
        html += '</div>';
        if (si < sections.length - 1) html += '<hr class="tr-quote-cart__divider">';
      });

      // Running total intentionally omitted from sidebar
    }

    html += '</div>';
    cart.innerHTML = html;
  }

  function removeCartItem(action, key) {
    switch (action) {
      case 'hors':
        state.horsOeuvres = null;
        getEls('[data-hors-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        break;
      case 'cocktailAddon': {
        state.cocktailAddons = state.cocktailAddons.filter(function(k) { return k !== key; });
        var cac = getEl('[data-cocktail-addon-card="' + key + '"]');
        if (cac) cac.classList.remove('is-selected');
        if (key === 'cocktail_savory_station') {
          state.savoryStations = [];
          var scl = getEl('[data-savory-checklist]');
          if (scl) { scl.style.display = 'none'; getEls('[data-savory-check]').forEach(function(cb){ cb.checked = false; }); }
        }
        break;
      }
      case 'course': {
        state.courses = state.courses.filter(function(k) { return k !== key; });
        var cc = getEl('[data-course-card="' + key + '"]');
        if (cc) cc.classList.remove('is-selected');
        break;
      }
      case 'mainCourse':
        state.mainCourse = null;
        getEls('[data-main-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        updatePlatedCourses();
        break;
      case 'entreeAddon': {
        state.entreeAddons = state.entreeAddons.filter(function(k) { return k !== key; });
        var eac = getEl('[data-entree-addon-card="' + key + '"]');
        if (eac) eac.classList.remove('is-selected');
        break;
      }
      case 'eventAddon': {
        state.eventAddons = state.eventAddons.filter(function(k) { return k !== key; });
        var evrc = getEl('[data-event-addon-card="' + key + '"]');
        if (evrc) evrc.classList.remove('is-selected');
        break;
      }
      case 'dessert':
        state.dessertBase = null;
        getEls('[data-dessert-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        break;
      case 'dessertAddon': {
        state.dessertAddons = state.dessertAddons.filter(function(k) { return k !== key; });
        var dac = getEl('[data-dessert-addon-card="' + key + '"]');
        if (dac) dac.classList.remove('is-selected');
        if (key === 'dessert_addon_sweet_station') {
          state.sweetStationQty = 0;
          var sws = getEl('[data-sweet-stepper]');
          if (sws) sws.style.display = 'none';
        }
        break;
      }
      case 'dessertAddonGroup': {
        // Combined Pastries/Cookies/Fresh Fruit row — clear all 3 real keys + cards at once
        state.dessertAddons = state.dessertAddons.filter(function(k) { return DESSERT_TRIO_KEYS.indexOf(k) === -1; });
        DESSERT_TRIO_KEYS.forEach(function(k) {
          var trioCard = getEl('[data-dessert-addon-card="' + k + '"]');
          if (trioCard) trioCard.classList.remove('is-selected');
        });
        break;
      }
      case 'afterDinner':
        state.afterDinner = null;
        getEls('[data-afterdinner-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        break;
      case 'bar':
        state.barPackage = null;
        state.cocktailTab = false;
        state.cashForCocktails = false;
        updateCocktailHandlingStatus();
        getEls('[data-bar-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        updateEspressoMartiniAvailability();
        break;
      case 'barEnhancement': {
        state.barEnhancements = state.barEnhancements.filter(function(k) { return k !== key; });
        var bec = getEl('[data-bar-enhancement-card="' + key + '"]');
        if (bec) bec.classList.remove('is-selected');
        if (key === 'bar_add_mocktail') {
          var msc = getEl('[data-mocktail-scope]');
          if (msc) msc.style.display = 'none';
        }
        break;
      }
      case 'room': {
        state.rooms = state.rooms.filter(function(r) { return r !== key; });
        const roomCard = getEl('[data-room-card="' + key + '"]');
        if (roomCard) roomCard.classList.remove('is-selected');
        break;
      }
    }
    renderCart();
    if (state.currentStep === state.totalSteps) renderEstimate();
  }

  function renderSummary() {
    const summaryMainLabels = {
      entree_plated:       'Plated Dinner',
      lunch_dinner_buffet: 'Buffet',
      food_stations:       'Food Stations',
      hors_doeuvres_heavy: "Heavily Passed Hors d'oeuvres",
      brunch_buffet:       'Brunch Buffet',
    };
    const summaryBarLabels = {
      bar_premium:     'Premium Open Bar',
      bar_cocktails:   'Full Open Bar',
      wine_beer:       state.cocktailTab ? 'Wine & Beer + Cocktail Tab' : 'Wine & Beer',
      bar_soft_drinks: 'Non-Alcoholic Beverages',
      bar_cash:        'Cash Bar',
    };
    const roomLabels = {
      'atlantic-room':   'The Atlantic Room',
      'crystal-room':    'The Crystal Room',
      'terrace-room':    'The Terrace Room',
      'lantern-room':    'The Lantern Room',
      'lobby-bar':       'The Lobby Bar',
      'outdoor-gardens': 'Outdoor Gardens & Arched Bridge',
    };
    const addonLabels = {
      cocktail_hour: 'Cocktail Hour Space',
      ceremony:      'Ceremony Setup',
      bridal_suite:  'Dedicated Bridal Suite',
      av_package:    'AV Package',
      photo_booth:   'Photo Booth Area',
      valet:         'Valet Parking',
    };
    const lines = [];

    if (state.eventType)
      lines.push({ label: 'Event', value: state.eventType });

    const guestParts = [];
    if (state.adults)   guestParts.push(`${state.adults} adult${state.adults !== 1 ? 's' : ''}`);
    if (state.children) guestParts.push(`${state.children} child${state.children !== 1 ? 'ren' : ''}`);
    if (guestParts.length) lines.push({ label: 'Guests', value: guestParts.join(', ') });

    if (state.date) {
      const d = new Date(state.date + 'T00:00:00');
      lines.push({ label: 'Date', value: d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) });
    }
    if (state.startTime) {
      lines.push({ label: 'Start Time', value: computeSchedule(state).display });
    }
    if (state.menuNotes)
      lines.push({ label: 'Menu Notes', value: state.menuNotes });

    if (state.mainCourse)
      lines.push({ label: 'Catering', value: summaryMainLabels[state.mainCourse] || state.mainCourse });
    if (state.barPackage)
      lines.push({ label: 'Bar Service', value: summaryBarLabels[state.barPackage] || state.barPackage });

    const roomNames = state.rooms.map(r => roomLabels[r] || r.replace(/_/g, ' '));
    if (roomNames.length) lines.push({ label: 'Venue Space', value: roomNames.join(', ') });

    const allEnhNames = []
      .concat((state.cocktailAddons || []).map(k => k === 'cocktail_savory_station' ? getSavoryLabel() : (cocktailAddonLabels[k] || k.replace(/_/g, ' '))))
      .concat((state.entreeAddons || []).map(k => entreeAddonLabels[k] || k.replace(/_/g, ' ')))
      .concat((state.eventAddons || []).map(k => eventAddonLabels[k] || k.replace(/_/g, ' ')))
      .concat(state.barEnhancements.map(k => barEnhancementDisplay(k)));
    if (allEnhNames.length) lines.push({ label: 'Enhancements', value: allEnhNames.join(', ') });

    const listEl = getEl('[data-estimate-summary]');
    if (!listEl) return;

    listEl.innerHTML = lines.map(l =>
      `<li style="display:flex; justify-content:space-between; gap:1rem; padding:0.4rem 0; border-bottom:1px solid rgba(255,255,255,0.06);">
        <span style="color:rgba(250,248,244,0.5); flex-shrink:0;">${l.label}</span>
        <span style="text-align:right;">${l.value}</span>
      </li>`
    ).join('');
  }

  /* ------------------------------------------------------------------
     LEAD CAPTURE & DRAFT ORDER
  ------------------------------------------------------------------ */
  async function submitLeadAndDeposit() {
    const depositBtn = getEl('[data-quote-deposit]');
    const endpoint = window.QuoteRates?.depositEndpoint;

    if (!endpoint) {
      showError('Deposit checkout is not yet configured. Please contact us directly at Events@WestchesterParty.com.');
      return;
    }

    // Validate required fields
    if (!isValidEmail(state.email)) {
      showError('Please enter a valid email address before reserving.');
      return;
    }

    const customer = {
      firstName: state.firstName,
      lastName:  state.lastName,
      email:     state.email,
      phone:     state.phone,
    };



    const estimate = calculateEstimate();
    // Deposit = 10% of grand total (including tax) — must match proposal
    const TAX_RATE = 0.08375;
    const taxableAmount = estimate.subtotal + estimate.adminFee;
    const taxAmount = Math.round(taxableAmount * TAX_RATE * 100) / 100;
    const grandTotal = Math.round((taxableAmount + taxAmount) * 100) / 100;
    const depositAmount = Math.max(Math.round(grandTotal * 0.10 * 100) / 100, 300);
    const ccFee = Math.round(depositAmount * CC_FEE_PCT * 100) / 100;

    // Clarity + GA4 begin_checkout
    if (typeof window.clarity === 'function') window.clarity('set', 'deposit_clicked', 'true');
    trackGA4('begin_checkout', {
      event_type: state.eventType || '',
      value: depositAmount + ccFee,
      currency: 'USD',
      guest_count: (state.adults || 0) + (state.children || 0),
      estimated_total: grandTotal,
      deposit_amount: depositAmount,
      rooms: state.rooms.join(',') || ''
    });

    // Loading state
    if (depositBtn) {
      depositBtn.disabled = true;
      depositBtn.dataset.originalText = depositBtn.textContent;
      depositBtn.textContent = 'Preparing Checkout...';
      depositBtn.classList.add('is-loading');
    }
    clearErrors();

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer,
          depositAmount,
          ccFee,
          eventType: state.eventType,
          proposalSummary: buildProposalSummary(),
          eventDetails: {
            eventDate: state.date ? new Date(state.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : (state.flexibleDate ? 'Flexible' : ''),
            guestCount: (state.adults || 0) + ' adults' + (state.children ? ', ' + state.children + ' children' : ''),
            rooms: state.rooms.map(function(r) { return r.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); }); }).join(', '),
            grandTotal: grandTotal,
            remainingBalance: Math.round((grandTotal - depositAmount) * 100) / 100
          },
          ga4ClientId: getGA4ClientId(),
          ga4SessionId: getGA4SessionId()
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Network error');
      }

      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (err) {
      var debugMsg = err && err.message ? err.message : String(err);
      trackGA4('quote_deposit_error', { event_type: state.eventType || '', estimated_total: grandTotal });
      showError('Unable to process your deposit at this time. Please contact us directly at Events@WestchesterParty.com. (Debug: ' + debugMsg + ')');
      console.error('Draft order error:', err);
      console.error('Endpoint was:', endpoint);
      // Reset button
      if (depositBtn) {
        depositBtn.disabled = false;
        depositBtn.textContent = depositBtn.dataset.originalText || 'Reserve with 10% Deposit';
        depositBtn.classList.remove('is-loading');
      }
    }
  }

  /* ------------------------------------------------------------------
     SEND PROPOSAL — venue notification + Flow automation
     Part A: POST to /contact (Shopify built-in) → venue gets full proposal
     Part B: POST to Worker → creates metaobject → triggers Flow + FlowMail emails
  ------------------------------------------------------------------ */
  function buildProposalSummary() {
    const estimate = calculateEstimate();
    const lines = [];

    lines.push('--- EVENT PROPOSAL ---');
    lines.push('');
    lines.push('Event Type: ' + (state.eventType || ''));
    lines.push('Guests: ' + state.adults + ' adults' + (state.children ? ', ' + state.children + ' children' : ''));

    if (state.date) {
      const d = new Date(state.date + 'T00:00:00');
      lines.push('Date: ' + d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }));
    } else if (state.flexibleDate) {
      const days = state.dayPreferences.length ? state.dayPreferences.join(', ') : 'Any';
      lines.push('Date: Flexible (' + days + ')');
    }

    if (state.startTime) {
      lines.push('Start Time: ' + computeSchedule(state).display);
    }
    if (state.dateTimeNotes) lines.push('Date/Time Notes: ' + state.dateTimeNotes);

    lines.push('');
    lines.push('--- FOOD & BEVERAGES ---');
    if (state.horsOeuvres) lines.push('Hors d\'Oeuvres: ' + (horsOeuvresLabels[state.horsOeuvres] || state.horsOeuvres));
    state.cocktailAddons.forEach(k => lines.push('Cocktail Enhancement: ' + (k === 'cocktail_savory_station' ? getSavoryLabel() : (cocktailAddonLabels[k] || k))));
    state.courses.forEach(k => lines.push('Course: ' + (courseLabels[k] || k)));
    if (state.mainCourse) lines.push('Main Course: ' + (mainCourseLabels[state.mainCourse] || state.mainCourse));
    state.entreeAddons.forEach(k => lines.push('Entrée Enhancement: ' + (entreeAddonLabels[k] || k)));
    state.eventAddons.forEach(k => lines.push('Event Enhancement: ' + (eventAddonLabels[k] || k)));
    if (state.dessertBase) lines.push('Dessert: ' + (dessertBaseLabels[state.dessertBase] || state.dessertBase));
    getDessertAddonDisplayList().forEach(function(d) { lines.push('Dessert Add-on: ' + d); });
    if (state.afterDinner) lines.push('After-Dinner: ' + (afterDinnerLabels[state.afterDinner] || state.afterDinner));

    lines.push('');
    lines.push('--- BAR ---');
    if (state.barPackage) {
      var bpLabel = (state.barPackage === 'wine_beer' && state.cocktailTab) ? barLabels['wine_beer_tab'] : (barLabels[state.barPackage] || state.barPackage);
      lines.push('Bar Package: ' + bpLabel);
    }
    if (state.barPackage === 'wine_beer' && state.cashForCocktails) {
      lines.push('Cocktail Handling: Guest-Paid Cocktails (Cash for Cocktails)');
    }
    state.barEnhancements.forEach(k => lines.push('Enhancement: ' + barEnhancementDisplay(k)));

    if (state.rooms.length) {
      lines.push('');
      lines.push('--- VENUE SPACE ---');
      state.rooms.forEach(r => lines.push('Room: ' + r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())));
    }

    if (state.menuNotes) {
      lines.push('');
      lines.push('--- SPECIAL REQUESTS ---');
      lines.push(state.menuNotes);
    }

    if (state.additionalRequests) {
      lines.push('');
      lines.push('--- ADDITIONAL REQUESTS ---');
      lines.push(state.additionalRequests);
    }

    if (state.notes) {
      lines.push('');
      lines.push('--- ADDITIONAL NOTES ---');
      lines.push(state.notes);
    }

    if (state.referral) {
      lines.push('');
      lines.push('--- REFERRAL ---');
      lines.push('How They Heard About Us: ' + state.referral);
    }

    lines.push('');
    lines.push('--- ESTIMATE ---');
    lines.push('Subtotal: ' + formatCurrency(estimate.subtotal));
    lines.push('Admin Fee (' + estimate.adminFeePct + '%): ' + formatCurrency(estimate.adminFee));
    var taxableAmt = estimate.subtotal + estimate.adminFee;
    var taxAmt = Math.round(taxableAmt * 0.08375 * 100) / 100;
    var grandTotal = Math.round((taxableAmt + taxAmt) * 100) / 100;
    lines.push('Tax (8.375%): ' + formatCurrency(taxAmt));
    lines.push('TOTAL: ' + formatCurrency(grandTotal));

    return lines.join('\n');
  }

  /* ------------------------------------------------------------------
     SHARED PROPOSAL PAYLOAD BUILDER
     Single source of truth for the server-PDF / proposal payload. Reads
     LIVE `state` (so the customer's name is always present) and converts
     every field to its display LABEL — cocktail/dessert/add-on/room/date —
     exactly the way the server template (pdf-templates.js buildProposalHtml)
     expects. Used by BOTH the inquiry proposal (submitProposal) and the
     signed contract (submitEsign) so the two PDFs can never diverge.
     PARITY: keep field set in sync with normalizeProposalData() in worker.js.
  ------------------------------------------------------------------ */
  function buildServerProposalPayload() {
    var estimate = calculateEstimate();
    var attr = getAttribution();
    var taxableAmt = estimate.subtotal + estimate.adminFee;
    var taxAmt = Math.round(taxableAmt * 0.08375 * 100) / 100;
    var grandTotal = Math.round((taxableAmt + taxAmt) * 100) / 100;
    var deposit = Math.max(Math.round(grandTotal * 0.10 * 100) / 100, 300);

    var dateStr = '';
    if (state.date) {
      var d = new Date(state.date + 'T00:00:00');
      dateStr = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } else if (state.flexibleDate) {
      dateStr = 'Flexible (' + (state.dayPreferences.length ? state.dayPreferences.join(', ') : 'Any') + ')';
    }

    return {
      firstName: state.firstName || '',
      lastName: state.lastName || '',
      email: state.email,
      phone: state.phone || '',
      eventType: state.eventType || '',
      eventTypeKey: state.eventType || '',
      /* §B — the normalized DISPLAY form of the occasion, new 2026-08-31.
         ⚠️ snake_case is DELIBERATE and ⛔ not a slip: this key name is the
         cross-front contract, named by ~/TR/projects/growth/specs/occasion-normalization.md
         §6, and the CRM reads it. Matching the consumer beats matching local camelCase.
         🔑 `eventType` above is left EXACTLY as the visitor typed it — ⛔ do not normalize
         it here. Three independent pullers consume it and the CRM derives
         `occasion_canonical` (the 12 reporting buckets) from it. Display and reporting
         are two jobs; growth §6 is explicit that one string must not do both. */
      occasion_display: normalizeOccasion(state.eventType),
      adults: state.adults || 0,
      children: state.children || 0,
      // As BILLED after the 25-guest event minimum — see calculateEstimate().
      // The contract's price schedule renders these, not the entered counts.
      billedAdults: estimate.billedAdults,
      billedChildren: estimate.billedChildren,
      date: dateStr,
      // ⛔ The full window, both ends already adjusted -- see computeSchedule().
      startTime: state.startTime ? computeSchedule(state).display : '',
      // What the contract may promise as unlimited bar. ⛔ NOT the event
      // duration: a ceremony lengthens the event and buys no bar hour.
      barHours: computeSchedule(state).barHours,
      dateTimeNotes: state.dateTimeNotes || '',
      horsOeuvres: horsOeuvresLabels[state.horsOeuvres] || state.horsOeuvres || '',
      cocktailAddons: state.cocktailAddons.map(function(k) { return k === 'cocktail_savory_station' ? getSavoryLabel() : (cocktailAddonLabels[k] || k); }),
      courses: state.courses.map(function(k) { return courseLabels[k] || k; }),
      mainCourse: mainCourseLabels[state.mainCourse] || state.mainCourse || '',
      mainCourseKey: state.mainCourse || '',
      entreeAddons: state.entreeAddons.map(function(k) { return entreeAddonLabels[k] || k; }),
      // Raw keys alongside the labels, mirroring barEnhancementKeys. The Worker
      // composes the steak entrée name from these; matching on labels alone is
      // the fallback it keeps for payloads sent before 2026-08-31.
      entreeAddonKeys: state.entreeAddons.slice(),
      // Labels only, deliberately. entreeAddonKeys exists because the Worker has
      // to COMPOSE a dish name from them; nothing composes anything from an event
      // addon, and ⛔ a wire field nothing reads is the same trap as an Admin
      // metafield that steers nothing (backlog 0d(b)).
      eventAddons: state.eventAddons.map(function(k) { return eventAddonLabels[k] || k; }),
      dessertBase: dessertBaseLabels[state.dessertBase] || state.dessertBase || '',
      dessertAddons: getDessertAddonDisplayList(),
      afterDinner: afterDinnerLabels[state.afterDinner] || state.afterDinner || '',
      barPackage: (state.barPackage === 'wine_beer' && state.cocktailTab) ? (barLabels['wine_beer_tab'] || 'Wine & Beer + Cocktail Tab') : (barLabels[state.barPackage] || state.barPackage || ''),
      barPackageKey: (state.barPackage === 'wine_beer' && state.cocktailTab) ? 'wine_beer_tab' : (state.barPackage || ''),
      cocktailHandling: state.barPackage === 'wine_beer'
        ? (state.cocktailTab ? 'Host-Paid Cocktail Tab' : state.cashForCocktails ? 'Guest-Paid Cocktails (Cash for Cocktails)' : '')
        : '',
      barEnhancements: state.barEnhancements.map(function(k) { return barEnhancementDisplay(k); }),
      barEnhancementKeys: state.barEnhancements.slice(),
      rooms: state.rooms.map(function(r) { return roomDisplayNames[r] || r.replace(/[-_]/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); }); }),
      menuNotes: state.menuNotes || '',
      additionalRequests: state.additionalRequests || '',
      notes: state.notes || '',
      subtotal: estimate.subtotal,
      adminFeePct: estimate.adminFeePct,
      adminFee: estimate.adminFee,
      tax: taxAmt,
      grandTotal: grandTotal,
      adultRate: estimate.adultRate,
      childRate: estimate.childRate,
      // Drives the contract's minimum-head-count basis (adult vs total).
      // ⛔ Computed ONCE, above, beside childBlended — the only place that value
      // exists. Do not re-derive it in a renderer.
      childRateElevated: Boolean(estimate.childRateElevated),
      lineItems: estimate.lineItems.map(function(i) { return { label: i.label, rate: i.rate, isBar: i.isBar, childOnly: i.childOnly || false, adultOnly: i.adultOnly || false, everyone: i.everyone || false }; }),
      deposit: deposit,
      referral: state.referral || '',
      followUpIntent: state.followUpIntent || '',
      // Ad attribution (from the tr_attr cookie; empty strings for organic).
      // Worker maps these camelCase keys → snake_case metaobject fields in Wave B.
      gclid: attr.gclid || '',
      gbraid: attr.gbraid || '',
      wbraid: attr.wbraid || '',
      utmSource: attr.utm_source || '',
      utmMedium: attr.utm_medium || '',
      utmCampaign: attr.utm_campaign || '',
      utmTerm: attr.utm_term || '',
      utmContent: attr.utm_content || '',
      attrCapturedAt: attr.ts || '',
      submittedAt: new Date().toISOString()
    };
  }
  // Expose for proposal.js (modal "Download PDF") so it shares the same builder.
  window.buildServerProposalPayload = buildServerProposalPayload;

  // Track whether user has acknowledged the dollar minimum via the popup
  var _minShortfallAcknowledged = false;
  var _capacityAcknowledged = false;   // set only by the explicit override button

  // Re-entrancy guard: nothing disables the Send-Proposal button or the lead-modal
  // Continue button for the duration of the awaited Worker call, so a double
  // click/tap (or a double-submit race between the two entry paths) reaches this
  // point twice before the first call resolves. Each call independently builds a
  // fresh `submittedAt` in buildServerProposalPayload(), so the Worker's
  // `email:submittedAt` idempotency key does NOT catch this case (it only catches
  // this function's own internal network retry, which reuses one `proposalData`).
  // Without this guard, two concurrent calls both reach the Worker and both
  // succeed, producing two metaobjects / two emails / two CRM rows for one visitor
  // action.
  var _submitProposalInFlight = false;

  async function submitProposal() {
    const btn = getEl('[data-send-proposal]');
    const statusEl = getEl('[data-send-proposal-status]');

    // ── §A: normalize + ENFORCE at the point the proposal is actually constructed ──
    // ⛔ The button gate and the input `required` attribute are UX, not enforcement: both
    // paths into this function (the Step 6 button and the lead modal) can be reached with
    // prefilled or restored state, and a scripted submit bypasses both. This is the one
    // place every proposal passes through, so it is where the contract is kept.
    // Normalizing here also guarantees the stored value matches the displayed one even if
    // the visitor never blurred the field (submit-by-Enter, autofill).
    state.firstName = normalizePersonName(state.firstName);
    state.lastName  = normalizePersonName(state.lastName);
    if (!state.firstName || !state.lastName) {
      if (statusEl) {
        statusEl.textContent = 'Please enter your first and last name.';
        statusEl.style.display = 'block';
        statusEl.style.color = '#991b1b';
      }
      var fnEl = getEl(state.firstName ? '[data-input-lastname]' : '[data-input-firstname]');
      if (fnEl) { try { fnEl.focus(); } catch (e) {} }
      return false;
    }

    // Validate email
    if (!isValidEmail(state.email)) {
      if (statusEl) {
        statusEl.textContent = 'Please enter a valid email address.';
        statusEl.style.display = 'block';
        statusEl.style.color = '#991b1b';
      }
      return false;
    }

    // ── Under-budget popup — show confirmation modal instead of blocking ──
    if (!_minShortfallAcknowledged) {
      var info = getShortfallInfo();
      if (info) {
        var modal = getEl('[data-proposal-budget-modal]');
        var textEl = getEl('[data-proposal-budget-text]');
        if (modal && textEl) {
          textEl.innerHTML = 'Reminder: you are currently under budget <strong>' + info.perPersonFmt + ' per person</strong> for ' + info.roomName + '. Would you like to make adjustments now or speak with our staff?';
          modal.style.display = 'flex';
          return; // Modal buttons handle proceed/adjust
        }
      }
    }

    // A concurrent call is already in flight (double click/tap before the button's
    // own visual state can stop a second event) — do not build a second payload
    // with a different submittedAt and send a second request.
    if (_submitProposalInFlight) {
      // The Worker's `deduped: true` field can no longer measure the underlying
      // double-tap rate — this guard now stops a second call before it ever
      // reaches the Worker. This is the only remaining signal for that rate.
      trackGA4('quote_double_submit_blocked', { event_type: state.eventType || '' });
      return false;
    }
    _submitProposalInFlight = true;

    try {
      return await submitProposalOnce();
    } finally {
      _submitProposalInFlight = false;
    }
  }

  async function submitProposalOnce() {
    const btn = getEl('[data-send-proposal]');
    const statusEl = getEl('[data-send-proposal-status]');

    // Loading state
    if (btn) { btn.classList.add('is-loading'); btn.textContent = 'Sending...'; }
    if (statusEl) statusEl.style.display = 'none';

    const proposalText = buildProposalSummary();
    let partBOk = false;

    // ── Build proposal data first (no PDF yet) ──
    // Shared builder = same payload the signed contract uses (parity).
    var proposalData = buildServerProposalPayload();
    proposalData.proposalText = proposalText;
    proposalData.ga4ClientId = getGA4ClientId();
    proposalData.ga4SessionId = getGA4SessionId();

    // ── Part A: POST to /contact (Shopify) — fire-and-forget redundant lead capture.
    //   /contact returns a 302 redirect that fetch follows by default, re-rendering
    //   the entire contact page (~2-4s). The Worker is the primary path (creates
    //   metaobject, sends both emails with PDF, feeds drip campaign), so we don't
    //   make the user wait on /contact. `redirect: 'manual'` skips the follow.
    //   NOTE: /contact only lands an email/lead in Shopify Admin — it does NOT feed
    //   the metaobject or any of the three pullers (FileMaker, CRM ×2). So its outcome
    //   is deliberately NOT a success signal for this flow: success is gated on the
    //   Worker (Part B) alone. See decisions/website.md → "The Worker is shared
    //   infrastructure" and the 2026-07-23 audit §D-1.
    // ── Part B: POST to Worker /submit-proposal (awaited, authoritative).
    //   PDF + email sending happen via ctx.waitUntil server-side after the response.
    try {
      const formData = new FormData();
      formData.append('form_type', 'contact');
      formData.append('utf8', '\u2713');
      formData.append('contact[email]', state.email);
      formData.append('contact[name]', (state.firstName + ' ' + state.lastName).trim());
      if (state.phone) formData.append('contact[phone]', state.phone);
      formData.append('contact[body]', proposalText);
      fetch('/contact#contact_form', {
        method: 'POST',
        body: formData,
        redirect: 'manual',
      }).catch(function(err) { console.error('Contact form error:', err); });
    } catch (err) {
      console.error('Contact form setup error:', err);
    }

    var workerPromise = (async function() {
      const depositUrl = window.QuoteRates?.depositEndpoint;
      if (!depositUrl) return false;
      var proposalUrl = depositUrl.replace(/\/create-draft-order\/?$/, '/submit-proposal');

      // One attempt: timeout-bounded fetch. Returns { ok, retryable }.
      // retryable = the failure is transient (network drop, timeout, 5xx) and did
      // not necessarily land, so a single retry is worthwhile. A 4xx is a client
      // error that will not self-heal, so we don't retry it.
      async function attempt() {
        var controller = new AbortController();
        var timer = setTimeout(function() { controller.abort(); }, 10000);
        try {
          var resp = await fetch(proposalUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(proposalData),
            signal: controller.signal,
          });
          if (resp.ok) return { ok: true };
          var bodyText = ''; try { bodyText = await resp.text(); } catch (_) {}
          console.error('Proposal submit error:', resp.status, bodyText);
          return { ok: false, retryable: resp.status >= 500 };
        } catch (err) {
          // Network failure or AbortController timeout — transient, worth one retry.
          console.error('Proposal submit error:', err);
          return { ok: false, retryable: true };
        } finally {
          clearTimeout(timer);
        }
      }

      var first = await attempt();
      if (first.ok) return true;
      if (!first.retryable) return false;
      // One retry on network/timeout/5xx. This can duplicate the send if the first
      // request actually landed but the response was lost — a duplicate proposal
      // email is an acceptable trade against a silently lost lead. Full retry-safety
      // needs /submit-proposal idempotency (2026-07-23 audit §D-19, separate item).
      var second = await attempt();
      return second.ok === true;
    })();

    // Only wait on the Worker — it's the source of truth for success.
    partBOk = await workerPromise;

    // PDF is now generated server-side by the Worker — no client-side generation needed

    // ── Update UI — success ONLY on a real Worker success ──
    //   The Worker is the sole channel that creates the metaobject the three pullers
    //   read, so it is the only channel whose success means the lead was actually
    //   captured. Gating on it (not "either channel") keeps the "Proposal Sent" UI,
    //   state.leadCaptured, and the quote_proposal_sent conversion honest, and makes
    //   the quote_proposal_error branch below reachable on any Worker failure.
    if (partBOk) {
      if (btn) { btn.classList.remove('is-loading'); btn.classList.add('is-success'); btn.textContent = 'Proposal Sent'; }
      if (statusEl) {
        statusEl.textContent = 'Your proposal has been sent. Our events team will reach out within 24 hours.';
        statusEl.style.display = 'block';
        statusEl.style.color = '#2d6a4f';
      }
      state._leadSubmitted = true;
      // Persist the gate flag and reveal the estimate the visitor just unlocked.
      state.leadCaptured = true;
      saveState();
      unlockEstimate();
      var revealEl = getEl('[data-estimate-reveal]');
      if (revealEl) revealEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Show e-sign section
      var esignSection = getEl('[data-esign-section]');
      if (esignSection) {
        esignSection.style.display = '';
        initEsignPad();
        esignSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Clarity + GA4 proposal sent tracking
      if (typeof window.clarity === 'function') window.clarity('set', 'proposal_sent', 'true');
      var proposalEstimate; try { proposalEstimate = calculateEstimate().total; } catch (_) { proposalEstimate = 0; }
      // value = 50% of tour-click value ($200). Static expected-revenue weight, not face value,
      // so Ads doesn't bid toward tire-kickers with the biggest quotes (see O1, 2026-05-22).
      trackGA4('quote_proposal_sent', {
        event_type: state.eventType,
        guest_count: (state.adults || 0) + (state.children || 0),
        estimated_total: proposalEstimate,
        value: 100,
        currency: 'USD',
        rooms: state.rooms.join(',') || '',
        main_course: state.mainCourse || '',
        bar_package: state.barPackage || ''
      });
      return true;
    } else {
      trackGA4('quote_proposal_error', { event_type: state.eventType || '', guest_count: (state.adults || 0) + (state.children || 0) });
      if (btn) { btn.classList.remove('is-loading'); btn.textContent = 'Send My Proposal'; }
      if (statusEl) {
        statusEl.textContent = 'Something went wrong. Please try again or email us at Events@WestchesterParty.com.';
        statusEl.style.display = 'block';
        statusEl.style.color = '#991b1b';
      }
      return false;
    }
  }

  /* ------------------------------------------------------------------
     MIMOSA HINT — show on Soft Drinks / Cash Bar cards only when brunch
     is selected, so the customer knows mimosas & bellinis aren't
     included with those packages (they require Wine & Beer or higher).
  ------------------------------------------------------------------ */
  function updateMimosaHints() {
    var show = state.mainCourse === 'brunch_buffet';
    getEls('[data-mimosa-hint]').forEach(function(el) {
      el.style.display = show ? 'block' : 'none';
    });
  }

  /* ------------------------------------------------------------------
     AFTER-DINNER CONDITIONAL AVAILABILITY
     Espresso/Cappuccino cards are conditional on main course selection:
     - "Brunch service" only available if brunch_buffet selected
     - "After-dinner service" only available if a non-brunch main selected
  ------------------------------------------------------------------ */
  // Stores the user's after-dinner selection before brunch overrides it
  var _preBrunchAfterDinner = null;

  function updateAfterDinnerAvailability() {
    var isBrunch = state.mainCourse === 'brunch_buffet';

    // For brunch: auto-select espresso brunch and disable all others
    if (isBrunch) {
      // Save current selection before brunch overrides it
      if (state.afterDinner && state.afterDinner !== 'coffee_espresso_brunch') {
        _preBrunchAfterDinner = state.afterDinner;
      }
      getEls('[data-afterdinner-card]').forEach(card => {
        var key = card.dataset.afterdinnerCard;
        if (key === 'coffee_espresso_brunch') {
          card.classList.add('is-selected');
          card.classList.remove('is-disabled');
          state.afterDinner = 'coffee_espresso_brunch';
        } else {
          card.classList.add('is-disabled');
          card.classList.remove('is-selected');
        }
      });
      renderCart();
      return;
    }

    // Non-brunch: use data-requires-main rules, also re-enable coffee_tea
    getEls('[data-afterdinner-card]').forEach(card => {
      var key = card.dataset.afterdinnerCard;
      if (!card.dataset.requiresMain) {
        // coffee_tea — always available for non-brunch
        card.classList.remove('is-disabled');
        return;
      }
      var allowed = card.dataset.requiresMain.split(',').map(s => s.trim());
      var isAvailable = !state.mainCourse || allowed.includes(state.mainCourse);
      card.classList.toggle('is-disabled', !isAvailable);
      if (!isAvailable && state.afterDinner === key) {
        state.afterDinner = null;
        card.classList.remove('is-selected');
      }
    });

    // Restore pre-brunch selection (or default to coffee_tea) when switching away from brunch
    if (!state.afterDinner) {
      var restoreKey = _preBrunchAfterDinner || 'coffee_tea';
      _preBrunchAfterDinner = null;
      // Verify the restore target is available (not disabled)
      var restoreCard = getEl('[data-afterdinner-card="' + restoreKey + '"]');
      if (restoreCard && !restoreCard.classList.contains('is-disabled')) {
        state.afterDinner = restoreKey;
        restoreCard.classList.add('is-selected');
      } else {
        // Fallback to coffee_tea if saved choice is unavailable
        var fallback = getEl('[data-afterdinner-card="coffee_tea"]');
        if (fallback && !fallback.classList.contains('is-disabled')) {
          state.afterDinner = 'coffee_tea';
          fallback.classList.add('is-selected');
        }
      }
    }
    renderCart();
  }

  /* ------------------------------------------------------------------
     FOOD PRE-SELECTION — apply recommended defaults on first Step 4 visit
     Brunch time (11 AM slot): Brunch Buffet + Cake + Espresso Bar (Brunch) + Wine & Beer
     All other times: 6-choice Hors d'oeuvres + Plated + Cake + Coffee & Tea + Full Open Bar
  ------------------------------------------------------------------ */
  function applyFoodDefaults() {
    state._foodDefaultsApplied = true;

    var isBrunchTime = (state.startTime || '').indexOf('11:00 AM') === 0;

    if (isBrunchTime) {
      // Brunch defaults
      if (!state.mainCourse) {
        state.mainCourse = 'brunch_buffet';
        getEls('[data-main-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        var brunchCard = getEl('[data-main-card="brunch_buffet"]');
        if (brunchCard) brunchCard.classList.add('is-selected');
        updateAfterDinnerAvailability(); // This auto-selects espresso brunch + disables others
        updatePlatedCourses();
      }
      if (!state.dessertBase) autoSelectDessertCake();
      if (!state.barPackage) {
        state.barPackage = 'wine_beer';
        getEls('[data-bar-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        var wbCard = getEl('[data-bar-card="wine_beer"]');
        if (wbCard) wbCard.classList.add('is-selected');
      }
    } else {
      // Standard evening/afternoon defaults
      if (!state.horsOeuvres) {
        state.horsOeuvres = 'hors_doeuvres_6_full';
        getEls('[data-hors-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        var horsCard = getEl('[data-hors-card="hors_doeuvres_6_full"]');
        if (horsCard) horsCard.classList.add('is-selected');
      }
      if (!state.mainCourse) {
        state.mainCourse = 'entree_plated';
        getEls('[data-main-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        var platedCard = getEl('[data-main-card="entree_plated"]');
        if (platedCard) platedCard.classList.add('is-selected');
        updateAfterDinnerAvailability();
        updatePlatedCourses();
      }
      if (!state.dessertBase) autoSelectDessertCake();
      if (!state.afterDinner) {
        state.afterDinner = 'coffee_tea';
        getEls('[data-afterdinner-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        var coffeeCard = getEl('[data-afterdinner-card="coffee_tea"]');
        if (coffeeCard) coffeeCard.classList.add('is-selected');
      }
      if (!state.barPackage) {
        state.barPackage = 'bar_cocktails';
        getEls('[data-bar-card]').forEach(function(c) { c.classList.remove('is-selected'); });
        var barCard = getEl('[data-bar-card="bar_cocktails"]');
        if (barCard) barCard.classList.add('is-selected');
      }
    }

    updateMenuVariants();
    renderCart();
    saveState();
  }

  /* ------------------------------------------------------------------
     AUTO-SELECT CELEBRATION CAKE — default dessert
  ------------------------------------------------------------------ */
  function autoSelectDessertCake() {
    state.dessertBase = 'dessert_cake';
    getEls('[data-dessert-card]').forEach(function(c) { c.classList.remove('is-selected'); });
    var cakeCard = getEl('[data-dessert-card="dessert_cake"]');
    if (cakeCard) cakeCard.classList.add('is-selected');
  }

  /* ------------------------------------------------------------------
     CHILDREN'S MENU AVAILABILITY
     Requires minimum 30 children to enable the Dedicated Children's Menu card.
  ------------------------------------------------------------------ */
  function updateChildrensMenuAvailability() {
    getEls('[data-requires-min-children]').forEach(card => {
      const min = parseInt(card.dataset.requiresMinChildren, 10) || 0;
      const childCount = state.children || 0;
      const isAvailable = childCount >= min;
      card.classList.toggle('is-disabled', !isAvailable);
      // If selected but no longer available, deselect
      if (!isAvailable) {
        const key = card.dataset.entreeAddonCard;
        if (key && state.entreeAddons.includes(key)) {
          state.entreeAddons = state.entreeAddons.filter(k => k !== key);
          card.classList.remove('is-selected');
          renderCart();
        }
      }
    });
  }

  /* ------------------------------------------------------------------
     EVENT ENHANCEMENT AVAILABILITY
     🔴 The hour extension is offered ONLY on the evening time slots. The full
     reasoning is on EVENING_TIME_SLOTS — in one line: this builder has no
     availability data, an extension off an afternoon slot can run into an
     evening turn, and a blocked turn costs ~2.5x what the extension earns.
     ⛔ Do not relax this to "warn the client instead"; a priced line on an
     instant proposal is a sale, not a warning.
  ------------------------------------------------------------------ */
  function updateEventAddonAvailability() {
    var note = getEl('[data-event-addon-note]');
    var evening = isEveningSlot(state.startTime);
    var anyGated = false;

    getEls('[data-requires-evening-slot]').forEach(function(card) {
      var key = card.dataset.eventAddonCard;
      card.classList.toggle('is-disabled', !evening);
      if (!evening) {
        anyGated = true;
        if (key && state.eventAddons.indexOf(key) !== -1) {
          // Deselect silently. ⛔ Do NOT call renderCart() here — this runs FROM
          // renderCart, before the estimate is built, so the dropped item is
          // already absent from the line items this pass computes.
          state.eventAddons = state.eventAddons.filter(function(k) { return k !== key; });
          card.classList.remove('is-selected');
        }
      }
    });

    if (!note) return;
    if (anyGated) {
      note.textContent = state.startTime
        ? 'Extending your event is available on our evening time slots. For a longer afternoon event, add a note below and we will build it with you.'
        : 'Select an evening time slot on Step 2 to add an additional hour. For a longer afternoon event, add a note below and we will build it with you.';
      note.style.display = '';
    } else {
      note.style.display = 'none';
    }
  }

  /** Reflect the Cocktail Requests modal choice (if any) on the Wine & Beer
      card. Hidden unless Wine & Beer is selected AND a choice has been made. */
  function updateCocktailHandlingStatus() {
    var statusEl = getEl('[data-cocktail-handling-status]');
    if (!statusEl) return;
    if (state.barPackage !== 'wine_beer') {
      statusEl.style.display = 'none';
      return;
    }
    var label = state.cocktailTab ? 'Host-Paid Cocktail Tab'
              : state.cashForCocktails ? 'Guest-Paid Cocktails'
              : null;
    if (label) {
      var statusText = getEl('[data-cocktail-handling-status-text]');
      if (statusText) statusText.textContent = label;
      statusEl.style.display = 'block';
    } else {
      statusEl.style.display = 'none';
    }
  }

  /** Open the Cocktail Requests modal, filling in live-computed dollar copy
      (never hardcoded) and highlighting the currently chosen option, if any. */
  function openCocktailRequestsModal() {
    var modal = getEl('[data-cocktail-requests-modal]');
    if (!modal) return;
    var RATES = window.QuoteRates || {};

    var priceEl = getEl('[data-guest-cocktail-price]');
    if (priceEl) {
      var guestPrice = Number(RATES.misc?.guest_cocktail_price || 12.5);
      priceEl.textContent = '$' + guestPrice.toFixed(2);
    }
    var deltaEl = getEl('[data-cocktail-upgrade-delta]');
    if (deltaEl) {
      var delta = (RATES.bar?.bar_cocktails || 0) - (RATES.bar?.wine_beer || 0);
      deltaEl.textContent = '+' + formatCurrency(delta);
    }

    getEls('[data-cocktail-request-option]').forEach(function(opt) {
      var key = opt.dataset.cocktailRequestOption;
      var isSelected = (key === 'host_tab' && state.cocktailTab) || (key === 'guest_paid' && state.cashForCocktails);
      opt.classList.toggle('is-selected', isSelected);
    });

    modal.style.display = 'flex';
  }

  function closeCocktailRequestsModal() {
    var modal = getEl('[data-cocktail-requests-modal]');
    if (modal) modal.style.display = 'none';
  }

  /** Sync the Espresso Martinis card with the current bar package:
      hidden on Cash Bar/Soft Drinks, "included" (disabled, no charge) on
      Premium Open Bar, a normal toggleable add-on on Full Open Bar/Wine & Beer. */
  function updateEspressoMartiniAvailability() {
    var card = getEl('[data-bar-enhancement-card="bar_espresso_martinis"]');
    if (!card) return;
    var includedNote = card.querySelector('[data-espresso-martini-included-note]');
    var learnMoreLink = card.querySelector('[data-espresso-martini-info]');
    var tooltip = card.querySelector('[data-espresso-martini-tooltip]');
    var allowed  = ESPRESSO_MARTINI_ALLOWED_PACKAGES.indexOf(state.barPackage) !== -1;
    var isPremium = state.barPackage === 'bar_premium';

    if (!allowed) {
      card.style.display = 'none';
      card.classList.remove('is-selected', 'is-disabled');
      state.barEnhancements = state.barEnhancements.filter(function(k) { return k !== 'bar_espresso_martinis'; });
      if (tooltip) tooltip.style.display = 'none';
    } else {
      card.style.display = '';
      if (isPremium) {
        card.classList.add('is-disabled');
        card.classList.remove('is-selected');
        state.barEnhancements = state.barEnhancements.filter(function(k) { return k !== 'bar_espresso_martinis'; });
        if (includedNote) includedNote.style.display = 'block';
        if (learnMoreLink) learnMoreLink.style.display = 'none';
        if (tooltip) tooltip.style.display = 'none';
      } else {
        card.classList.remove('is-disabled');
        if (includedNote) includedNote.style.display = 'none';
        if (learnMoreLink) learnMoreLink.style.display = '';
      }
    }
  }

  /* Gate the Mocktail "Kids Only" scope on ≥10 children. */
  function updateMocktailScopeAvailability() {
    var kidsLabel = getEl('[data-mocktail-kids-label]');
    var kidsRadio = getEl('[data-mocktail-scope-opt][value="kids"]');
    var note      = getEl('[data-mocktail-kids-note]');
    var enough    = (state.children || 0) >= 10;
    if (kidsRadio) kidsRadio.disabled = !enough;
    if (kidsLabel) kidsLabel.style.opacity = enough ? '1' : '0.5';
    if (note)      note.style.display = enough ? 'none' : 'block';
    // If kids-only was selected but no longer eligible, snap back to 'all'
    if (!enough && state.mocktailScope === 'kids') {
      state.mocktailScope = 'all';
      var allOpt = getEl('[data-mocktail-scope-opt][value="all"]');
      if (allOpt) allOpt.checked = true;
    }
  }

  // Food Stations require a 75-guest minimum. Below that the main-course card is
  // gated: dimmed, non-selectable, and a soft note appears on click. If the count
  // drops below the minimum after Food Stations was chosen, the selection is
  // cleared so no one reaches a price on an invalid format. Granular per-station
  // minimums (75/100/130 for 2/3/4 stations) are communicated in the modals; this
  // entry gate just enforces the floor.
  // 2026-08-19: the 1-station tier was RETIRED and the floor moved 40 -> 75, so two
  // stations is now the smallest station event we run. Canonical rule + every other
  // site that states it: specs/quote-calculator.md and specs/station-minimums-update.md.
  var STATIONS_MIN_GUESTS = 75;
  function refreshStationsGate() {
    var card = getEl('[data-stations-gate]');
    if (!card) return;
    var note      = card.querySelector('[data-stations-gate-note]');
    var guests    = (state.adults || 0) + (state.children || 0);
    var locked    = guests < STATIONS_MIN_GUESTS;
    card.classList.toggle('is-gated', locked);
    if (!locked && note) {
      note.classList.remove('is-visible');
      note.textContent = '';
    }
    // Clear an invalid prior selection.
    if (locked && state.mainCourse === 'food_stations') {
      state.mainCourse = '';
      card.classList.remove('is-selected');
      if (note) {
        note.textContent = 'Food Stations need at least ' + STATIONS_MIN_GUESTS + ' guests (you have ' + guests + '). Choose another format or add guests.';
        note.classList.add('is-visible');
      }
      renderCart();
    }
  }

  /* ------------------------------------------------------------------
     EVENT BINDING
  ------------------------------------------------------------------ */
  function bindEvents() {
    // Step navigation buttons
    const nextBtn = getEl('[data-quote-next]');
    const backBtn = getEl('[data-quote-back]');
    if (nextBtn) nextBtn.addEventListener('click', nextStep);
    if (backBtn) backBtn.addEventListener('click', function() {
      trackGA4('quote_step_back', { from_step: state.currentStep, to_step: state.currentStep - 1, event_type: state.eventType || '' });
      prevStep();
    });

    // Clickable progress step indicators — allow jumping back to completed steps
    getEls('[data-progress-step]').forEach(function(stepEl) {
      stepEl.addEventListener('click', function() {
        var target = parseInt(stepEl.dataset.progressStep, 10);
        if (target >= state.currentStep) return; // only allow jumping backward
        trackGA4('quote_step_jumped', { from_step: state.currentStep, to_step: target, event_type: state.eventType || '' });
        showStep(target);
      });
    });

    // Event type text input (Step 1)
    var eventTypeInput = getEl('[data-input-event-type]');
    if (eventTypeInput) {
      eventTypeInput.addEventListener('input', function(e) {
        state.eventType = e.target.value;
        trackGA4('quote_event_type_selected', { event_type: e.target.value, method: 'text' });
        renderCart();
      });
    }

    // Simple Proposal confirmation modal (Step 1)
    const spBtn = getEl('[data-simple-proposal-btn]');
    const spModal = getEl('[data-simple-proposal-modal]');
    const spCancel = getEl('[data-simple-proposal-cancel]');
    if (spBtn && spModal) {
      spBtn.addEventListener('click', () => {
        spModal.style.display = 'flex';
        trackGA4('quote_early_exit_click', { step: state.currentStep, event_type: state.eventType || '', guest_count: (state.adults || 0) + (state.children || 0) });
      });
      if (spCancel) spCancel.addEventListener('click', () => { spModal.style.display = 'none'; });
      spModal.addEventListener('click', e => { if (e.target === spModal) spModal.style.display = 'none'; });
    }

    // Adult/children count inputs (Step 1)
    const adultsInput   = getEl('[data-input-adults]');
    const childrenInput = getEl('[data-input-children]');
    if (adultsInput) {
      adultsInput.addEventListener('input', e => {
        state.adults = parseInt(e.target.value, 10) || 0;
        updateGuestMinimumNotice();
        refreshStationsGate();
        renderCart();
      });
    }
    if (childrenInput) {
      childrenInput.addEventListener('input', e => {
        state.children = parseInt(e.target.value, 10) || 0;
        updateGuestMinimumNotice();
        updateChildrensMenuAvailability();
        updateMocktailScopeAvailability();
        refreshStationsGate();
        renderCart();
      });
    }

    const notesInput = getEl('[data-input-notes]');
    if (notesInput) notesInput.addEventListener('input', e => { state.notes = e.target.value; saveState(); });

    // Date / flexible (Step 2)
    const dateInput     = getEl('[data-input-date]');
    const flexibleCheck = getEl('[data-input-flexible]');
    const dayPrefs      = getEls('[data-input-day-pref]');
    const flexibleDays  = getEl('[data-flexible-days]');

    if (dateInput) {
      // Enforce minimum date = today (prevent past date selection)
      var today = new Date();
      var todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
      dateInput.setAttribute('min', todayStr);

      // Open native date picker on any click — avoids text-entry mask glitch
      dateInput.addEventListener('click', function() { try { dateInput.showPicker(); } catch(e) {} });

      dateInput.addEventListener('change', e => {
        if (e.target.value && e.target.value < todayStr) {
          e.target.value = '';
          state.date = null;
          showError('Please select a date today or later.');
          return;
        }
        state.date = e.target.value;
        updateWeekdaySlots();
        renderCart();
      });
    }

    if (flexibleCheck) {
      flexibleCheck.addEventListener('change', e => {
        state.flexibleDate = e.target.checked;
        if (flexibleDays) flexibleDays.hidden = !e.target.checked;
        updateWeekdaySlots();
        renderCart();
      });
    }

    dayPrefs.forEach(cb => {
      cb.addEventListener('change', () => {
        state.dayPreferences = Array.from(getEls('[data-input-day-pref]:checked'))
          .map(el => el.value);
        updateWeekdaySlots();
        saveState();
      });
    });

    // Start time slot cards (Step 2)
    getEls('[data-time-slot]').forEach(card => {
      card.addEventListener('click', () => {
        getEls('[data-time-slot]').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        state.startTime = card.dataset.timeSlot;
        renderCart();
      });
    });

    // Date/time notes (Step 2 — optional)
    const dateTimeNotesInput = getEl('[data-input-datetime-notes]');
    if (dateTimeNotesInput) {
      dateTimeNotesInput.addEventListener('input', e => {
        state.dateTimeNotes = e.target.value.trim();
        saveState();
      });
    }

    // Menu notes (Step 4 — optional)
    const menuNotesInput = getEl('[data-input-menu-notes]');
    if (menuNotesInput) {
      menuNotesInput.addEventListener('input', e => {
        state.menuNotes = e.target.value.trim();
        saveState();
      });
    }

    // Additional requests (Step 5)
    const additionalRequestsInput = getEl('[data-input-additional-requests]');
    if (additionalRequestsInput) {
      additionalRequestsInput.addEventListener('input', e => {
        state.additionalRequests = e.target.value.trim();
        saveState();
      });
    }

    // Duration default (field removed — standard 4 hours)
    state.duration = '4';

    // Hors d'Oeuvres (Step 3 — optional toggle)
    getEls('[data-hors-card]').forEach(card => {
      card.addEventListener('click', () => {
        const key = card.dataset.horsCard;
        if (state.horsOeuvres === key) {
          state.horsOeuvres = null;
          card.classList.remove('is-selected');
        } else {
          getEls('[data-hors-card]').forEach(c => c.classList.remove('is-selected'));
          state.horsOeuvres = key;
          card.classList.add('is-selected');
        }
        updateMenuVariants();
        renderCart();
      });
    });

    // Cocktail hour enhancements (multi-select toggle cards)
    getEls('[data-cocktail-addon-card]').forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't toggle card when clicking the savory checklist itself
        if (e.target.closest('[data-savory-checklist]')) return;
        // Don't toggle card when clicking the "Learn More" link
        if (e.target.closest('[data-ceremony-open]')) return;
        const key = card.dataset.cocktailAddonCard;
        if (state.cocktailAddons.includes(key)) {
          state.cocktailAddons = state.cocktailAddons.filter(k => k !== key);
          card.classList.remove('is-selected');
          // Clear savory stations when deselecting
          if (key === 'cocktail_savory_station') {
            state.savoryStations = [];
            var sc = getEl('[data-savory-checklist]');
            if (sc) { sc.style.display = 'none'; getEls('[data-savory-check]').forEach(function(cb){ cb.checked = false; }); }
          }
        } else {
          state.cocktailAddons.push(key);
          card.classList.add('is-selected');
          // Show savory checklist when selecting
          if (key === 'cocktail_savory_station') {
            var sc = getEl('[data-savory-checklist]');
            if (sc) sc.style.display = 'block';
          }
        }
        renderCart();
      });
    });

    // Savory station checklist (multi-select — each its own price)
    getEls('[data-savory-check]').forEach(function (cb) {
      cb.addEventListener('change', function () {
        var k = this.value;
        if (this.checked) {
          if (state.savoryStations.indexOf(k) === -1) state.savoryStations.push(k);
        } else {
          state.savoryStations = state.savoryStations.filter(function (x) { return x !== k; });
        }
        saveState();
        renderCart();
      });
    });

    // Courses (multi-select toggle cards — locked when plated is selected)
    getEls('[data-course-card]').forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-locked')) return;
        const key = card.dataset.courseCard;
        if (state.courses.includes(key)) {
          state.courses = state.courses.filter(k => k !== key);
          card.classList.remove('is-selected');
        } else {
          state.courses.push(key);
          card.classList.add('is-selected');
        }
        renderCart();
      });
    });

    // Main Course (required select one)
    getEls('[data-main-card]').forEach(card => {
      card.addEventListener('click', () => {
        // Gated Food Stations: don't select; surface the soft minimum note.
        if (card.classList.contains('is-gated')) {
          var gateNote = card.querySelector('[data-stations-gate-note]');
          var g = (state.adults || 0) + (state.children || 0);
          if (gateNote) {
            gateNote.textContent = 'Food Stations need at least ' + STATIONS_MIN_GUESTS + ' guests (you have ' + g + '). Choose another format or add guests.';
            gateNote.classList.add('is-visible');
          }
          trackGA4('quote_stations_gate_blocked', { guests: g, event_type: state.eventType || '' });
          return;
        }
        getEls('[data-main-card]').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        state.mainCourse = card.dataset.mainCard;
        trackGA4('quote_menu_selected', { selection_type: 'main_course', selection_value: state.mainCourse, event_type: state.eventType || '' });
        updateAfterDinnerAvailability();
        updatePlatedCourses();
        updateMimosaHints();

        // Auto-default dessert to Celebration Cake if none selected yet
        if (!state.dessertBase) {
          autoSelectDessertCake();
        }

        // Brunch auto-defaults: Wine & Beer + Cake
        if (state.mainCourse === 'brunch_buffet') {
          // Auto-select Wine & Beer if no bar chosen yet
          if (!state.barPackage) {
            state.barPackage = 'wine_beer';
            getEls('[data-bar-card]').forEach(c => c.classList.remove('is-selected'));
            var wbCard = getEl('[data-bar-card="wine_beer"]');
            if (wbCard) wbCard.classList.add('is-selected');
          }
          // Auto-select Celebration Cake if no dessert chosen yet
          if (!state.dessertBase) {
            autoSelectDessertCake();
          }
        }

        renderCart();
      });
    });

    // Entrée enhancements (multi-select toggle cards)
    getEls('[data-entree-addon-card]').forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-disabled')) return;
        const key = card.dataset.entreeAddonCard;
        if (state.entreeAddons.includes(key)) {
          state.entreeAddons = state.entreeAddons.filter(k => k !== key);
          card.classList.remove('is-selected');
          trackGA4('quote_enhancement_toggled', { enhancement_type: 'entree', enhancement_key: key, action: 'removed', event_type: state.eventType || '' });
        } else {
          state.entreeAddons.push(key);
          card.classList.add('is-selected');
          trackGA4('quote_enhancement_toggled', { enhancement_type: 'entree', enhancement_key: key, action: 'added', event_type: state.eventType || '' });
        }
        renderCart();
      });
    });

    // Event enhancements (multi-select toggle cards)
    getEls('[data-event-addon-card]').forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-disabled')) return;
        const key = card.dataset.eventAddonCard;
        if (state.eventAddons.includes(key)) {
          state.eventAddons = state.eventAddons.filter(k => k !== key);
          card.classList.remove('is-selected');
          trackGA4('quote_enhancement_toggled', { enhancement_type: 'event', enhancement_key: key, action: 'removed', event_type: state.eventType || '' });
        } else {
          state.eventAddons.push(key);
          card.classList.add('is-selected');
          trackGA4('quote_enhancement_toggled', { enhancement_type: 'event', enhancement_key: key, action: 'added', event_type: state.eventType || '' });
        }
        renderCart();
      });
    });

    // Dessert base (optional select one, toggleable)
    getEls('[data-dessert-card]').forEach(card => {
      card.addEventListener('click', () => {
        const key = card.dataset.dessertCard;
        if (state.dessertBase === key) {
          state.dessertBase = null;
          card.classList.remove('is-selected');
        } else {
          getEls('[data-dessert-card]').forEach(c => c.classList.remove('is-selected'));
          state.dessertBase = key;
          card.classList.add('is-selected');
        }
        renderCart();
      });
    });

    // Dessert add-ons (multi-select toggle cards)
    getEls('[data-dessert-addon-card]').forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't toggle the card when clicking the sweet-station stepper
        if (e.target.closest('[data-sweet-stepper]')) return;
        const key = card.dataset.dessertAddonCard;
        if (state.dessertAddons.includes(key)) {
          state.dessertAddons = state.dessertAddons.filter(k => k !== key);
          card.classList.remove('is-selected');
          if (key === 'dessert_addon_sweet_station') {
            state.sweetStationQty = 0;
            var sw = getEl('[data-sweet-stepper]');
            if (sw) sw.style.display = 'none';
          }
        } else {
          state.dessertAddons.push(key);
          card.classList.add('is-selected');
          if (key === 'dessert_addon_sweet_station') {
            state.sweetStationQty = Math.max(1, state.sweetStationQty || 0);
            var sw = getEl('[data-sweet-stepper]');
            if (sw) sw.style.display = 'flex';
            updateSweetQtyDisplay();
          }
        }
        renderCart();
      });
    });

    // Sweet station quantity stepper (cap at 5)
    function updateSweetQtyDisplay() {
      var q = getEl('[data-sweet-qty]');
      if (q) q.textContent = state.sweetStationQty || 1;
    }
    var sweetInc = getEl('[data-sweet-inc]');
    var sweetDec = getEl('[data-sweet-dec]');
    if (sweetInc) sweetInc.addEventListener('click', function (e) {
      e.stopPropagation();
      state.sweetStationQty = Math.min(5, (state.sweetStationQty || 1) + 1);
      updateSweetQtyDisplay(); saveState(); renderCart();
    });
    if (sweetDec) sweetDec.addEventListener('click', function (e) {
      e.stopPropagation();
      state.sweetStationQty = Math.max(1, (state.sweetStationQty || 1) - 1);
      updateSweetQtyDisplay(); saveState(); renderCart();
    });

    // After-dinner (optional select one, toggleable — some options conditional on main course)
    getEls('[data-afterdinner-card]').forEach(card => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-disabled')) return;
        const key = card.dataset.afterdinnerCard;
        if (state.afterDinner === key) {
          state.afterDinner = null;
          card.classList.remove('is-selected');
        } else {
          getEls('[data-afterdinner-card]').forEach(c => c.classList.remove('is-selected'));
          state.afterDinner = key;
          card.classList.add('is-selected');
        }
        renderCart();
      });
    });

    // Bar package (required select one)
    getEls('[data-bar-card]').forEach(card => {
      card.addEventListener('click', () => {
        getEls('[data-bar-card]').forEach(c => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        state.barPackage = card.dataset.barCard;
        trackGA4('quote_bar_selected', { selection_type: 'bar_package', selection_value: state.barPackage, event_type: state.eventType || '' });
        if (state.barPackage !== 'wine_beer') {
          state.cocktailTab = false;
          state.cashForCocktails = false;
        }
        updateCocktailHandlingStatus();
        // Enable/disable Aperol & Hugo Spritz card (Wine & Beer only)
        var spritzCard = getEl('[data-wine-beer-only][data-bar-enhancement-card="bar_spritz_upgrade"]');
        if (spritzCard) {
          if (state.barPackage === 'wine_beer') {
            spritzCard.classList.remove('is-disabled');
          } else {
            spritzCard.classList.add('is-disabled');
            spritzCard.classList.remove('is-selected');
            state.barEnhancements = state.barEnhancements.filter(function(k) { return k !== 'bar_spritz_upgrade'; });
          }
        }
        updateEspressoMartiniAvailability();
        renderCart();
        // Wine & Beer opens the Cocktail Requests modal so the host chooses
        // how guest cocktail requests are handled during the event.
        if (state.barPackage === 'wine_beer') openCocktailRequestsModal();
      });
    });

    // Cocktail Requests modal (Wine & Beer bar package)
    var cocktailModal = getEl('[data-cocktail-requests-modal]');
    if (cocktailModal) {
      var cocktailModalClose = getEl('[data-cocktail-requests-close]');
      if (cocktailModalClose) cocktailModalClose.addEventListener('click', closeCocktailRequestsModal);
      cocktailModal.addEventListener('click', function(e) { if (e.target === cocktailModal) closeCocktailRequestsModal(); });

      getEls('[data-cocktail-request-option]').forEach(function(opt) {
        opt.addEventListener('click', function() {
          var choice = opt.dataset.cocktailRequestOption;
          if (choice === 'upgrade_full_bar') {
            closeCocktailRequestsModal();
            var fullBarCard = getEl('[data-bar-card="bar_cocktails"]');
            if (fullBarCard) fullBarCard.click(); // reuses the bar-card selection logic above
            return;
          }
          state.cocktailTab = (choice === 'host_tab');
          state.cashForCocktails = (choice === 'guest_paid');
          updateCocktailHandlingStatus();
          renderCart();
          closeCocktailRequestsModal();
        });
      });
    }

    var cocktailStatusEdit = getEl('[data-cocktail-handling-edit]');
    if (cocktailStatusEdit) {
      cocktailStatusEdit.addEventListener('click', function(e) {
        e.stopPropagation();
        openCocktailRequestsModal();
      });
    }

    // Cash bar info tooltip toggle
    const cashInfoLink = getEl('[data-cash-bar-info]');
    const cashTooltip  = getEl('[data-cash-bar-tooltip]');
    const cashClose    = getEl('[data-cash-bar-close]');
    if (cashInfoLink && cashTooltip) {
      cashInfoLink.addEventListener('click', (e) => {
        e.stopPropagation();
        cashTooltip.style.display = cashTooltip.style.display === 'none' ? 'block' : 'none';
      });
    }
    if (cashClose && cashTooltip) {
      cashClose.addEventListener('click', (e) => {
        e.stopPropagation();
        cashTooltip.style.display = 'none';
      });
    }

    // Celebration Cake signature tooltip toggle
    var cakeToggle = getEl('[data-cake-signature-toggle]');
    var cakeTip    = getEl('[data-cake-signature-tip]');
    if (cakeToggle && cakeTip) {
      cakeToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        cakeTip.style.display = cakeTip.style.display === 'none' ? 'block' : 'none';
      });
    }

    // Espresso Martinis tooltip toggle
    const espressoInfoLink = getEl('[data-espresso-martini-info]');
    const espressoTooltip  = getEl('[data-espresso-martini-tooltip]');
    const espressoClose    = getEl('[data-espresso-martini-close]');
    if (espressoInfoLink && espressoTooltip) {
      espressoInfoLink.addEventListener('click', (e) => {
        e.stopPropagation();
        espressoTooltip.style.display = espressoTooltip.style.display === 'none' ? 'block' : 'none';
      });
    }
    if (espressoClose && espressoTooltip) {
      espressoClose.addEventListener('click', (e) => {
        e.stopPropagation();
        espressoTooltip.style.display = 'none';
      });
    }

    // Champagne Wall tooltip toggle
    const champagneInfoLink = getEl('[data-champagne-wall-info]');
    const champagneTooltip  = getEl('[data-champagne-wall-tooltip]');
    const champagneClose    = getEl('[data-champagne-wall-close]');
    if (champagneInfoLink && champagneTooltip) {
      champagneInfoLink.addEventListener('click', (e) => {
        e.stopPropagation();
        champagneTooltip.style.display = champagneTooltip.style.display === 'none' ? 'block' : 'none';
      });
    }
    if (champagneClose && champagneTooltip) {
      champagneClose.addEventListener('click', (e) => {
        e.stopPropagation();
        champagneTooltip.style.display = 'none';
      });
    }

    // Aperol & Hugo Spritz tooltip toggle
    const spritzInfoLink = getEl('[data-spritz-info]');
    const spritzTooltip  = getEl('[data-spritz-tooltip]');
    const spritzClose    = getEl('[data-spritz-close]');
    if (spritzInfoLink && spritzTooltip) {
      spritzInfoLink.addEventListener('click', (e) => {
        e.stopPropagation();
        spritzTooltip.style.display = spritzTooltip.style.display === 'none' ? 'block' : 'none';
      });
    }
    if (spritzClose && spritzTooltip) {
      spritzClose.addEventListener('click', (e) => {
        e.stopPropagation();
        spritzTooltip.style.display = 'none';
      });
    }

    // Wine service at table tooltip toggle
    const wineInfoLink = getEl('[data-wine-table-info]');
    const wineTooltip  = getEl('[data-wine-table-tooltip]');
    const wineClose    = getEl('[data-wine-table-close]');
    if (wineInfoLink && wineTooltip) {
      wineInfoLink.addEventListener('click', (e) => {
        e.stopPropagation();
        wineTooltip.style.display = wineTooltip.style.display === 'none' ? 'block' : 'none';
      });
    }
    if (wineClose && wineTooltip) {
      wineClose.addEventListener('click', (e) => {
        e.stopPropagation();
        wineTooltip.style.display = 'none';
      });
    }

    // Bar enhancements (multi-select toggle cards)
    getEls('[data-bar-enhancement-card]').forEach(card => {
      card.addEventListener('click', (e) => {
        if (card.classList.contains('is-disabled')) return;
        // Don't toggle the card when interacting with the mocktail scope selector
        if (e.target.closest('[data-mocktail-scope]')) return;
        const key = card.dataset.barEnhancementCard;
        if (state.barEnhancements.includes(key)) {
          state.barEnhancements = state.barEnhancements.filter(k => k !== key);
          card.classList.remove('is-selected');
          trackGA4('quote_enhancement_toggled', { enhancement_type: 'bar', enhancement_key: key, action: 'removed', event_type: state.eventType || '' });
          if (key === 'bar_add_mocktail') {
            var ms = getEl('[data-mocktail-scope]');
            if (ms) ms.style.display = 'none';
          }
        } else {
          state.barEnhancements.push(key);
          card.classList.add('is-selected');
          trackGA4('quote_enhancement_toggled', { enhancement_type: 'bar', enhancement_key: key, action: 'added', event_type: state.eventType || '' });
          if (key === 'bar_add_mocktail') {
            var ms2 = getEl('[data-mocktail-scope]');
            if (ms2) ms2.style.display = 'block';
            updateMocktailScopeAvailability();
          }
        }
        renderCart();
      });
    });

    // Mocktail scope radios (Kids & Adults / Kids Only)
    getEls('[data-mocktail-scope-opt]').forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (this.value === 'kids' && (state.children || 0) < 10) {
          // Block kids-only until ≥10 children; snap back to 'all'
          state.mocktailScope = 'all';
          var allOpt = getEl('[data-mocktail-scope-opt][value="all"]');
          if (allOpt) allOpt.checked = true;
        } else {
          state.mocktailScope = this.value;
        }
        saveState();
        renderCart();
      });
    });

    // Room cards (Step 3) — single or multi select (respects headcount gate)
    getEls('[data-room-card]').forEach(card => {
      card.addEventListener('click', () => {
        // Block selection if room is gated by headcount minimum
        if (card.classList.contains('is-gated')) return;

        const roomKey = card.dataset.roomCard;
        const isMulti = card.dataset.multiSelect === 'true';
        if (isMulti) {
          card.classList.toggle('is-selected');
          const idx = state.rooms.indexOf(roomKey);
          if (idx > -1) state.rooms.splice(idx, 1);
          else          state.rooms.push(roomKey);
        } else {
          getEls('[data-room-card]').forEach(c => c.classList.remove('is-selected'));
          card.classList.add('is-selected');
          state.rooms = [roomKey];
        }
        trackGA4('quote_room_selected', { room: roomKey, event_type: state.eventType || '', guest_count: (state.adults || 0) + (state.children || 0) });
        renderCart();
      });
    });

    // Addon checkboxes (Step 5 — Enhancements) — informational only, not tracked in estimate

    // Lead capture fields (Step 6 — below estimate)
    const proposalBtn = getEl('[data-send-proposal]');
    const fields = {
      '[data-input-firstname]': 'firstName',
      '[data-input-lastname]':  'lastName',
      '[data-input-email]':     'email',
      '[data-input-phone]':     'phone',
    };
    Object.entries(fields).forEach(([selector, key]) => {
      const el = getEl(selector);
      if (!el) return;
      el.addEventListener('input', e => {
        state[key] = e.target.value;
        saveState();
        if (key === 'firstName' || key === 'lastName') renderProposalHeader();
        syncProposalButton();
      });
      // §A: standardize the name once the visitor leaves the field, so what they see is
      // what we store. ⛔ NEVER on 'input' — rewriting mid-keystroke moves the caret and
      // is the class of bug §C spent a session ruling out. normalizePersonName is
      // idempotent, so a re-blur on an already-clean value is a no-op.
      if (key === 'firstName' || key === 'lastName') {
        el.addEventListener('blur', e => {
          const clean = normalizePersonName(e.target.value);
          if (clean !== e.target.value) e.target.value = clean;
          state[key] = clean;
          saveState();
          renderProposalHeader();
          syncProposalButton();
        });
      }
    });

    // Referral dropdown (Step 6 — below estimate)
    var referralSelect = getEl('[data-input-referral]');
    if (referralSelect) referralSelect.addEventListener('change', function(e) {
      state.referral = e.target.value;
      saveState();
      syncProposalButton();
    });

    // Follow-up intent dropdown (Step 6 — below referral)
    var intentSelect = getEl('[data-input-followup-intent]');
    if (intentSelect) intentSelect.addEventListener('change', function(e) {
      state.followUpIntent = e.target.value;
      saveState();
      syncProposalButton();
    });

    function syncProposalButton() {
      if (!proposalBtn) return;
      // §A 2026-08-31: first AND last name are now REQUIRED. Before this, the button lit
      // up without either — `firstName` arrived EMPTY on 18.7% of 465 submissions while
      // `lastName` was empty on 0.0%, because the First Name box was 26-71px wide on
      // phones (§C). ⛔ The box fix shipped FIRST; requiring a field nobody can see turns
      // a data problem into lost leads. The modal path already required firstName, so the
      // two entry paths disagreed — this is what aligns them.
      var isReady = isProposalReady();
      proposalBtn.disabled = !isReady;
      var hint = getEl('[data-send-proposal-hint]');
      if (hint) hint.style.display = isReady ? 'none' : '';
    }

    // Send Proposal button — submit directly (intent captured via dropdown)
    if (proposalBtn) proposalBtn.addEventListener('click', function() {
      // Validate phone
      var phoneDigits = (state.phone || '').replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        var statusEl = getEl('[data-send-proposal-status]');
        if (statusEl) {
          statusEl.textContent = 'Please enter your phone number.';
          statusEl.style.display = 'block';
          statusEl.style.color = '#991b1b';
        }
        return;
      }
      submitProposal();
    });


    // Show e-sign after viewing proposal (modal close) if lead info is present
    document.addEventListener('tr:proposal-viewed', function() {
      if (isValidEmail(state.email) && state.firstName) {
        var esignSection = getEl('[data-esign-section]');
        if (esignSection && esignSection.style.display === 'none') {
          esignSection.style.display = '';
          initEsignPad();
        }
      }
    });

    // ── Phone mask (XXX) XXX-XXXX — cursor-aware ──
    getEls('[data-phone-mask]').forEach(function(input) {
      input.addEventListener('input', function(e) {
        var cursorPos = e.target.selectionStart;
        var prevVal = e.target.value;
        // Count digits before cursor in the pre-format value
        var digitsBefore = prevVal.substring(0, cursorPos).replace(/\D/g, '').length;
        var x = prevVal.replace(/\D/g, '').substring(0, 10);
        var formatted = '';
        if (x.length > 0) formatted = '(' + x.substring(0, 3);
        if (x.length >= 3) formatted += ') ' + x.substring(3, 6);
        if (x.length >= 6) formatted += '-' + x.substring(6, 10);
        e.target.value = formatted;
        // Restore cursor: walk through formatted string until we pass the same number of digits
        var newPos = 0;
        var counted = 0;
        for (var i = 0; i < formatted.length && counted < digitsBefore; i++) {
          if (/\d/.test(formatted[i])) counted++;
          newPos = i + 1;
        }
        e.target.setSelectionRange(newPos, newPos);
        // Update state
        if (e.target.matches('[data-input-phone]')) {
          state.phone = formatted;
          saveState();
        }
      });
    });

    // ── Lead capture modal logic ──
    var leadModal = getEl('[data-lead-modal]');
    var leadSubmitBtn = getEl('[data-lead-modal-submit]');
    var leadCancelBtn = getEl('[data-lead-modal-cancel]');
    var leadErrorEl = getEl('[data-lead-modal-error]');
    var pendingLeadAction = null; // stores the callback to execute after capture

    function hasLeadInfo() {
      return state.leadCaptured || state._leadSubmitted || (isValidEmail(state.email) && state.firstName);
    }

    function showLeadModal(actionCallback) {
      if (!leadModal) return actionCallback();
      pendingLeadAction = actionCallback;
      // Pre-fill modal from state if anything was already entered in the bottom form
      var mFirst = getEl('[data-lead-firstname]');
      var mLast = getEl('[data-lead-lastname]');
      var mEmail = getEl('[data-lead-email]');
      var mPhone = getEl('[data-lead-phone]');
      var mReferral = getEl('[data-lead-referral]');
      var mIntent = getEl('[data-lead-followup-intent]');
      if (mFirst) mFirst.value = state.firstName || '';
      if (mLast) mLast.value = state.lastName || '';
      if (mEmail) mEmail.value = state.email || '';
      if (mPhone) mPhone.value = state.phone || '';
      if (mReferral) mReferral.value = state.referral || '';
      if (mIntent) mIntent.value = state.followUpIntent || '';
      if (leadErrorEl) leadErrorEl.style.display = 'none';
      leadModal.style.display = 'flex';
    }

    function closeLeadModal() {
      if (leadModal) leadModal.style.display = 'none';
      pendingLeadAction = null;
    }

    function syncLeadToForm() {
      // Sync modal values to the bottom contact form
      var mainFirst = getEl('[data-input-firstname]');
      var mainLast = getEl('[data-input-lastname]');
      var mainEmail = getEl('[data-input-email]');
      var mainPhone = getEl('[data-input-phone]');
      var mainReferral = getEl('[data-input-referral]');
      if (mainFirst) mainFirst.value = state.firstName || '';
      if (mainLast) mainLast.value = state.lastName || '';
      if (mainEmail) mainEmail.value = state.email || '';
      if (mainPhone) mainPhone.value = state.phone || '';
      if (mainReferral) mainReferral.value = state.referral || '';
      var mainIntent = getEl('[data-input-followup-intent]');
      if (mainIntent) mainIntent.value = state.followUpIntent || '';
      if (proposalBtn) {
        proposalBtn.disabled = !isProposalReady();
      }
      renderProposalHeader();
    }

    if (leadSubmitBtn) {
      leadSubmitBtn.addEventListener('click', async function() {
        var mFirst = getEl('[data-lead-firstname]');
        var mLast = getEl('[data-lead-lastname]');
        var mEmail = getEl('[data-lead-email]');
        var mPhone = getEl('[data-lead-phone]');
        var mReferral = getEl('[data-lead-referral]');
        var mIntent = getEl('[data-lead-followup-intent]');

        // §A: same normalizer as the Step 6 form — one rule, both entry paths.
        var firstName = normalizePersonName(mFirst ? mFirst.value : '');
        var lastName = normalizePersonName(mLast ? mLast.value : '');
        var email = mEmail ? mEmail.value.trim() : '';
        var phone = mPhone ? mPhone.value.trim() : '';
        var referral = mReferral ? mReferral.value : '';
        var followUpIntent = mIntent ? mIntent.value : '';

        if (!firstName || !lastName) {
          // §A 2026-08-31: lastName is now required here too. It never was, which is why
          // the two entry paths disagreed about what a complete lead looks like.
          if (leadErrorEl) { leadErrorEl.textContent = 'Please enter your first and last name.'; leadErrorEl.style.display = 'block'; }
          return;
        }
        if (!isValidEmail(email)) {
          if (leadErrorEl) { leadErrorEl.textContent = 'Please enter a valid email address.'; leadErrorEl.style.display = 'block'; }
          return;
        }
        if (!referral) {
          if (leadErrorEl) { leadErrorEl.textContent = 'Please tell us how you heard about us.'; leadErrorEl.style.display = 'block'; }
          return;
        }
        if (!followUpIntent) {
          if (leadErrorEl) { leadErrorEl.textContent = 'Please select how you would like us to follow up.'; leadErrorEl.style.display = 'block'; }
          return;
        }
        var phoneDigitsModal = phone.replace(/\D/g, '');
        if (phoneDigitsModal.length < 10) {
          if (leadErrorEl) { leadErrorEl.textContent = 'Please enter your phone number.'; leadErrorEl.style.display = 'block'; }
          return;
        }

        // Populate state
        state.firstName = firstName;
        state.lastName = lastName;
        state.email = email;
        state.phone = phone;
        state.referral = referral;
        state.followUpIntent = followUpIntent;
        saveState();
        syncLeadToForm();

        // Submit proposal in background (same as Send My Proposal)
        leadSubmitBtn.textContent = 'Sending...';
        leadSubmitBtn.disabled = true;
        // Do not unlock or continue until the Worker has accepted the proposal.
        // `generate_lead` marks Step-6 arrival, not capture; only submitProposal's
        // true result means the metaobject-backed lead exists.
        var submitted = await submitProposal();
        if (!submitted) {
          leadSubmitBtn.textContent = 'Continue';
          leadSubmitBtn.disabled = false;
          return;
        }

        // Close modal and execute the pending action
        leadModal.style.display = 'none';
        leadSubmitBtn.textContent = 'Continue';
        leadSubmitBtn.disabled = false;

        if (pendingLeadAction) {
          var action = pendingLeadAction;
          pendingLeadAction = null;
          action();
        }
      });
    }

    if (leadCancelBtn) leadCancelBtn.addEventListener('click', closeLeadModal);
    if (leadModal) leadModal.addEventListener('click', function(e) { if (e.target === leadModal) closeLeadModal(); });

    // ── CTA interception — require lead info before proceeding ──
    // Deposit CTA (blocked when under minimum via updateDepositGate)
    const depositBtn = getEl('[data-quote-deposit]');
    if (depositBtn) {
      depositBtn.addEventListener('click', function() {
        // Double-check: if under minimum, don't proceed
        var shortfall = getShortfallInfo();
        if (shortfall) return;
        if (hasLeadInfo()) {
          submitLeadAndDeposit();
        } else {
          showLeadModal(function() { submitLeadAndDeposit(); });
        }
      });
    }

    // View Proposal CTA — gate on a real submission. state._leadSubmitted is set
    // after a successful submitProposal() this session; state.leadCaptured is the
    // persisted equivalent for a returning visitor who already submitted (and is
    // therefore already unlocked). We deliberately do NOT use the typed-in
    // firstName+email here, which would silently bypass the proposal email send.
    // Note: with the Step 6 gate in place this button is hidden until the estimate
    // is unlocked, so this guard is now defensive rather than load-bearing.
    var viewProposalBtn = getEl('#tr-view-proposal');
    if (viewProposalBtn) {
      viewProposalBtn.addEventListener('click', function(e) {
        if (!state._leadSubmitted && !state.leadCaptured) {
          e.stopImmediatePropagation();
          showLeadModal(function() { viewProposalBtn.click(); });
        }
        // If the proposal email was already sent, let proposal.js open the viewing modal.
      });
    }

    // Schedule Tour (Calendly) CTA
    var tourBtn = getEl('[data-lead-action="calendly"]');
    if (tourBtn) {
      tourBtn.addEventListener('click', function(e) {
        if (!hasLeadInfo()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showLeadModal(function() {
            // Open Calendly with prefilled info
            if (typeof Calendly !== 'undefined') {
              Calendly.initPopupWidget({
                url: tourBtn.dataset.calendlyUrl || 'https://calendly.com/tr10562/30min',
                prefill: { name: (state.firstName + ' ' + state.lastName).trim(), email: state.email }
              });
              trackGA4('quote_tour_click', { event_type: state.eventType || '', guest_count: (state.adults || 0) + (state.children || 0), value: 200, currency: 'USD' });
              if (!window._trTourBookedFired) {
                window._trTourBookedFired = true;
                trackGA4('tour_booked', { value: 100, currency: 'USD', source: 'quote_calculator', event_type: state.eventType || '' });
              }
            }
          });
        } else {
          // Open Calendly directly with prefilled info
          e.preventDefault();
          if (typeof Calendly !== 'undefined') {
            Calendly.initPopupWidget({
              url: tourBtn.dataset.calendlyUrl || 'https://calendly.com/tr10562/30min',
              prefill: { name: (state.firstName + ' ' + state.lastName).trim(), email: state.email }
            });
            trackGA4('quote_tour_click', { event_type: state.eventType || '', guest_count: (state.adults || 0) + (state.children || 0), value: 200, currency: 'USD' });
            if (!window._trTourBookedFired) {
              window._trTourBookedFired = true;
              trackGA4('tour_booked', { value: 100, currency: 'USD', source: 'quote_calculator', event_type: state.eventType || '' });
            }
          }
        }
      });
    }

    // ── Savory station reminder modal (Step 4→5 transition) ──
    var savoryModal = getEl('[data-savory-reminder-modal]');
    var savoryGoBack = getEl('[data-savory-reminder-goback]');
    var savoryRemove = getEl('[data-savory-reminder-remove]');
    if (savoryModal) {
      if (savoryGoBack) savoryGoBack.addEventListener('click', function() { savoryModal.style.display = 'none'; });
      if (savoryRemove) savoryRemove.addEventListener('click', function() {
        savoryModal.style.display = 'none';
        savoryModal._dismissed = true;
        // Remove savory station from cocktail addons
        state.cocktailAddons = (state.cocktailAddons || []).filter(function(k) { return k !== 'cocktail_savory_station'; });
        state.savoryStations = [];
        // Uncheck the savory station card visually + hide its checklist
        var savoryCard = getEl('[data-cocktail-addon-card="cocktail_savory_station"]');
        if (savoryCard) savoryCard.classList.remove('is-selected');
        var savoryChecklist = getEl('[data-savory-checklist]');
        if (savoryChecklist) { savoryChecklist.style.display = 'none'; getEls('[data-savory-check]').forEach(function(cb){ cb.checked = false; }); }
        // Re-trigger nextStep (which calls renderCart via showStep→renderEstimate)
        nextStep();
      });
      savoryModal.addEventListener('click', function(e) { if (e.target === savoryModal) savoryModal.style.display = 'none'; });
    }

    // ── Room capacity modal (Step 4→5 transition) — the room MAXIMUM ──
    // "Choose a Larger Space" is the primary action and goes BACK to Step 3.
    // "Continue Anyway" is the escape, and it sets _capacityAcknowledged so
    // the user is not re-asked on the same selection; returning to Step 3
    // clears it (see showStep). Both are tracked.
    var capModalEl     = getEl('[data-capacity-reminder-modal]');
    var capChooseRoom  = getEl('[data-capacity-reminder-rooms]');
    var capProceed     = getEl('[data-capacity-reminder-proceed]');
    if (capModalEl) {
      if (capChooseRoom) capChooseRoom.addEventListener('click', function() {
        capModalEl.style.display = 'none';
        trackGA4('quote_capacity_modal_action', { action: 'choose_room', context: 'step4_to_5' });
        showStep(3);
      });
      if (capProceed) capProceed.addEventListener('click', function() {
        capModalEl.style.display = 'none';
        _capacityAcknowledged = true;
        var over = getSeatedCapacityInfo();
        trackGA4('quote_capacity_modal_action', {
          action: 'proceed', context: 'step4_to_5',
          room: over ? over.roomName : '', room_seats: over ? over.seats : 0,
          guest_count: over ? over.guests : 0, over_by: over ? over.over : 0
        });
        if (state.currentStep < state.totalSteps) showStep(state.currentStep + 1);
      });
      capModalEl.addEventListener('click', function(e) { if (e.target === capModalEl) capModalEl.style.display = 'none'; });
    }

    // ── Budget reminder modal (Step 5→6 transition) ──
    var budgetModal = getEl('[data-budget-reminder-modal]');
    var budgetAdjust = getEl('[data-budget-reminder-adjust]');
    var budgetProceed = getEl('[data-budget-reminder-proceed]');
    if (budgetModal) {
      if (budgetAdjust) budgetAdjust.addEventListener('click', function() { budgetModal.style.display = 'none'; trackGA4('quote_budget_modal_action', { action: 'adjust', context: 'step5_to_6' }); });
      if (budgetProceed) budgetProceed.addEventListener('click', function() {
        budgetModal.style.display = 'none';
        trackGA4('quote_budget_modal_action', { action: 'proceed', context: 'step5_to_6' });
        // Proceed to Step 6
        if (state.currentStep < state.totalSteps) showStep(state.currentStep + 1);
        if (state.currentStep === state.totalSteps) renderEstimate();
      });
      budgetModal.addEventListener('click', function(e) { if (e.target === budgetModal) budgetModal.style.display = 'none'; });
    }

    // ── Send Proposal under-budget confirmation modal ──
    var proposalBudgetModal = getEl('[data-proposal-budget-modal]');
    var proposalBudgetAdjust = getEl('[data-proposal-budget-adjust]');
    var proposalBudgetSend = getEl('[data-proposal-budget-send]');
    if (proposalBudgetModal) {
      if (proposalBudgetAdjust) proposalBudgetAdjust.addEventListener('click', function() {
        proposalBudgetModal.style.display = 'none';
        trackGA4('quote_budget_modal_action', { action: 'adjust', context: 'proposal_send' });
        // Go back to Step 5 to make adjustments
        showStep(5);
      });
      if (proposalBudgetSend) proposalBudgetSend.addEventListener('click', function() {
        proposalBudgetModal.style.display = 'none';
        trackGA4('quote_budget_modal_action', { action: 'proceed', context: 'proposal_send' });
        _minShortfallAcknowledged = true;
        submitProposal(); // Re-call — will skip modal this time
      });
      proposalBudgetModal.addEventListener('click', function(e) { if (e.target === proposalBudgetModal) proposalBudgetModal.style.display = 'none'; });
    }

    // Cart remove buttons — event delegation on the static section container
    const quoteSection = getEl('#quote-calculator');
    if (quoteSection) {
      quoteSection.addEventListener('click', e => {
        const btn = e.target.closest('[data-cart-remove]');
        if (btn) removeCartItem(btn.dataset.cartRemove, btn.dataset.cartKey);
      });
    }

    // Initial weekday slot visibility
    updateWeekdaySlots();

  }

  /* ------------------------------------------------------------------
     DYNAMIC ROOM FILTERING (Step 3)
     Event type from Step 1 shows/hides relevant room options
  ------------------------------------------------------------------ */
  function filterRoomsByEventType(eventType) {
    getEls('[data-room-card]').forEach(card => {
      const allowedTypes = (card.dataset.roomEvents || '').split(',').map(s => s.trim());
      const showAll = allowedTypes.includes('all') || allowedTypes.length === 0;
      const matches = showAll || allowedTypes.includes(eventType);
      card.closest('[data-room-item]')?.style.setProperty('display', matches ? '' : 'none');
    });
  }

  /* ------------------------------------------------------------------
     INIT
  ------------------------------------------------------------------ */
  function showResumeBanner(savedSnapshot) {
    var banner = getEl('[data-quote-resume-banner]');
    if (!banner) return;

    var savedStep = savedSnapshot.currentStep || 1;
    var stepNames = { 1: 'Event Details', 2: 'Date & Time', 3: 'Venue & Spaces', 4: 'Food & Beverages', 5: 'Enhancements & Add-ons', 6: 'Your Proposal' };
    var label = stepNames[savedStep] || ('Step ' + savedStep);
    var detail = getEl('[data-resume-detail]');
    if (detail) detail.textContent = 'You were on: ' + label;

    banner.style.display = '';

    getEl('[data-resume-continue]')?.addEventListener('click', function() {
      banner.style.display = 'none';
      trackGA4('quote_resume_action', { action: 'continue', resumed_step: savedSnapshot.currentStep || 1, event_type: savedSnapshot.eventType || '' });
      // Restore from snapshot (localStorage was already overwritten by showStep(1))
      Object.keys(savedSnapshot).forEach(function(k) { if (k in state) state[k] = savedSnapshot[k]; });
      applyStateToUI();
      showStep(state.currentStep);
      if (state.currentStep === state.totalSteps) renderEstimate();
      renderCart();
    });

    getEl('[data-resume-start-over]')?.addEventListener('click', function() {
      banner.style.display = 'none';
      trackGA4('quote_resume_action', { action: 'start_over', abandoned_step: savedSnapshot.currentStep || 1, event_type: savedSnapshot.eventType || '' });
      clearSavedState();
      showStep(1);
      renderCart();
    });
  }

  /* ------------------------------------------------------------------
     E-SIGN PAD (Step 6 — appears after proposal is sent)
  ------------------------------------------------------------------ */
  var _esignPad = null;

  function loadSignaturePadLib() {
    return new Promise(function (resolve, reject) {
      if (window.SignaturePad) { resolve(); return; }
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/signature_pad@4.2.0/dist/signature_pad.umd.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function initEsignPad() {
    var canvas = document.querySelector('[data-esign-canvas]');
    if (!canvas || _esignPad) return;
    loadSignaturePadLib().then(function () {
      var wrap = canvas.parentElement;
      var ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = wrap.offsetWidth * ratio;
      canvas.height = 110 * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      _esignPad = new SignaturePad(canvas, {
        penColor: '#1A1209',
        minWidth: 0.8,
        maxWidth: 2.2,
        backgroundColor: 'rgba(255,255,255,0)'
      });
    });
    // Clear
    var clearBtn = document.querySelector('[data-esign-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (_esignPad) _esignPad.clear();
      });
    }
    // Submit
    var submitBtn = document.querySelector('[data-esign-submit]');
    if (submitBtn) submitBtn.addEventListener('click', submitEsign);
  }

  async function submitEsign() {
    var statusEl = document.querySelector('[data-esign-status]');
    var btn = document.querySelector('[data-esign-submit]');

    if (!_esignPad || _esignPad.isEmpty()) {
      if (statusEl) {
        statusEl.textContent = 'Please sign above before submitting.';
        statusEl.style.display = 'block';
        statusEl.style.color = '#991b1b';
      }
      return;
    }

    if (btn) { btn.textContent = 'Sending Signed Contract...'; btn.disabled = true; }
    if (statusEl) statusEl.style.display = 'none';

    var sigDataUrl = _esignPad.toDataURL('image/png');
    var signedDateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    // Generate the signed PDF server-side, reusing the SAME payload builder as
    // the inquiry proposal so the signed contract matches it exactly — plus the
    // signature image + signed date, which the server renders on contract page 4.
    // (No DOM injection: the PDF is rendered fresh on the server and never sees
    // the modal DOM, so the signature must travel in the payload.)
    var depositUrl = window.QuoteRates?.depositEndpoint;
    var signedPdfBase64 = null;
    if (depositUrl) {
      try {
        var pdfUrl = depositUrl.replace(/\/create-draft-order\/?$/, '/generate-pdf');
        var pdfPayload = buildServerProposalPayload();
        pdfPayload.signatureDataUrl = sigDataUrl;
        pdfPayload.signedDate = signedDateStr;
        pdfPayload._returnBase64 = true;
        var pdfResp = await fetch(pdfUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pdfPayload)
        });
        if (pdfResp.ok) {
          var pdfData = await pdfResp.json();
          signedPdfBase64 = pdfData.pdfBase64 || null;
        } else {
          console.error('Signed PDF generation returned', pdfResp.status);
        }
      } catch (err) {
        console.error('Signed PDF generation failed:', err);
      }
    }

    // Send to worker
    if (depositUrl && signedPdfBase64) {
      try {
        var esignUrl = depositUrl.replace(/\/create-draft-order\/?$/, '/submit-signed-contract');
        var resp = await fetch(esignUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: state.firstName || '',
            lastName: state.lastName || '',
            email: state.email || '',
            phone: state.phone || '',
            eventType: state.eventType || '',
            signatureData: sigDataUrl,
            pdfBase64: signedPdfBase64,
            signedAt: new Date().toISOString()
          })
        });
        if (resp.ok) {
          if (btn) { btn.textContent = 'Contract Signed & Sent'; btn.classList.add('is-success'); }
          if (statusEl) {
            statusEl.textContent = 'Your signed contract has been sent. You and our team will both receive a copy. The contract becomes binding once your deposit is received and the venue countersigns.';
            statusEl.style.display = 'block';
            statusEl.style.color = '#2d6a4f';
          }
          var contractEstimate; try { contractEstimate = calculateEstimate().total; } catch (_) { contractEstimate = 0; }
          trackGA4('quote_contract_signed', {
            event_type: state.eventType,
            guest_count: (state.adults || 0) + (state.children || 0),
            estimated_total: contractEstimate,
            rooms: state.rooms.join(',') || ''
          });
        } else {
          throw new Error('Server returned ' + resp.status);
        }
      } catch (err) {
        console.error('E-sign submit error:', err);
        trackGA4('quote_esign_error', { error_type: 'server_error', event_type: state.eventType || '' });
        if (btn) { btn.textContent = 'Sign & Send Contract'; btn.disabled = false; }
        if (statusEl) {
          statusEl.textContent = 'Something went wrong. Please try again or email us at Events@WestchesterParty.com.';
          statusEl.style.display = 'block';
          statusEl.style.color = '#991b1b';
        }
      }
    } else {
      trackGA4('quote_esign_error', { error_type: 'pdf_generation_failed', event_type: state.eventType || '' });
      if (btn) { btn.textContent = 'Sign & Send Contract'; btn.disabled = false; }
      if (statusEl) {
        statusEl.textContent = 'Could not generate signed document. Please try again.';
        statusEl.style.display = 'block';
        statusEl.style.color = '#991b1b';
      }
    }
  }

  /* ------------------------------------------------------------------
     STEP 4 TELEMETRY (audit §D-5)
     Step 4 (Food & Beverages) is the ~24-card wall where ~50% of mobile
     sessions drop, and existing events only see enter/exit of the whole step.
     These two events localize the drop WITHIN Step 4 so the eventual card-wall
     collapse is evidence-based, not a guess:
       - quote_step4_section_reached: fires once per sub-section (hors / main /
         dessert / afterdinner / bar) as it first scrolls into view — the
         relative drop across sections shows how far down the wall visitors get.
       - quote_step4_first_interaction: fires once on the first tap/change of any
         Step-4 control, separating "saw it, did nothing" from "engaged, then left."
     Instrument-only: no layout change here (that redesign is gated on this data).
  ------------------------------------------------------------------ */
  function initStep4Telemetry() {
    var step4 = getEl('[data-step="4"]');
    if (!step4) return;

    // ── section-reached (IntersectionObserver) ──
    var sections = getEls('[data-step4-section]');
    if (sections.length && 'IntersectionObserver' in window) {
      var reached = {};
      var obs = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          // The container is display-toggled; only count while Step 4 is active.
          if (state.currentStep !== 4) return;
          var name = entry.target.getAttribute('data-step4-section');
          if (!name || reached[name]) return;
          reached[name] = true;
          trackGA4('quote_step4_section_reached', { section: name, event_type: state.eventType || '' });
        });
      }, { threshold: 0.5 });
      sections.forEach(function(el) { obs.observe(el); });
    }

    // ── first-interaction (one-time, capture-phase delegated listener) ──
    var interacted = false;
    function onFirstInteraction(e) {
      if (interacted) return;
      // Only count interaction with an actual control, not stray clicks.
      if (!e.target.closest('button, input, select, textarea')) return;
      interacted = true;
      trackGA4('quote_step4_first_interaction', { event_type: state.eventType || '' });
      step4.removeEventListener('click', onFirstInteraction, true);
      step4.removeEventListener('change', onFirstInteraction, true);
    }
    step4.addEventListener('click', onFirstInteraction, true);
    step4.addEventListener('change', onFirstInteraction, true);
  }

  /* ------------------------------------------------------------------
     OCCASION PREFILL FROM ?event= (added 2026-08-18)

     The corporate and mitzvah journey CTAs link to
     /pages/instant-quote?event=<slug> (snippets/event-feature-block.liquid).
     Until now nothing read the parameter, so a buyer handed straight here
     from an occasion page still landed on an empty "What's the Occasion?"
     box — and because event_type is the first param on every quote_step_*
     event, that whole entry path was invisible in GA4.

     ⛔ STRICT WHITELIST — never pass the raw parameter through. state.eventType
     is not display-only: it reaches the proposal PDF, the venue-side inquiry
     subject line, and the body of the automated follow-up email
     ("your upcoming ${eventType}"). An unmapped value is ignored entirely.

     ⚠️ The label is what the customer reads back in all three of those places,
     and it also decides the follow-up's tone via categorizeEventType() in
     scripts/deposit-worker/followup-templates.js — which matches by SUBSTRING.
     'Corporate Event' falls through to `neutral`; 'Bar/Bat Mitzvah' matches
     'mitzvah' → `celebratory`. Check that file before changing a label here.

     `subtitle` overrides Step 1's "Tell us about what you're celebrating."
     where celebrating is the wrong verb.
  ------------------------------------------------------------------ */
  const EVENT_PARAM_MAP = {
    corporate:        { label: 'Corporate Event', subtitle: "Tell us about the event you're planning." },
    bar_bat_mitzvah:  { label: 'Bar/Bat Mitzvah' },
    wedding:          { label: 'Wedding' },                    // dormant
    bridal_shower:    { label: 'Bridal Shower' },             // dormant
    baby_shower:      { label: 'Baby Shower' },               // dormant
    birthday:         { label: 'Milestone Birthday' },   // dormant
    celebration_of_life: { label: 'Celebration of Life', subtitle: 'Tell us about the gathering you are planning.' }  // dormant
  };
  // "dormant" = no CTA emits that slug today. They are here so that adding the
  // link later is a one-line theme edit and not a silent no-op; grep
  // 'instant-quote?' in the theme for the ones actually in use.

  // Must run BEFORE showStep(1) — that is what fires quote_step_1, and the
  // prefilled occasion has to be on state by then or the event ships blank.
  function applyEventParam() {
    var slug;
    try {
      slug = new URLSearchParams(window.location.search).get('event');
    } catch (e) { return; }
    if (!slug) return;

    // hasOwnProperty, not a bare lookup: '?event=__proto__' / 'constructor' /
    // 'toString' all resolve up the prototype chain to a truthy object whose
    // .label is undefined, which would put the string "undefined" in the box.
    var key = String(slug).trim().toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(EVENT_PARAM_MAP, key)) return;
    var match = EVENT_PARAM_MAP[key];
    if (!match || typeof match.label !== 'string' || !match.label) return;

    state.eventType = match.label;
    var eti = getEl('[data-input-event-type]');
    if (eti) eti.value = match.label;

    if (match.subtitle) {
      var sub = getEl('[data-step-subtitle="1"]');
      if (sub) sub.textContent = match.subtitle;
    }
  }

  function init() {
    const calculator = getEl('#quote-calculator');
    if (!calculator) return;

    bindEvents();
    initStep4Telemetry();
    applyEventParam();

    // Snapshot saved state BEFORE showStep(1) overwrites it
    var savedSnapshot = null;
    if (hasSavedState()) {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        var payload = JSON.parse(raw);
        savedSnapshot = payload.state || payload;
      } catch (e) { /* noop */ }
    }

    showStep(1);
    renderCart();

    if (savedSnapshot && (savedSnapshot.currentStep > 1 || savedSnapshot.eventType)) {
      showResumeBanner(savedSnapshot);
    }

    // Pricing teaser is now static text (no per-person price range)

    // GA4: track quote page view (distinct from page_view — measures intent)
    trackGA4('quote_page_view', { returning: savedSnapshot ? 'yes' : 'no' });

    // GA4: track abandonment when user leaves mid-funnel
    window.addEventListener('beforeunload', function () {
      if (state.currentStep > 1 && !state._leadSubmitted) {
        trackGA4('quote_abandoned', {
          event_type: state.eventType || '',
          abandoned_step: state.currentStep,
          guest_count: (state.adults || 0) + (state.children || 0)
        });
      }
    });
  }

  /* ------------------------------------------------------------------
     MENU VARIANT SWAP (with / without hors d'oeuvres)
  ------------------------------------------------------------------ */
  function updateMenuVariants() {
    var hd = state.horsOeuvres;
    document.querySelectorAll('[data-menu-variant]').forEach(function(link) {
      var noUrl  = link.getAttribute('data-menu-preview-no-hd');
      var hd6Url = link.getAttribute('data-menu-preview-hd-6');
      var hd4Url = link.getAttribute('data-menu-preview-hd-4');
      var url;
      if (hd === 'hors_doeuvres_6_full') {
        url = hd6Url || noUrl;
      } else if (hd === 'hors_doeuvres_4_half') {
        url = hd4Url || noUrl;
      } else {
        url = noUrl || hd6Url || hd4Url;
      }
      if (url) {
        link.setAttribute('data-menu-preview', url);
      }
    });
  }

  /* ------------------------------------------------------------------
     SAMPLE MENU LIGHTBOX
  ------------------------------------------------------------------ */
  // Body scroll is locked while ANY overlay (image lightbox, comparison modal,
  // all-stations modal) is open. The all-stations modal can stack over the
  // lightbox, so we restore scroll only when none remain open.
  function anyTrModalOpen() {
    var sel = '[data-menu-lightbox],[data-format-compare-modal],[data-all-stations-modal],[data-ceremony-modal]';
    return Array.prototype.some.call(document.querySelectorAll(sel), function(m) {
      return m.style.display === 'flex';
    });
  }
  function setBodyScroll() {
    document.body.style.overflow = anyTrModalOpen() ? 'hidden' : '';
  }

  function initMenuLightbox() {
    var lightbox  = document.querySelector('[data-menu-lightbox]');
    if (!lightbox) return;

    var imgEl      = lightbox.querySelector('[data-menu-lightbox-img]');
    var titleEl    = lightbox.querySelector('[data-menu-lightbox-title]');
    var topSlot    = lightbox.querySelector('[data-stations-top]');
    var bottomSlot = lightbox.querySelector('[data-stations-bottom]');
    var closeBtns  = lightbox.querySelectorAll('[data-menu-lightbox-close]');

    function openLightbox(src, label, isStations) {
      imgEl.src = src;
      imgEl.alt = label + ' — Sample Menu';
      titleEl.textContent = label + ' — Sample Menu';
      if (topSlot)    topSlot.style.display    = isStations ? 'block' : 'none';
      if (bottomSlot) bottomSlot.style.display = isStations ? 'block' : 'none';
      lightbox.style.display = 'flex';
      setBodyScroll();
    }

    function closeLightbox() {
      lightbox.style.display = 'none';
      imgEl.src = '';
      setBodyScroll();
    }

    // Delegate clicks on sample menu links (inside cards)
    document.addEventListener('click', function(e) {
      var link = e.target.closest('[data-menu-preview]');
      if (link) {
        e.preventDefault();
        e.stopPropagation(); // Don't toggle the card selection
        var isStations = link.hasAttribute('data-menu-stations');
        openLightbox(link.dataset.menuPreview, link.dataset.menuLabel || 'Menu', isStations);
        if (isStations) trackGA4('quote_sample_menu_opened', { menu: 'food_stations' });
      }
    });

    closeBtns.forEach(function(btn) {
      btn.addEventListener('click', closeLightbox);
    });

    // Close on Escape — but defer to the all-stations modal if it's stacked on top.
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && lightbox.style.display === 'flex') {
        var sm = document.querySelector('[data-all-stations-modal]');
        if (sm && sm.style.display === 'flex') return;
        closeLightbox();
      }
    });
  }

  // Text-content modals: "How serving styles compare" (Change 3) and
  // "All available stations" (Change 6). The latter opens over the lightbox.
  function initInfoModals() {
    var compareModal  = document.querySelector('[data-format-compare-modal]');
    var stationsModal = document.querySelector('[data-all-stations-modal]');
    var ceremonyModal = document.querySelector('[data-ceremony-modal]');

    function openModal(modal, gaName) {
      if (!modal) return;
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      if (gaName) trackGA4(gaName, { event_type: state.eventType || '' });
    }
    function closeModal(modal) {
      if (!modal) return;
      modal.style.display = 'none';
      setBodyScroll();
    }

    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-format-compare-open]')) {
        e.preventDefault();
        openModal(compareModal, 'quote_format_compare_opened');
        return;
      }
      if (e.target.closest('[data-all-stations-open]')) {
        e.preventDefault();
        openModal(stationsModal, 'quote_all_stations_opened'); // stacks over lightbox
        return;
      }
      if (e.target.closest('[data-ceremony-open]')) {
        e.preventDefault();
        openModal(ceremonyModal, 'quote_ceremony_info_opened');
        return;
      }
      if (e.target.closest('[data-info-modal-close]'))   { closeModal(compareModal);  return; }
      if (e.target.closest('[data-all-stations-close]')) { closeModal(stationsModal); return; }
      if (e.target.closest('[data-ceremony-close]'))     { closeModal(ceremonyModal); return; }
      // Inline minimums disclosure inside the comparison modal (Change 3 / Q4)
      var minToggle = e.target.closest('[data-stations-min-toggle]');
      if (minToggle) {
        e.preventDefault();
        var detail = compareModal && compareModal.querySelector('[data-stations-min-detail]');
        if (detail) detail.style.display = detail.style.display === 'block' ? 'none' : 'block';
        return;
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape') return;
      if (stationsModal && stationsModal.style.display === 'flex') { closeModal(stationsModal); return; }
      if (compareModal  && compareModal.style.display  === 'flex') { closeModal(compareModal);  return; }
      if (ceremonyModal && ceremonyModal.style.display === 'flex') { closeModal(ceremonyModal); return; }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { init(); initMenuLightbox(); initInfoModals(); });
  } else {
    init();
    initMenuLightbox();
    initInfoModals();
  }
})();
